import {
  Fragment, createContext, useContext, useEffect, useId, useMemo, useRef, useState,
  type KeyboardEvent, type ReactNode,
} from "react";
import { werteAusDaten } from "../daten/fehlerwerte";
import { T, einheitFuer, useT } from "../i18n";
import { Erklaerung, FehlerBand, Knopf, Marke, Punkt, euro } from "./index";
import { Zeitraum, type Spanne } from "./Zeitraum";
import { Wochenwahl, wochenwort } from "./Wochenwahl";
import { Blattvonunten } from "./Blattvonunten";
import { Qualifikationsband } from "./Qualifikationsfrage";
import {
  Abschnitt, Kurzfassung, Leersatz, Wertzeile, Zeilenblatt, Zweizeiler, type Punkt as Punktart,
} from "./Handyformen";
import { Blattsatz, Hakenzeile, Wahlzeile, Zahlzeile } from "./Postendetails";
import { Tabelle, type Spalte } from "./Tabelle";
import { AbholHaken } from "./Lieferweg";
import { anRegelstelle, bedienerBeiAbholung, listenfolge } from "./lieferwegregeln";
import { Fehler } from "../daten/klient";
import { betragFehler, betragFeldtext, zahlAnzeige, zahlLesen } from "../daten/zahlfeld";
import { Betragssatz } from "./Betragssatz";
import { Zeitraumvorschlagband } from "./Zeitraumvorschlag";
import type { Zeitraumvorschlag } from "../daten/zeitraumvorschlag";
import { Laengensatz, textZuLang } from "./Laengensatz";
import { useRuf } from "../schale/Ruf";
import { useTelefon } from "../schale/geraet";
import { useDarfSchreiben } from "../schale/Sitzung";
import {
  useBausteinBenutzt, useBedienerauswahl, useLeistungsarten, useMaschinentypen, usePositionAendern,
  usePositionAnlegen, usePositionLoeschen, usePreis, useTextbausteine,
  useTextbausteinAnlegen, useVerfuegbarkeit, useVerlauf, useVorgang, useVorgangAendern,
  useWaren, useZubehoerAuswahl, useZubehoerTypen,
} from "../daten/haken";
import type { Bausteinart, Bedienerkandidat, Geraetestueck, Position, Textbaustein, VorgangZustand } from "../daten/typen";
import type { Erwartungszeile, Zuschlagserwartung as Erwartung } from "../daten/routen";
import { useNachschlagen, type Typenlage } from "../daten/nachschlagen";
// Die Texte je Belegart (Migration 0105) — eigene Zeile, damit die Liste oben ruhig bleibt.
import { useBelegtexte, useBelegtexteSchreiben } from "../daten/haken";
import type { Belegart } from "../daten/typen";
import { ZUBEHOER_LEER, ZubehoerWahl, type ZubehoerWert } from "./ZubehoerWahl";
import { AusstattungWahl, ausstattungVon, mitAusstattung } from "./Ausstattung";
import "../stile/telefon/positionen.css";
// Das Stückfeld des gezählten Bestands (0194).
import "../stile/bestand.css";
// Die Mietzeile, die Gerätemarke und Menge mit Einheit (Paket 9, 07.10.2026).
import "../stile/positionszeile.css";
import { Geraeteklappe } from "./Geraeteklappe";
import { bausteinEinfuegen, entwurfLoeschen, entwurfSetzen, textImFeld } from "./belegtextentwurf";
import { tagImBetrieb, tagKurz as tagKurzImBetrieb } from "../daten/betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Der Positionseditor — einer, an zwei Stellen.

   Am 15.09.2026 hier herausgehoben. Bis dahin konnte „Angebot bauen"
   alles und „Ein Auftrag" gar nichts. **Zwei Editoren für dieselbe
   Sache laufen auseinander**, und dann ist einer von beiden der
   schlechtere. Also steht er einmal hier und wird zweimal benutzt.

   **Am selben Tag neu geschnitten** (Luca, vor dem leeren Entwurf):
   *„Das sieht ja dumm aus, wenn man ein Angebot erstellt. Und ich kann
   immer noch keine Leistungen richtig auswählen?? Das ist immer noch
   Freitext."* Und dazu: *„Die Karten beim Hinzufügen find ich ja
   grundsätzlich nicht schlecht, … mach sie nur intuitiv."*

   **Am 16.09.2026 auf den geteilten Tabellenbaustein gestellt.** Bis
   dahin war die Positionsliste ein eigenes Raster (`.pos`) mit eigenem
   Kopf (`.pos-kopf`) und eigenen Spaltenbreiten — die letzte Liste der
   Anwendung, die nicht über `bausteine/Tabelle.tsx` lief. Luca: *„Baue
   mal bitte im Angebot und im Auftragsmodul die gleichen Tabellenköpfe,
   die wir jetzt überall verwenden … dann erkennt man das auch schneller
   wieder."* Angebot und Auftrag tragen seitdem **dieselben sechs
   Spalten**; nur im Angebot kommt die Spalte mit den Knöpfen dazu.

   **Die Eingabezeile ist eine Zeile der Tabelle geblieben** — sie hängt
   über `anhang` im selben `<tbody>` wie die gespeicherten Zeilen und
   nicht als Formular daneben. Dafür gibt der Baustein den Platz her
   (`Tabelle.tsx`, `anhang`); das ist die einzige Stelle, an der er für
   diesen Umbau erweitert wurde.

   - Die vier Karten bleiben — mit Zeichen, Wort und einem Beispiel aus
     den echten Daten statt eines Erklärsatzes. Leer sind sie der Inhalt
     („Erste Position"), danach eine schmale Reihe unter der Tabelle.
   - Eine Karte öffnet die passende Auswahl **als Zeile der Tabelle**
     (Xero-Muster, uebergabe/2026-09-15-angebotsmodul.md), kein Kasten
     dazwischen.
   - Leistung und Ware kommen **aus dem Katalog**. Freitext ist ein
     ausdrücklicher Weg am Ende der Liste, nicht der Regelfall.
   - Im Angebot kann eine Position **optional** sein oder **statt** einer
     anderen gelten — Lisas Radlader: 4 Tage, oder die Woche.

   Was hier gilt:

   **Regel 2** — eine Mietposition hängt am Maschinentyp.
   **Regel 3** — abgerechnet wird das Ist; eine nachgetragene Position
   kommt dazu und ändert den Plan nicht rückwirkend.
   **Regel 4** — was eingefroren ist, bleibt es; das Preisfeld sperrt.
   **Die Abstufung der API ist die Abstufung der Maske** (34-api.md).
   ══════════════════════════════════════════════════════════════════ */

/**
 * Die Arten, die von Hand angelegt werden.
 *
 * Im Angebot sind es vier — `zuschlag` entsteht dort gerechnet, aus den Regeln
 * am Schnappschuss. In der **Schnellerfassung** kommt er als fünfte dazu: Dort
 * steht der gerechnete Zuschlag schon in der Liste, und wer am Telefon einen
 * anderen zusagt, braucht die Zeile dafür. Welche Arten gezeigt werden, sagt
 * die Maske (`Karten`, Eigenschaft `arten`) — nicht diese Liste.
 */
export type NeueArt = "miete" | "leistung" | "verkauf" | "zuschlag" | "text";

/** Das Wort zur Art einer Zeile. */
export const ARTWORT: Record<string, string> = {
  miete: "angebotBauen.artMiete",
  leistung: "angebotBauen.artLeistung",
  verkauf: "angebotBauen.artVerkauf",
  text: "angebotBauen.artText",
  zuschlag: "angebotBauen.artZuschlag",
};

/** Was einen Betrag trägt und darum eine Wahl sein kann. */
const WAEHLBAR = new Set(["miete", "leistung", "verkauf"]);

/** `optionen.zwei_alternativen` → `optionen.fehler.zweiAlternativen`. */
function kamel(wort: string): string {
  return wort.split("_").map((w, i) => (i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1))).join("");
}

/**
 * Der i18n-Pfad zu einem Fehler.
 *
 * Die Wahlpositionen bringen eigene Schlüssel mit (`optionen.*`); ihre Sätze
 * stehen unter `optionen.fehler`, nicht unter `fehler` — sie gehören zu dieser
 * Sache und nirgends sonst hin.
 */
export function positionsFehlerPfad(e: unknown): string {
  if (!(e instanceof Fehler)) return "fehler.unbekannt";
  if (e.schluessel.startsWith("optionen.")) return `optionen.fehler.${kamel(e.schluessel.slice(9))}`;
  return e.textPfad;
}

/**
 * Was der Fehler mitbringt — die Werte für den Satz.
 * `lexware.artikel_fehlt` nennt die Position, der die Zuordnung fehlt;
 * `annahme.kein_geraet` die Zeile ohne Gerät und ihren Zeitraum.
 *
 * Exportiert seit dem 18.09.2026: Das Angebotsblatt zeigt denselben Fehler,
 * und ohne die Werte stünde dort „{bezeichnung}" im Satz.
 *
 * **`von` und `bis` werden zu Tagen.** Die API schickt, was sie hat — mal
 * `2031-07-07`, mal einen vollen Zeitstempel. Im Satz stand das bisher roh:
 * „ist vom 2031-07-07T07:00:00.000Z bis …". Die Uhrzeit hat dort nichts
 * verloren, der Balken beginnt ohnehin morgens und endet abends.
 */
export function fehlerWerte(e: unknown): Record<string, string | number> {
  if (!(e instanceof Fehler)) return {};
  /* Dieselbe Lesart wie `Fehler.werte` (`daten/fehlerwerte.ts`): Ein ISO-Tag
     in `von`, `bis`, `gueltig_bis`, `datum`, `seit` … wird deutsch. Bis zum
     05.10.2026 kannte diese Stelle nur drei Felder, und die Rückfrage F20
     sagte „Der Mietbeginn am 2026-10-04 …" (N7, F20-Rest). */
  return werteAusDaten(e.daten);
}

/** Was in einem Zahlenfeld steht, als Zahl. Leer heißt: nichts gesetzt. */
export function alsZahl(wert: string): number | undefined {
  return zahlLesen(wert) ?? undefined;
}

/**
 * Die Einheit einer Leistungsart aus ihrer Zählgröße — derselbe Schlüssel,
 * den die API an die Position schreibt (`routen/vorgaenge.ts`).
 */
export function einheitAusGroesse(groesse: string): string {
  return groesse === "MODULE" ? "einheit.modul" : `einheit.${groesse.toLowerCase()}`;
}

/**
 * **Woran eine Zuschlagszeile hängt** — „zu Pos 2" oder „einmal je Auftrag"
 * (Systemprüfung B-09). Zwei Maschinen tragen zweimal dieselbe Versicherung;
 * ohne diesen Zusatz stand zweimal derselbe Name untereinander, und niemand
 * konnte sagen, welche zu welcher gehört. Steht die Position nicht (mehr) in
 * der Liste, bleibt der Zusatz weg statt einer leeren Nummer.
 */
export function zuschlagsbezug(
  z: Erwartungszeile,
  nummern: Map<string, string>,
  t: (k: string, w?: Record<string, string | number>) => string,
): string {
  if (z.jeAuftrag) return t("positionen.jeAuftrag");
  const nr = z.positionId ? nummern.get(z.positionId) : undefined;
  return nr ? t("positionen.zuPos", { nr }) : "";
}

/**
 * **Wann eine Leistung stattfindet — so genau, wie es feststeht** (B-02).
 * Grob geplant die Woche, fest geplant die beiden Tage. Bis zum 24.09.2026
 * stand auch ein genauer Termin als Woche da („KW 41"), und wer nach dem Tag
 * suchte, fand ihn nicht. Ohne Termin: leer — die Marke „Termin offen" sagt
 * es dann.
 */
export function leistungstermin(
  p: Position,
  t: (k: string, w?: Record<string, string | number>) => string,
): string {
  if (p.art !== "leistung" || !p.von) return "";
  return p.zeitschaerfe === "kw"
    ? wochenwort(p.von, t, p.bis ?? null)
    : t("positionen.karte.zeitraum", { von: tagKurz(p.von), bis: tagKurz(p.bis ?? null) });
}

/* ══════════════ Die Wahlpositionen — gerechnet, nicht gespeichert ══════════════ */

/** Zählt die Zeile in die Summe? Optionen und Alternativen tun es nicht. */
export function zaehlt(p: Position): boolean {
  return !p.optional && !p.alternativeZu;
}

/**
 * **Ist `a` eine Alternative zu `p`?**
 *
 * Der geradeheraus geschriebene Vergleich `a.alternativeZu === p.id` sagt ja,
 * wenn **beide fehlen** — und dann ist jede Zeile die Alternative jeder
 * anderen. Genau das stand am 18.09.2026 auf `/angebote/neu` mit
 * Attrappendaten: Der Kopf zählte drei Positionen, die Liste zeigte zwölf,
 * jede viermal. Die Beispieldaten tragen keine Kennung, und `undefined ===
 * undefined` ist wahr.
 *
 * Mit echten Daten fiel es nicht auf, weil jede Position eine Kennung hat.
 * Eine Zeile, die nur mit echten Daten stimmt, ist trotzdem keine, die man
 * stehen lässt: Die Attrappe ist der Weg, auf dem die Oberfläche angesehen
 * wird.
 */
export function istAlternativeVon(a: Position, p: Position): boolean {
  return Boolean(a.alternativeZu) && Boolean(p.id) && a.alternativeZu === p.id;
}

/** Die Summe des Angebots — dieselbe Zahl, die drüben unten auf dem Papier steht. */
export function angebotsnetto(zeilen: Position[]): number {
  return Math.round(zeilen.reduce((s, p) => (zaehlt(p) ? s + (p.summe ?? 0) : s), 0) * 100) / 100;
}

/** Was die Optionen zusammen kosten würden. Alternativen nicht: Sie ersetzen. */
export function optionsnetto(zeilen: Position[]): number {
  return Math.round(zeilen.reduce((s, p) => (p.optional ? s + (p.summe ?? 0) : s), 0) * 100) / 100;
}

/**
 * Die Nummern, wie sie auf dem Papier drüben stehen.
 *
 * Gemessen am 15.09.2026 an AG0023: Lexware zählt die gewöhnlichen Positionen
 * durch, die Alternativen darunter als „1.A1", die optionalen für sich als
 * „O1", und eine Textzeile trägt keine Nummer. Wer am Telefon „die O1" sagt,
 * soll hier dieselbe Zeile finden.
 */
export function positionsnummern(zeilen: Position[], t: (k: string, w?: Record<string, string | number>) => string): Map<string, string> {
  const nummern = new Map<string, string>();
  let gewoehnlich = 0;
  let optional = 0;
  const oben = zeilen.filter((p) => !p.alternativeZu || !zeilen.some((x) => x.id === p.alternativeZu));
  for (const p of oben) {
    const schluessel = p.id ?? String(p.nr);
    if (p.art === "text") { nummern.set(schluessel, ""); continue; }
    let eigene: string;
    if (p.optional) {
      optional += 1;
      eigene = t("optionen.nrOptional", { k: optional });
    } else {
      gewoehnlich += 1;
      eigene = String(gewoehnlich);
    }
    nummern.set(schluessel, eigene);
    zeilen.filter((a) => istAlternativeVon(a, p)).forEach((a, k) => {
      nummern.set(a.id ?? String(a.nr), t("optionen.nrAlternative", { nr: eigene, k: k + 1 }));
    });
  }
  return nummern;
}

/* ══════════════ Was in welchem Zustand geht ══════════════ */

export type Positionsrechte = {
  /** Eine neue Position anlegen. */
  anlegen: boolean;
  /** Eine bestehende ändern. */
  aendern: boolean;
  /** Eine wieder hergeben. Hört ausdrücklich früher auf als das Anlegen. */
  loeschen: boolean;
  /**
   * Eine Position optional oder zur Alternative machen. **Nur im Entwurf:**
   * Versendet liegt die Wahl beim Kunden, angenommen ist sie getroffen.
   */
  wahl: boolean;
  /** Warum gerade nichts geht — ein i18n-Schlüssel, sonst leer. */
  grund: string;
};

/**
 * Die Zustände, in denen `POST` und `PATCH …/positionen` annehmen.
 *
 * `angebot` fehlt mit Absicht: Ein versendetes Angebot liegt beim Kunden.
 * `zurueck` steht mit dabei: Zwischen Rücknahme und Abrechnung trägt ein
 * Mensch nach, was dazugekommen ist.
 */
const OFFEN: VorgangZustand[] = ["entwurf", "angenommen", "läuft", "zurueck"];

/** Die Zustände, in denen `DELETE` eine Position wieder hergibt. */
const WEG: VorgangZustand[] = ["entwurf"];

/** Was der Zustand des Vorgangs an seinen Positionen erlaubt. */
export function rechteAus(
  zustand: VorgangZustand,
  /** Darf die Sitzung überhaupt schreiben (`useDarfSchreiben`, P6-01)? Vorgabe: ja. */
  schreibt = true,
): Positionsrechte {
  /* **Wer nur liest, ändert an keinem Zustand etwas** (P6-01, 05.10.2026):
     Die Prüferin sah am Auftrag die Felder und Knöpfe der Disposition. Der
     Satz sagt, warum — so wie beim abgerechneten Auftrag. */
  if (!schreibt) {
    return { anlegen: false, aendern: false, loeschen: false, wahl: false, grund: "positionen.zu.nurLesen" };
  }
  const offen = OFFEN.includes(zustand);
  return {
    anlegen: offen,
    aendern: offen,
    loeschen: WEG.includes(zustand),
    wahl: zustand === "entwurf",
    grund: offen ? "" : `positionen.zu.${zustand}`,
  };
}

/**
 * Eine Zeile ohne Preis geht mit 0,00 hinaus — das muss man ihr ansehen.
 * Seit dem Katalog gilt das für Leistung und Ware genauso: Ein fehlender Satz
 * im Stamm ist „kein Preis", nicht 0,00.
 */
export function ohnePreis(p: Position): boolean {
  return WAEHLBAR.has(p.art ?? "") && !p.eingefroren
    && (p.einzel === null || p.einzel === undefined || p.einzel === 0);
}

/**
 * **Wartet dieser Vorgang auf ein Aufmaß?** — die Positionen, deren Menge erst
 * jemand vor Ort messen muss.
 *
 * Abgeleitet und nicht gespeichert (Leitsatz 4): Ein Zustandsfeld, das man von
 * Hand setzen kann, läuft dem Wahren davon. Die Wahrheit steht an den Zeilen —
 * `needs_site_survey` an der Leistungsart, Menge 0 in der Position.
 */
export function wartetAufAufmass(zeilen: Position[]): Position[] {
  return zeilen.filter(mengeFolgt);
}

/**
 * **Die Mietzeilen, an denen kein Gerät hängt** (Systemprüfung, Befund 23,
 * 24.09.2026) — dieselbe Frage wie die Zeile selbst (`Geraete`): eine Miete
 * mit Zeitraum, kein Anbauteil, und unter ihren Stücken kein Träger. Sie
 * entsteht, wenn beim Anlegen nichts frei war; das ist gewollt (Regel 2,
 * Prozess B1) — aber es soll niemand übersehen, bevor es hinausgeht.
 */
export function mietzeilenOhneGeraet(zeilen: Position[]): Position[] {
  return zeilen.filter((p) =>
    p.art === "miete" && !p.anbauAn && Boolean(p.von && p.bis)
    && Array.isArray(p.geraete) && !p.geraete.some((g) => g.rolle === "traeger"));
}

/** Eine Leistung vor dem Aufmaß: Die Menge kennt noch niemand. */
export function mengeFolgt(p: Position): boolean {
  return p.aufmass === true && Number(p.menge) === 0;
}

/**
 * **Steht die Menge auf der Mindestmenge?** (E10, 04.10.2026) — dann ist sie
 * meist angehoben, und die Zeile sagt es („Mindestmenge 200"), wie das Papier.
 */
export function aufMindestmenge(p: Position): boolean {
  const mindest = p.mindestmenge ?? 0;
  return p.art === "leistung" && mindest > 0 && Math.abs(Number(p.menge) - mindest) < 1e-9;
}

/* ══════════════ Der Rabatt — bewusst versteckt (Durchgang 16.09.2026, AG3) ══════════════

   Luca: *„Ich würde das nicht im Standard aktiv haben, weil ansonsten machen
   die Leute das immer."* Darum gibt es die Spalte nicht, bis jemand sie
   holt: „+ Rabatt" neben den Karten fragt „Prozent oder Betrag" und schaltet
   sie frei. Steht an einer Zeile schon ein Rabatt, ist sie ohnehin da — ein
   Nachlass, den man nicht sieht, wäre schlimmer als eine Spalte zu viel.

   **Nur rabattfähige Zeilen haben ein Feld.** Eine Miete immer; eine
   Leistung, eine Ware und ein Zuschlag nur, wenn der Stammsatz so geschaltet
   ist (Preisstamm, Migration 0106). Die anderen zeigen einen Strich — auch
   die Zuschlagszeilen aus den Regeln, die ohnehin keine Felder tragen. */

export type Rabattart = "prozent" | "betrag";

/** Was freigeschaltet ist — leer heißt: niemand hat „+ Rabatt" gedrückt. */
export type Rabattspalte = { art: Rabattart | ""; setzen: (art: Rabattart | "") => void };

/** Trägt die Zeile schon einen Rabatt? */
export function hatRabatt(p: Position): boolean {
  return (p.rabattProzent ?? 0) > 0 || (p.rabattBetrag ?? 0) > 0;
}

/**
 * In welcher Art das Feld einer Zeile rechnet: in der, die schon dasteht —
 * sonst in der gewählten. Wer von Prozent auf Betrag wechseln will, leert das
 * Feld; die API nimmt die andere Art dabei zurück.
 */
function rabattArtVon(p: Position, gewaehlt: Rabattart | ""): Rabattart {
  if ((p.rabattProzent ?? 0) > 0) return "prozent";
  if ((p.rabattBetrag ?? 0) > 0) return "betrag";
  return gewaehlt || "prozent";
}

/**
 * Ob die Liste gerade eine Rabattspalte trägt. Die Eingabezeile und ihr Fuß
 * hängen im selben `<tbody>` und müssen dieselbe Zahl Zellen liefern — sie
 * lesen es hier, statt dass die Frage durch drei Zeilenarten gereicht wird.
 */
const MitRabattspalte = createContext(false);

/* ══════════════ Die Liste ══════════════ */

/**
 * Die Form der Tabelle — und damit, wie viele Zellen eine Eingabezeile hat.
 *
 * Angebot und Auftrag tragen **dieselben sechs Spalten**; im Angebot kommt
 * die Spalte mit den Knöpfen (hoch, runter, weg) dazu. Beide Zahlen stehen
 * hier und nicht zweimal: Die Eingabezeile muss dieselbe Zellenzahl liefern
 * wie der Kopf, sonst verrutscht das Raster um genau eine Spalte.
 */
export type Positionsform = "angebot" | "auftrag";

export function zellenZahl(form: Positionsform): number {
  return form === "angebot" ? 7 : 6;
}

type ListenEigenschaften = {
  /** Die Vorgangsnummer — `V-2026-0021`. */
  nr: string;
  zeilen: Position[];
  rechte: Positionsrechte;
  /**
   * `angebot` — sieben Spalten: Pos · Position · Art · Menge · Einzel ·
   * Summe · die Knöpfe. `auftrag` — dieselben sechs ohne die Knöpfe.
   *
   * **Das Ist steht nicht mehr hier.** Es kommt aus dem Protokoll und
   * gehört damit in die zweite Tabelle des Auftrags (Regel 3): Wer Plan
   * und Ist in einer Zeile liest, verwechselt sie.
   */
  form: Positionsform;
  /** Die Eingabezeile — als Zeile der Tabelle, nicht als Formular daneben. */
  eingabe?: ReactNode;
  /** Der Fuß des Blatts: die Summe, ein Satz, ein Zähler. */
  fuss?: ReactNode;
  /**
   * Die Zuschläge nach heutigem Stand — als Zeilen **unter** den Positionen,
   * nicht als Block unter der Summe. Gerechnet hat sie der Server
   * (`dienste/zuschlagserwartung.ts`); die Liste zeigt sie nur.
   */
  zuschlaege?: Erwartung | undefined;
  /** Die freigeschaltete Rabattspalte — aus `usePositionDazu`. Ohne sie nur, wo schon ein Rabatt steht. */
  rabatt?: Rabattspalte | undefined;
};

/**
 * Eine Zeile der Liste: eine Position — oder ein Zuschlag, der aus einer Regel
 * folgt.
 *
 * **Am 16.09.2026 in die Tabelle geholt.** Die Zuschläge standen als eigener
 * Block „Dazu nach heutigem Stand" unter dem Netto. Luca: *„Das muss ja als
 * Position auftauchen und nicht so ein komischer Baustein da unten. Die
 * Netto-Summe ist dann ja die Gesamtsumme, die darunter steht."* Wer ein
 * Angebot liest, liest Zeilen und eine Summe — ein zweiter Block mit einer
 * zweiten Summe ist eine Rechnung, die man selbst zusammenzählen muss.
 *
 * **Gespeichert ist die Zeile trotzdem nicht.** Ein Zuschlag bleibt eine
 * *Regel*: bei der Annahme eingefroren (Regel 4), abgerechnet aus dem Ist des
 * Rücknahmeprotokolls (Regel 3). Als `rental_order_item` gälte er im
 * Abrechnungslauf als von Hand erfasst und schlüge mit seinen Planzahlen die
 * gemessenen (`packages/fach`, `vonHand`). Darum trägt die Zeile keine `id`:
 * Ohne sie lässt sich nichts an ihr ändern, verschieben oder löschen.
 */
type Listenzeile = Position & { regel?: Erwartungszeile };

export function Positionsliste({ nr, zeilen, rechte, form, eingabe, fuss, zuschlaege, rabatt }: ListenEigenschaften) {
  const t = useT();
  const ruf = useRuf();
  const aendern = usePositionAendern();
  const loeschen = usePositionLoeschen();

  /* Was gerade in einem Feld steht, bevor es hinausgeht. Ohne diese
     Zwischenlage springt jeder Tastendruck gegen den Server. */
  const [entwuerfe, setzeEntwuerfe] = useState<Record<string, Record<string, string>>>({});
  /* Ein neuer Zeitraum schlägt einen neuen Betrag vor (F3) — je Position
     einer, bis jemand übernimmt oder „So lassen" sagt. */
  const [vorschlaege, setzeVorschlaege] = useState<Record<string, { was: string; v: Zeitraumvorschlag }>>({});

  const feldWert = (p: Position, feld: string, serverwert: string) =>
    entwuerfe[p.id ?? ""]?.[feld] ?? serverwert;

  const setzeFeld = (p: Position, feld: string, wert: string) =>
    setzeEntwuerfe((alt) => ({
      ...alt,
      [p.id ?? ""]: { ...(alt[p.id ?? ""] ?? {}), [feld]: wert },
    }));

  /* Die Alternativen stehen unter ihrer Elternposition, eingerückt — so wie
     auf dem Papier drüben. Umsortiert werden nur die Zeilen oben; ihre
     Alternativen gehen mit. */
  /* **Die abgewählte Anlieferung steht an ihrem Platz** (Paket 9, Runde 4):
     Sie gehört in die Folge der Zuschläge, an den Platz ihrer Regel — nicht
     vor sie (`lieferwegregeln.ts`, `listenfolge`). `vorne` sind die
     Positionen ohne sie, `hinten` Regelzeilen und sie, nach dem Stamm. */
  const folgeListe = useMemo(() => listenfolge(zeilen, zuschlaege?.zeilen), [zeilen, zuschlaege]);
  const vorne = folgeListe.vorne;

  const geordnet = useMemo(() => {
    const oben = vorne.filter((p) => !p.alternativeZu || !vorne.some((x) => x.id === p.alternativeZu));
    const folge: { p: Position; eingerueckt: boolean; obenIndex: number }[] = [];
    oben.forEach((p, i) => {
      folge.push({ p, eingerueckt: false, obenIndex: i });
      vorne.filter((a) => istAlternativeVon(a, p)).forEach((a) => {
        folge.push({ p: a, eingerueckt: true, obenIndex: i });
      });
    });
    return { oben, folge };
  }, [vorne]);

  const nummern = useMemo(() => positionsnummern(vorne, t), [vorne, t]);

  /* Die Spalte steht, wenn jemand sie geholt hat — oder wenn schon ein Rabatt
     dasteht. Ohne beides gibt es sie nicht. */
  const rabattArt = rabatt?.art ?? "";
  const mitRabatt = Boolean(rabattArt) || zeilen.some(hatRabatt);

  /* **Die Rabattart je Zeile** (AG-07, Durchgang 03.10.2026). Die Spalte
     fragt „Prozent oder Betrag" einmal; stand an einer Zeile schon ein
     Prozentrabatt, ließ sich an einer anderen kein Betrag mehr geben. Jetzt
     schaltet das Zeichen hinter dem Feld (% / €) die Art dieser einen Zeile
     um. Wer an einer Zeile mit 5 % auf € wechselt und 50 einträgt, ersetzt
     die 5 % — beides zugleich gibt es nicht (Migration 0106). */
  const [artJeZeile, setzeArtJeZeile] = useState<Record<string, Rabattart>>({});
  const artDerZeile = (p: Position): Rabattart =>
    (p.id ? artJeZeile[p.id] : undefined) ?? rabattArtVon(p, rabattArt);

  /* **Die Zuschläge zählen weiter, wie auf dem Papier.** Seit dem 16.09.2026
     gehen sie als Positionen mit nach Lexware — hinter den gespeicherten, und
     Lexware zählt sie dort weiter durch (`mitte/zuschlagszeilen.ts`). Luca:
     *„Das muss ja matchen mit den Positionen zum Angebot."* Die optionalen
     („O1") und die Alternativen („1.A1") zählen für sich und schieben nichts. */
  const letzteNummer = useMemo(
    () => Math.max(0, ...[...nummern.values()].filter((n) => /^\d+$/.test(n)).map(Number)),
    [nummern],
  );
  /* Die gespeicherte Zeile an einer Regelstelle zählt dort, wo sie steht. */
  const hintenStelle = useMemo(() => new Map(folgeListe.hinten.flatMap((h, i) =>
    (h.zeile?.id ? [[h.zeile.id, i] as const] : []))), [folgeListe]);
  const nummer = (p: Listenzeile) => {
    if (p.regel) return String(letzteNummer + p.nr + 1);
    const hinten = p.id ? hintenStelle.get(p.id) : undefined;
    if (hinten !== undefined) return String(letzteNummer + hinten + 1);
    return nummern.get(p.id ?? String(p.nr)) ?? "";
  };

  /* Die Zuschläge als Zeilen, in der Reihenfolge des Stamms. `nr` ist hier nur
     der Platz in dieser Folge — derselbe Code steht zweimal da, wenn zwei
     Mietpositionen dieselbe Versicherung tragen. Die abgewählte Anlieferung
     steht dazwischen, an ihrem Platz, als die gespeicherte Zeile, die sie ist. */
  const regelzeilen = useMemo<Listenzeile[]>(() => folgeListe.hinten.map((h, i) => (h.zeile ?? {
    nr: i,
    was: h.regel.bezeichnung,
    bei: "",
    artikel: "",
    menge: h.regel.menge === null ? "" : String(h.regel.menge),
    einheit: h.regel.einheit_key,
    einzel: h.regel.einzelpreis,
    summe: h.regel.netto,
    art: "zuschlag",
    regel: h.regel,
  })), [folgeListe]);

  /** Ein Feld hinausschreiben — nur, wenn es sich wirklich geändert hat. */
  async function feldSchreiben(p: Position, feld: string, wert: string) {
    if (!p.id || !nr) return;
    /* **Eine dritte Stelle wird gesagt, nicht verschluckt** (N4-06): Der
       Entwurf bleibt im Feld, rot, mit dem Satz darunter — bis dahin ging
       „12,345" still als 12,34 hinaus. */
    if ((feld === "einzel" || (feld === "rabatt" && artDerZeile(p) === "betrag")) && betragFehler(wert) !== null) return;
    setzeEntwuerfe((alt) => {
      const rest = { ...(alt[p.id as string] ?? {}) };
      delete rest[feld];
      return { ...alt, [p.id as string]: rest };
    });

    const teil: Parameters<typeof aendern.mutateAsync>[0] = { nr, id: p.id };
    if (feld === "was") {
      if (wert.trim() === p.was) return;
      teil.bezeichnung = wert.trim();
    }
    if (feld === "menge") {
      const z = alsZahl(wert);
      if (z === undefined || String(z) === p.menge) return;
      teil.menge = z;
    }
    if (feld === "einzel") {
      /* Leer heißt „zurück zum Tarif" (AG-06): Bis zum 03.10.2026 sprang das
         Feld auf die getippte Zahl zurück, und der Tarif war nur durch
         Abtippen wiederzuholen. */
      if (wert.trim() === "") teil.einzelpreis = null;
      else {
        const z = alsZahl(wert);
        if (z === undefined || z === p.einzel) return;
        teil.einzelpreis = z;
      }
    }
    if (feld === "von") {
      if (wert === (p.von ?? "")) return;
      teil.von = wert || null;
    }
    if (feld === "bis") {
      if (wert === (p.bis ?? "")) return;
      teil.bis = wert || null;
    }
    if (feld === "rabatt") {
      // Leer heißt: kein Rabatt. Die Art ist die der Zeile oder die gewählte.
      const z = alsZahl(wert) ?? 0;
      if (artDerZeile(p) === "betrag") {
        if (z === (p.rabattBetrag ?? 0)) return;
        teil.rabatt_betrag = z;
      } else {
        if (z === (p.rabattProzent ?? 0)) return;
        teil.rabatt_prozent = z;
      }
    }

    await aendern.mutateAsync(teil);
    if (teil.einzelpreis === null) ruf("posPreisTarif");
    else ruf(feld === "einzel" ? "posPreisVonHand" : "posGeaendert", undefined,
      feld === "einzel" ? "acht" : "gut");
  }

  /** Die Rolle im Angebot: in der Summe, optional, oder statt einer anderen. */
  async function rolleSchreiben(p: Position, wert: string) {
    if (!p.id || !nr) return;
    const teil: Parameters<typeof aendern.mutateAsync>[0] =
      wert === "optional" ? { nr, id: p.id, optional: true }
        : wert.startsWith("statt:") ? { nr, id: p.id, alternative_zu: wert.slice(6) }
          : { nr, id: p.id, optional: false, alternative_zu: null };
    await aendern.mutateAsync(teil);
    ruf("posGeaendert");
  }

  /**
   * Zwei Zeilen tauschen ihren Platz — ein Tausch von `sort_order`. Eine
   * eigene Route dafür wäre eine zweite Wahrheit über dieselbe Spalte.
   */
  async function verschieben(obenIndex: number, richtung: -1 | 1) {
    const a = geordnet.oben[obenIndex];
    const b = geordnet.oben[obenIndex + richtung];
    if (!a?.id || !b?.id || !nr) return;
    await aendern.mutateAsync({ nr, id: a.id, reihenfolge: b.nr });
    await aendern.mutateAsync({ nr, id: b.id, reihenfolge: a.nr });
  }

  async function positionWeg(p: Position) {
    if (!p.id || !nr) return;
    await loeschen.mutateAsync({ nr, id: p.id });
    ruf("posWeg", undefined, "stopp");
  }

  function bezeichnungsfeld(p: Position) {
    return (
      <input
        className="feld feld-zeile"
        value={feldWert(p, "was", p.was)}
        aria-label={t("angebotBauen.bezeichnung")}
        disabled={!rechte.aendern}
        title={rechte.aendern ? undefined : t(rechte.grund)}
        onChange={(e) => setzeFeld(p, "was", e.target.value)}
        onBlur={(e) => void feldSchreiben(p, "was", e.target.value)}
      />
    );
  }

  /**
   * Von und Bis einer Mietzeile — **der Zeitraumwähler der Anwendung**, nicht
   * zwei Datumsfelder des Browsers (18.09.2026). Luca: *„Nimm komplett den,
   * den du jetzt gerade gebaut hast."*
   *
   * **Geschrieben wird der Zeitraum in einem Zug.** Zwei Felder schrieben
   * Von und Bis einzeln, und dazwischen stand drüben ein halber Zeitraum —
   * ein Anfang nach dem alten Ende, den die Preisfindung als Fehler las.
   * Solange nach dem ersten Klick das Ende noch fehlt, bleibt es ein
   * Entwurf in der Zeile.
   */
  async function zeitSchreiben(p: Position, z: Spanne, schaerfe: "genau" | "kw" = "genau") {
    setzeFeld(p, "von", z.von ?? "");
    setzeFeld(p, "bis", z.bis ?? "");
    if (z.von && !z.bis) return;
    if (!p.id || !nr) return;
    setzeEntwuerfe((alt) => {
      const rest = { ...(alt[p.id as string] ?? {}) };
      delete rest["von"];
      delete rest["bis"];
      return { ...alt, [p.id as string]: rest };
    });
    /* Auch die Schärfe zählt zum Vergleich: Wer eine Woche wählt, in der die
       Zeile ohnehin schon steht, hat sie vielleicht gerade von „genau" auf
       „kw" zurückgestellt — und dann ist das eine Änderung. */
    if ((z.von ?? "") === (p.von ?? "") && (z.bis ?? "") === (p.bis ?? "")
        && schaerfe === (p.zeitschaerfe ?? "genau")) return;
    const neu = await aendern.mutateAsync({ nr, id: p.id, von: z.von, bis: z.bis, zeitschaerfe: schaerfe });
    ruf("posGeaendert");
    const id = p.id;
    setzeVorschlaege((alt) => {
      const rest = { ...alt };
      delete rest[id];
      return neu.vorschlag ? { ...rest, [id]: { was: p.was, v: neu.vorschlag } } : rest;
    });
  }

  function zeitfelder(p: Position) {
    /* **Eine Leistung trägt eine Woche, eine Miete zwei Tage** (21.09.2026).
       Bis heute trug eine Leistung gar nichts: Auf dem Angebot stand
       „PV-Reinigung, 1.240 m²" und kein Hinweis darauf, wann. Luca: *„Was
       bei der PV-Reinigung fehlt, ist eine Einordnung vom Datum, weil man ja
       auch abschätzen muss, wann das passiert."*

       Der Wähler ist ein anderer, weil die Frage eine andere ist — grob
       gegen genau, nicht schmal gegen breit. Was dahinter steht, ist
       derselbe Zeitraum (`planned_from`/`planned_until`), damit das Brett
       ihn liest wie jeden anderen. */
    if (p.art === "leistung") {
      /* Gesperrt (nach der Annahme, am versendeten Angebot) steht der Termin
         als Wert — ein Knopf, der ein Blatt öffnet und dann nicht speichert,
         wäre ein Versprechen ohne Folge. Ein genauer Termin steht mit seinen
         Tagen da, nicht als Woche (B-02). */
      if (!rechte.aendern) {
        return p.von ? <span className="pos-zeit rechenweg">{leistungstermin(p, t)}</span> : null;
      }
      return (
        <span className="pos-zeit">
          <Wochenwahl
            id={`pos-${p.id ?? "neu"}-woche`}
            wert={p.von ?? null}
            {...(p.zeitschaerfe === "kw" ? { bis: p.bis ?? null } : {})}
            {...(p.von && p.zeitschaerfe !== "kw" ? { beschriftung: leistungstermin(p, t) } : {})}
            aendern={(w) => void zeitSchreiben(p, { von: w.von, bis: w.bis }, "kw")}
            leerWort={t("positionen.wannOffen")}
          />
        </span>
      );
    }
    if (p.art !== "miete") return null;
    const von = feldWert(p, "von", p.von ?? "");
    const bis = feldWert(p, "bis", p.bis ?? "");
    return (
      <span className="pos-zeit">
        <Zeitraum
          id={`pos-${p.id ?? "neu"}-zeit`}
          klein
          wert={{ von: von || null, bis: bis || null }}
          aendern={(z) => void zeitSchreiben(p, z)}
          schild={t("allgemein.zeitraum")}
          gesperrt={!rechte.aendern}
          titel={rechte.aendern ? undefined : t(rechte.grund)}
        />
      </span>
    );
  }

  /* Die Marken an der Zeile: kein Preis (rot — so geht es nicht hinaus, wie
     es gemeint ist), Menge nach Aufmaß (gelb — das kommt noch), und außerhalb
     des Entwurfs die Rolle im Angebot. */
  function marken(p: Position) {
    const liste: ReactNode[] = [];
    /* **Was nur Auskunft ist, steht als leiser Nebentext** (Paket 9, Runde 2):
       „Menge folgt aus dem Aufmaß“, die Mindestmenge, optional, „statt Pos“.
       Marken bleiben, wo etwas so nicht hinausgehen kann oder noch fehlt. */
    const hinweise: string[] = [];
    if (mengeFolgt(p)) hinweise.push(t("positionen.mengeAufmass"));
    /* Eine Leistung ohne Termin steht in keinem Brett — das soll man an der
       Zeile sehen, nicht erst, wenn der Kunde anruft (B-02). Im Entwurf
       reicht der Knopf „Wann? (KW)", danach ist er gesperrt.
       **Auch die grob eingeplante** (W1, 24.09.2026): „KW 39" steht im
       Brett, abgesprochen ist mit dem Kunden aber noch kein Tag. */
    if (p.art === "leistung" && (!p.von || p.zeitschaerfe === "kw") && !rechte.aendern) {
      liste.push(<Marke art="acht" key="termin">{t("positionen.terminOffen")}</Marke>);
    }
    if (ohnePreis(p)) liste.push(<Marke art="stopp" key="preis">{t("positionen.keinPreis")}</Marke>);
    /* Der Satz rechnet mit dem Vorgabegrad der Leistungsart (W4, 0184) —
       gelb, weil ihn niemand gesehen hat. Dasselbe steht auf dem Papier. */
    if (p.gradAngenommen) {
      liste.push(<Marke art="acht" key="grad">{t("positionen.gradAngenommen", { grad: p.gradAngenommen })}</Marke>);
    }
    /* Die Menge steht auf der Mindestmenge der Leistungsart (E10) — meist,
       weil sie angehoben wurde. Dasselbe steht auf dem Papier. */
    if (aufMindestmenge(p)) {
      hinweise.push(t("positionen.mindestmenge", { menge: zahlAnzeige(p.mindestmenge ?? 0, 4) }));
    }
    if (form === "angebot" && !rechte.wahl) {
      if (p.optional) hinweise.push(t("optionen.markeOptional"));
      if (p.alternativeZu) hinweise.push(t("optionen.markeStatt", { nr: nummern.get(p.alternativeZu) ?? "" }));
    }
    if (!liste.length && !hinweise.length) return null;
    return (
      <>
        {hinweise.length ? <span className="pos-hinweis">{hinweise.join(` ${t("allgemein.trennpunkt")} `)}</span> : null}
        {liste.length ? <span className="pos-marke opt-marken">{liste}</span> : null}
      </>
    );
  }

  function mengenfeld(p: Position) {
    if (p.art === "text") return <span className="leise">{t("allgemein.strich")}</span>;
    return (
      <input
        className="feld feld-zahl"
        inputMode="decimal"
        value={feldWert(p, "menge", mengeFolgt(p) ? "" : p.menge.replace(".", ","))}
        placeholder={mengeFolgt(p) ? t("allgemein.strich") : undefined}
        aria-label={t("angebotBauen.spalteMenge")}
        disabled={!rechte.aendern}
        title={rechte.aendern ? undefined : t(rechte.grund)}
        onChange={(e) => setzeFeld(p, "menge", e.target.value)}
        onBlur={(e) => void feldSchreiben(p, "menge", e.target.value)}
      />
    );
  }

  function einzelfeld(p: Position) {
    if (p.art === "text") return <span className="leise">{t("allgemein.strich")}</span>;
    // Mit Komma, wie überall (N4-06: „12.34" stand mit Punkt im Feld) — und
    // mit zwei Stellen: „65,00", nicht „65" (N6-01).
    const text = feldWert(p, "einzel", ohnePreis(p) ? "" : betragFeldtext(p.einzel));
    return (
      <>
        <input
          className="feld feld-zahl"
          inputMode="decimal"
          disabled={p.eingefroren || !rechte.aendern}
          title={p.eingefroren ? t("angebotBauen.eingefroren") : rechte.aendern ? undefined : t(rechte.grund)}
          value={text}
          aria-invalid={betragFehler(text) !== null}
          placeholder={ohnePreis(p) ? t("allgemein.strich") : undefined}
          aria-label={t("angebotBauen.spalteEinzel")}
          onChange={(e) => setzeFeld(p, "einzel", e.target.value)}
          onBlur={(e) => void feldSchreiben(p, "einzel", e.target.value)}
        />
        <Betragssatz text={text} />
      </>
    );
  }

  /**
   * Das Rabattfeld einer Zeile — oder ein Strich, wo keiner hingehört. Die
   * Einheit steht daneben, damit „10" nicht als Betrag gelesen wird.
   */
  function rabattfeld(p: Listenzeile) {
    if (p.regel || p.art === "text" || !p.rabattfaehig || !p.id) {
      return (
        <span className="leise" title={p.regel || p.art === "text" ? undefined : t("rabatt.nichtRabattfaehig")}>
          {t("allgemein.strich")}
        </span>
      );
    }
    const art = artDerZeile(p);
    const bestand = art === "betrag" ? (p.rabattBetrag ?? 0) : (p.rabattProzent ?? 0);
    const darf = !p.eingefroren && rechte.aendern;
    return (
      <span className="pos-rabatt">
        <input
          className="feld feld-zahl"
          inputMode="decimal"
          disabled={p.eingefroren || !rechte.aendern}
          title={p.eingefroren ? t("angebotBauen.eingefroren") : rechte.aendern ? undefined : t(rechte.grund)}
          /* Ein Nachlass in Euro ist ein Betrag: „6,00", nicht „6" (N6-01).
             Ein Prozentsatz bleibt, wie er ist. */
          value={feldWert(p, "rabatt", !bestand ? "" : art === "betrag" ? betragFeldtext(bestand) : String(bestand).replace(".", ","))}
          placeholder={t("allgemein.strich")}
          aria-label={t(art === "betrag" ? "rabatt.schildBetrag" : "rabatt.schildProzent")}
          aria-invalid={art === "betrag" && betragFehler(feldWert(p, "rabatt", "")) !== null}
          onChange={(e) => setzeFeld(p, "rabatt", e.target.value)}
          onBlur={(e) => void feldSchreiben(p, "rabatt", e.target.value)}
        />
        {darf ? (
          <button
            type="button"
            className="pos-rabatt-art rechenweg"
            title={t("rabatt.artWechseln")}
            aria-label={t("rabatt.artWechseln")}
            onClick={() => {
              const id = p.id as string;
              setzeEntwuerfe((alt) => {
                const rest = { ...(alt[id] ?? {}) };
                delete rest["rabatt"];
                return { ...alt, [id]: rest };
              });
              setzeArtJeZeile((alt) => ({ ...alt, [id]: art === "betrag" ? "prozent" : "betrag" }));
            }}
          >
            {t(art === "betrag" ? "rabatt.einheitBetrag" : "rabatt.einheitProzent")}
          </button>
        ) : (
          <span className="rechenweg">{t(art === "betrag" ? "rabatt.einheitBetrag" : "rabatt.einheitProzent")}</span>
        )}
        {/* Ein Nachlass ist ein Betrag: dieselbe Rückmeldung wie am
            Einzel-Feld daneben (N5-02) — rot allein sagt nichts. */}
        {art === "betrag" ? <Betragssatz text={feldWert(p, "rabatt", "")} /> : null}
      </span>
    );
  }

  /** Die Summe. Was nicht zählt, steht in Klammern — wie auf dem Papier drüben. */
  function summe(p: Position) {
    if (p.summe === null || p.summe === undefined || p.art === "text") {
      return <span className="leise">{t("allgemein.strich")}</span>;
    }
    const betrag = zaehlt(p) ? euro(p.summe) : <span className="opt-klammer">{t("optionen.inKlammern", { betrag: euro(p.summe) })}</span>;
    return <><VorRabatt p={p} />{betrag}</>;
  }

  /** Die Rolle im Angebot als Feld in der Art-Spalte — nur im Entwurf. */
  function rollenfeld(p: Position) {
    if (form !== "angebot" || !rechte.wahl || !rechte.aendern || !WAEHLBAR.has(p.art ?? "") || !p.id) return null;
    const hatAlternativen = zeilen.some((x) => istAlternativeVon(x, p));
    const wert = p.optional ? "optional" : p.alternativeZu ? `statt:${p.alternativeZu}` : "summe";
    /* Wählbar als Elternposition ist, was selbst zählt und einen Betrag trägt
       — keine Kette, keine Alternative zu einer Option (packages/fach). */
    const eltern = geordnet.oben.filter((x) => x.id && x.id !== p.id && zaehlt(x) && WAEHLBAR.has(x.art ?? ""));
    return (
      <select
        className="opt-rolle"
        value={wert}
        disabled={hatAlternativen || aendern.isPending}
        title={hatAlternativen ? t("optionen.hatAlternativen") : t("optionen.rolleSchild")}
        aria-label={t("optionen.rolleSchild")}
        onChange={(e) => void rolleSchreiben(p, e.target.value)}
      >
        <option value="summe">{t("optionen.rolleSumme")}</option>
        <option value="optional">{t("optionen.rolleOptional")}</option>
        {hatAlternativen ? null : eltern.map((x) => (
          <option key={x.id} value={`statt:${x.id}`}>
            {t("optionen.rolleStatt", { nr: nummer(x), was: x.was })}
          </option>
        ))}
      </select>
    );
  }

  /** Hoch, runter, weg. Alternativen gehen mit ihrer Elternposition. */
  function knoepfe(p: Position, eingerueckt: boolean, obenIndex: number) {
    if (!rechte.aendern) return null;
    return (
      <span className="pos-weg-reihe">
        {eingerueckt ? null : (
          <>
            <button type="button" disabled={obenIndex === 0}
              onClick={() => void verschieben(obenIndex, -1)}
              title={t("angebotBauen.positionHoch")}
              aria-label={t("angebotBauen.positionHoch")}>↑</button>
            <button type="button" disabled={obenIndex === geordnet.oben.length - 1}
              onClick={() => void verschieben(obenIndex, 1)}
              title={t("angebotBauen.positionRunter")}
              aria-label={t("angebotBauen.positionRunter")}>↓</button>
          </>
        )}
        {/* Wo nicht gelöscht werden darf, steht hier NICHTS — ein grauer Knopf
            wäre ein Versprechen, das der Satz darunter zurücknimmt. */}
        {rechte.loeschen ? (
          <button type="button"
            onClick={() => void positionWeg(p)}
            title={t("angebotBauen.positionEntfernen")}
            aria-label={t("angebotBauen.positionEntfernen")}>×</button>
        ) : null}
      </span>
    );
  }

  /* Die Einrückung und der Platz in der Reihenfolge hängen an der Zeile und
     nicht an ihrem Index in der Tabelle: Eine Alternative steht unter ihrer
     Elternposition, und nur die Zeilen oben wandern beim Sortieren. */
  const lage = useMemo(() => {
    const karte = new Map<string, { eingerueckt: boolean; obenIndex: number }>();
    geordnet.folge.forEach(({ p, eingerueckt, obenIndex }) => {
      karte.set(p.id ?? String(p.nr), { eingerueckt, obenIndex });
    });
    return karte;
  }, [geordnet]);

  const schluessel = (p: Listenzeile) => (p.regel ? `regel-${p.regel.code}-${p.nr}` : p.id ?? String(p.nr));

  /* ── Die Zellen einer Zuschlagszeile ──
     Keine Felder: Die Zeile folgt aus einer Regel und wird dort geändert, im
     Stamm. Ein Feld, das sich anklicken, aber nicht speichern lässt, wäre
     schlechter als ein Wert. */

  /** Ein Prozentzuschlag nennt seinen Satz — der Betrag hängt an einer
   *  Nettomiete, die sich noch ändert. */
  function regelWas(z: Erwartungszeile) {
    return (
      <>
        <b>{z.bezeichnung}</b>
        {zuschlagsbezug(z, nummern, t)
          ? <span className="rechenweg">{zuschlagsbezug(z, nummern, t)}</span>
          : null}
        {z.prozent !== null
          ? <span className="rechenweg">{t("zuschlaege.wegProzent", { prozent: z.prozent })}</span>
          : null}
        {/* Kein Betrag heißt nicht „kostet nichts" — und die beiden Gründe
            sind verschieden: Das eine wartet auf eine Messung, das andere auf
            einen gepflegten Satz. Dieselben Marken wie an einer Position:
            gelb, was noch kommt; rot, was so nicht hinausgehen kann. */}
        {/* **Auskunft, keine Warnung** (Paket 9, Runde 2): Dass die Menge
            aus dem Protokoll kommt, ist der Normalfall eines Verbrauchs —
            leiser Nebentext wie „zu Pos 1“, keine Versalien-Marke. */}
        {z.offen === "menge_folgt"
          ? <span className="pos-hinweis">{t("zuschlaege.mengeFolgt")}</span>
          : null}
        {z.offen === "betrag_fehlt"
          ? <span className="pos-marke"><Marke art="stopp">{t("zuschlaege.betragFehlt")}</Marke></span>
          : null}
        {/* Preis immer individuell (0227, E1 — die Anlieferung): rot, weil
            die Annahme ohne ihn nicht geht; eingegeben wird er rechts. */}
        {z.offen === "preis_fehlt"
          ? <span className="pos-marke"><Marke art="stopp">{t("zuschlaege.preisFehlt")}</Marke></span>
          : null}
        {/* „Kunde holt ab" an der Anlieferung selbst (Befund 17). */}
        <AbholHaken code={z.code} />
      </>
    );
  }

  /** Die Menge, nach der sich die Einheit beugt — auch die gerade getippte. */
  function mengeFuerEinheit(p: Listenzeile): number | null {
    if (p.regel) return p.regel.menge;
    const getippt = alsZahl(feldWert(p, "menge", p.menge.replace(".", ",")));
    return getippt ?? (Number.isFinite(Number(p.menge)) ? Number(p.menge) : null);
  }

  /** Ein Prozentzuschlag hat keine Menge, die jemand lesen könnte — nur „1". */
  function regelMenge(z: Erwartungszeile) {
    if (z.menge === null || z.prozent !== null) return <span className="leise">{t("allgemein.strich")}</span>;
    return <span className="pos-regel-zahl">{z.menge}</span>;
  }

  /** Wo kein Satz gepflegt ist, steht auch keiner — „0,00" sähe aus wie gratis. */
  function regelEinzel(z: Erwartungszeile) {
    /* **Preis immer individuell** (0227, E1): Hier wird er eingegeben — eine
       Zuschlagszeile mit diesem Preis entsteht und schlägt die Regel. */
    if (z.offen === "preis_fehlt") {
      return rechte.anlegen && nr
        ? <PreisAnRegel nr={nr} z={z} />
        : <span className="leise">{t("allgemein.strich")}</span>;
    }
    if (z.offen === "betrag_fehlt" || z.prozent !== null) return <span className="leise">{t("allgemein.strich")}</span>;
    return <span className="pos-regel-zahl">{euro(z.einzelpreis)}</span>;
  }

  /* ── Die Spalten ──
     Eine Spalte ist eine Beschreibung, keine Vorlage (28-designkonzept.md).
     **Keine trägt `wert`** — und das ist Absicht: Die Reihenfolge der
     Positionen IST die Aussage der Liste, sie steht auf dem Papier drüben
     genauso, und sie wird mit den Pfeilen gesetzt. Ein Sortierknopf daneben
     wäre ein zweiter Weg zu derselben Sache, der den ersten aufhebt. */
  /* Nicht gemerkt: Jede Zelle greift auf den Entwurfsstand der Felder zu, und
     eine gemerkte Spaltenliste hielte den von vorhin fest. Die Liste ist
     sieben Beschreibungen — sie jedes Mal zu bauen kostet nichts. */
  const spalten: Spalte<Listenzeile>[] = (() => {
    const liste: Spalte<Listenzeile>[] = [
      {
        /* Eine Textzeile trägt keine Nummer — drüben auf dem Papier auch
           nicht. Die Zelle bleibt dann leer und verschwindet gestapelt ganz.
           Eine Zuschlagszeile zählt weiter (`letzteNummer`). */
        id: "nr", kopf: "angebotBauen.spaltePos", breite: "46px", klasse: "pos-nr",
        zelle: (p) => nummer(p),
      },
      {
        id: "was", kopf: "angebotBauen.spaltePosition", traeger: true, klasse: "pos-was",
        zelle: (p) => (p.regel ? regelWas(p.regel) : (
          <>
            {bezeichnungsfeld(p)}
            {/* **Die Mietzeile in drei Zeilen** (Paket 9, Luca 07.10.2026):
                der Typ, darunter das Gerät mit seinen Anbauten nebeneinander,
                darunter Zeitraum und Bediener. Bis dahin standen sieben
                Dinge untereinander — Zeitraum, Gerät, Anbau, Bediener und
                noch einmal der Anbau als Auswahl. */}
            {p.art === "miete" && !p.anbauAn
              ? <Mietangaben nr={nr} p={p} rechte={rechte} zeitraum={zeitfelder(p)} />
              : zeitfelder(p)}
            {marken(p)}
            <AbholHaken zeile={p} />
          </>
        )),
      },
      {
        id: "art", kopf: "angebotBauen.spalteArt", breite: "106px", klasse: "pos-art",
        zelle: (p) => (
          <>
            <span className="pos-art-wort">
              {t(ARTWORT[p.art ?? "leistung"] ?? "angebotBauen.artLeistung")}
            </span>
            {p.regel ? null : rollenfeld(p)}
          </>
        ),
      },
      {
        id: "menge", kopf: "angebotBauen.spalteMenge", breite: "112px", rechts: true,
        klasse: "pos-menge",
        /* **Menge und Einheit auf einer Zeile** (Paket 9, Luca 07.10.2026:
           *„Die Einheit steht nicht neben der Menge, sondern da drunter, was
           für Quatsch ist."*). Die Zahl rechtsbündig in Tabellenziffern, die
           Einheit dahinter und gebeugt — „5 Tage", „1 Tag", „120 m²". */
        zelle: (p) => (
          <span className="pos-mengeneinheit">
            {p.regel ? regelMenge(p.regel) : mengenfeld(p)}
            {/* `unit_label` ist ein i18n-Schlüssel (`einheit.tag`), kein Wort.
                Ein Prozentzuschlag hat keine Einheit — sein Satz steht an der
                Bezeichnung. Eine Textzeile auch nicht. */}
            {/* Ohne Menge auch keine Einheit: „—“, nicht „— l“. */}
            {p.einheit && p.regel?.prozent == null && p.art !== "text"
              && !(p.regel && p.regel.menge === null)
              ? <span className="pos-einheit">{einheitFuer(t, p.einheit, mengeFuerEinheit(p))}</span>
              : null}
          </span>
        ),
      },
      {
        /* Die Klasse trägt nichts auf dem Schirm — am Telefon ist sie der
           Griff, an dem die Rechenzeile diese Spalte neben Menge und Summe
           stellt (stile/vorgaenge.css, „Die Positionsliste am Telefon"). */
        id: "einzel", kopf: "angebotBauen.spalteEinzel", breite: "76px", rechts: true,
        klasse: "pos-einzel",
        zelle: (p) => (p.regel ? regelEinzel(p.regel) : einzelfeld(p)),
      },
      /* Die Rabattspalte — nur, wenn sie freigeschaltet ist oder schon ein
         Rabatt dasteht. Zwischen Einzelpreis und Summe, weil sie genau das
         verbindet. */
      ...(mitRabatt ? [{
        id: "rabatt", kopf: "rabatt.spalte", breite: "92px", rechts: true, klasse: "pos-rabatt-zelle",
        zelle: (p: Listenzeile) => rabattfeld(p),
      }] : []),
      {
        id: "summe",
        kopf: form === "angebot" ? "angebotBauen.spalteSumme" : "allgemein.netto",
        breite: "96px", rechts: true, klasse: "geld",
        zelle: (p) => summe(p),
      },
    ];
    /* Hoch, runter, weg — nur im Angebot. Im Auftrag steht die Reihenfolge
       der Positionen längst auf einer Auftragsbestätigung.

       Der Spaltenkopf ist **absichtlich leer** (`positionen.spalteOrdnen`):
       „Reihenfolge" über drei Zeichen war das breiteste Wort im Kopf und zog
       den Blick auf die unwichtigste Spalte. Die Knöpfe sagen selbst, was sie
       tun — jeder trägt `title` und `aria-label`. Dieselbe Entscheidung wie
       bei der Kästchenspalte der Auswahl. */
    if (form === "angebot") {
      liste.push({
        id: "weg", kopf: "positionen.spalteOrdnen", breite: "54px", rechts: true,
        klasse: "pos-weg",
        zelle: (p) => {
          /* An einer Regelstelle wandert nichts: Die abgewählte Anlieferung
             steht, wo ihre Regel steht, und geht mit dem Haken wieder weg. */
          if (p.regel || anRegelstelle(p)) return null;
          const wo = lage.get(schluessel(p));
          return knoepfe(p, wo?.eingerueckt ?? false, wo?.obenIndex ?? 0);
        },
      });
    }
    return liste;
  })();

  /* Leer steht im Angebot nichts: Dort sind die Karten darunter der Inhalt.
     Sobald aber eine Eingabezeile offen ist, steht die Tabelle — der
     Spaltenkopf ist genau das, was ihre Felder beschriftet. */
  if (!zeilen.length && !eingabe && form === "angebot") return null;

  return (
    <MitRabattspalte.Provider value={mitRabatt}>
    <div className="pos-blatt">
      {Object.entries(vorschlaege).map(([id, x]) => (
        <Zeitraumvorschlagband
          key={id}
          nr={nr}
          id={id}
          was={x.was}
          vorschlag={x.v}
          fertig={() => setzeVorschlaege((alt) => { const rest = { ...alt }; delete rest[id]; return rest; })}
        />
      ))}
      <Tabelle<Listenzeile>
        /* Die Zuschläge stehen unter den Positionen und über der
           Eingabezeile: Sie folgen aus dem, was darüber steht, und eine neue
           Position kommt zu den Positionen dazu, nicht hinter die Zuschläge. */
        zeilen={[...geordnet.folge.map((f) => f.p), ...regelzeilen]}
        spalten={spalten}
        id={schluessel}
        schild="positionen.schild"
        zeilenklasse={(p) => {
          const wo = lage.get(schluessel(p));
          return [
            "pos-bearb",
            p.regel ? "pos-regel" : "",
            zaehlt(p) ? "" : "opt-zeile",
            wo?.eingerueckt ? "opt-alternative" : "",
          ].filter(Boolean).join(" ");
        }}
        {...(eingabe ? { anhang: eingabe } : {})}
        {...(fuss ? { fuss } : {})}
        leer={<p>{t("angebotBauen.nochKeinePositionen")}</p>}
        /* Die festen Spalten plus das, was die Bezeichnung mindestens
           braucht. Gemessen bei 1280 px: Dort bleiben in der Mitte 722 px,
           und die Liste passt ohne seitliches Scrollen hinein. Der Schatten
           am Rand ist die Absicherung, nicht der Regelfall. */
        mindestbreite={(form === "angebot" ? 736 : 666) + (mitRabatt ? 92 : 0)}
        /* Keine Karte am Telefon: Die Positionszeile ordnet sich dort schon
           selbst — Bezeichnung, Zeitraum, Rechenzeile, Ordnungsknöpfe
           (`stile/vorgaenge.css`, „Die Positionsliste"). */
        karte={false}
      />

      {aendern.isError ? <FehlerBand pfad={positionsFehlerPfad(aendern.error)} werte={fehlerWerte(aendern.error)} /> : null}
      {loeschen.isError ? <FehlerBand pfad={positionsFehlerPfad(loeschen.error)} werte={fehlerWerte(loeschen.error)} /> : null}
    </div>
    </MitRabattspalte.Provider>
  );
}

/* ══════════════ Die Positionen am Telefon: lesbare Karten (17.09.2026) ══════════════

   Luca: *„komplett mobile-fähig … ein komplettes mobiles Interface, welches
   vollumfänglich funktioniert."* Die Positionsliste ist am Schirm eine
   Tabelle aus Feldern — und am Telefon wurde daraus ein Formular: Datum,
   Datum, Haken, Bedienerwahl, Menge, Satz, jedes 44 px, eine Mietposition
   über einen halben Schirm. **Gelesen wird aber öfter als geändert**, und wer
   am Telefon wissen will, was verkauft ist, will keine Felder abzählen.

   Also zeigt das Telefon **Karten**: oben Nummer und Bezeichnung, darunter
   Zeitraum und Bediener, dann die Rechenzeile — Art · Menge · Satz · Netto
   nebeneinander, so wie die Zeile drüben auf dem Beleg steht. Die Zuschläge
   stehen als eigene Karten darunter, die Summe im Fuß. **Geändert wird in
   der Liste, die es schon gibt:** Ein Griff auf eine Karte schaltet die Maske
   in die Bearbeitung und rückt die Zeile ins Bild (`oeffnen`). Zwei Formen
   derselben Eingabe laufen auseinander; zwei Ansichten derselben Zeile nicht.

   Die Ordnung ist die der Liste: Alternativen unter ihrer Elternposition,
   dieselben Nummern (`positionsnummern`), was nicht zählt, in Klammern. */

export function Positionskarten({ nr, zeilen, zuschlaege, form, oeffnen, fuss }: {
  /** Der Vorgang — ohne ihn stehen die Geräte an der Karte nicht, denn ihr Klick führt ins Brett zu ihm. */
  nr?: string | undefined;
  zeilen: Position[];
  zuschlaege?: Erwartung | undefined;
  form: Positionsform;
  /** Eine Karte antippen: in die Bearbeitung, an diese Zeile. Fehlt er, ist die Karte nur zu lesen. */
  oeffnen?: ((p: Position) => void) | undefined;
  fuss?: ReactNode;
}) {
  const t = useT();
  /* Dieselbe Folge wie am Schreibtisch: die abgewählte Anlieferung an ihrem
     Regelplatz zwischen den Zuschlägen (`lieferwegregeln.ts`). */
  const { vorne, hinten } = useMemo(() => listenfolge(zeilen, zuschlaege?.zeilen), [zeilen, zuschlaege]);
  const nummern = useMemo(() => positionsnummern(vorne, t), [vorne, t]);
  const folge = useMemo(() => {
    const oben = vorne.filter((p) => !p.alternativeZu || !vorne.some((x) => x.id === p.alternativeZu));
    return oben.flatMap((p) => [
      { p, eingerueckt: false },
      ...vorne.filter((a) => istAlternativeVon(a, p)).map((a) => ({ p: a, eingerueckt: true })),
    ]);
  }, [vorne]);
  const regeln = hinten.flatMap((h) => (h.regel ? [h.regel] : []));
  /* Die Karten in ihrer Folge: erst die Positionen, dann die Zuschläge mit
     der gespeicherten Zeile an ihrem Platz. */
  const reihe: ({ p: Position; eingerueckt: boolean; z?: undefined; i?: undefined }
    | { z: Erwartungszeile; i: number; p?: undefined; eingerueckt?: undefined })[] = [
    ...folge,
    ...hinten.map((h, i) => (h.zeile ? { p: h.zeile, eingerueckt: false } : { z: h.regel, i })),
  ];
  const mitRabatt = zeilen.some(hatRabatt);

  if (!zeilen.length && !regeln.length) {
    return <p className="pos-karten-leer">{t("angebotBauen.nochKeinePositionen")}</p>;
  }

  /* Die Zuschläge: dieselbe Karte, leiser, ohne Griff — sie werden im
     Stamm geändert, nicht hier. */
  const regelkarte = (z: Erwartungszeile, i: number) => (
    <article key={`regel-${z.code}-${i}`} className="pos-karte regel">
      <div className="pos-karte-kopf">
        <b>{z.bezeichnung}</b>
      </div>
      {zuschlagsbezug(z, nummern, t) ? <p className="pos-karte-bei">{zuschlagsbezug(z, nummern, t)}</p> : null}
      {z.prozent !== null ? (
        <p className="pos-karte-bei">{t("zuschlaege.wegProzent", { prozent: z.prozent })}</p>
      ) : null}
      {z.offen === "menge_folgt" ? <p className="pos-karte-bei">{t(offenWort(z.offen))}</p> : z.offen ? (
        <span className="pos-karte-marken">
          <Marke art="stopp">{t(offenWort(z.offen))}</Marke>
        </span>
      ) : null}
      {z.offen === "preis_fehlt" && oeffnen && nr ? <PreisAnRegel nr={nr} z={z} /> : null}
      <AbholHaken code={z.code} />
      <dl className="pos-karte-rechnung">
        <div>
          <dt>{t("angebotBauen.spalteArt")}</dt>
          <dd>{t(ARTWORT.zuschlag ?? "angebotBauen.artLeistung")}</dd>
        </div>
        <div>
          <dt>{t("angebotBauen.spalteMenge")}</dt>
          <dd>
            {z.menge === null || z.prozent !== null ? t("allgemein.strich") : z.menge}
            {z.einheit_key && z.prozent === null && z.menge !== null ? <span className="rechenweg"> {einheitFuer(t, z.einheit_key, z.menge)}</span> : null}
          </dd>
        </div>
        <div>
          <dt>{t("angebotBauen.spalteEinzel")}</dt>
          <dd>{z.offen === "betrag_fehlt" || z.offen === "preis_fehlt" || z.prozent !== null ? t("allgemein.strich") : euro(z.einzelpreis)}</dd>
        </div>
        <div className="pos-karte-summe">
          <dt>{t(form === "angebot" ? "angebotBauen.spalteSumme" : "allgemein.netto")}</dt>
          <dd>{z.netto === null ? t("allgemein.strich") : euro(z.netto)}</dd>
        </div>
      </dl>
    </article>
  );

  const trenn = ` ${t("allgemein.trennpunkt")} `;

  return (
    <div className="pos-karten">
      {reihe.map(({ p, eingerueckt, z, i }) => {
        if (z) return regelkarte(z, i);
        const nummer = nummern.get(p.id ?? String(p.nr)) ?? "";
        const bei = [
          p.art === "miete" && (p.von || p.bis)
            ? t("positionen.karte.zeitraum", { von: tagKurz(p.von ?? null), bis: tagKurz(p.bis ?? null) })
            : "",
          /* **Die Leistung sagt ihre Woche** (21.09.2026). „KW 39 ·
             21.09.–27.09." statt zweier Daten: Der Zeitraum dahinter ist
             derselbe, die Auskunft eine andere — grob geplant und nicht
             zugesagt. */
          p.art === "leistung"
            ? (p.von ? leistungstermin(p, t) : t("positionen.terminOffen"))
            : "",
          p.bediener ? t("positionen.karte.bediener", { name: p.bediener.name })
            : p.bedienungspflicht === "nur_mit_person" || (p.bedienungspflicht === "wahlweise" && p.bedient)
              ? t("positionen.bedienerFehlt") : "",
          form === "angebot" && p.optional ? t("optionen.markeOptional") : "",
          form === "angebot" && p.alternativeZu
            ? t("optionen.markeStatt", { nr: nummern.get(p.alternativeZu) ?? "" }) : "",
        ].filter(Boolean).join(trenn);
        const rabatt = p.rabattBetrag ? euro(p.rabattBetrag)
          : p.rabattProzent ? t("positionen.karte.prozent", { zahl: String(p.rabattProzent).replace(".", ",") }) : "";
        const kopf = (
          <>
            {nummer ? <span className="pos-karte-nr">{nummer}</span> : null}
            <b>{p.was}</b>
          </>
        );
        return (
          <article
            key={p.id ?? p.nr}
            className={`pos-karte${zaehlt(p) ? "" : " opt"}${eingerueckt ? " alt" : ""}`}
          >
            {oeffnen && p.id ? (
              <button
                type="button"
                className="pos-karte-kopf"
                onClick={() => oeffnen(p)}
                aria-label={t("positionen.karte.bearbeiten", { was: p.was })}
              >
                {kopf}
              </button>
            ) : (
              <div className="pos-karte-kopf">{kopf}</div>
            )}
            {bei ? <p className="pos-karte-bei">{bei}</p> : null}
            {nr ? <Geraete nr={nr} p={p} fehltZeigen={false} /> : null}
            <AbholHaken zeile={p} />
            {/* Auskunft als Nebentext, Warnung als Marke (Paket 9, Runde 2). */}
            {mengeFolgt(p) || aufMindestmenge(p) ? (
              <p className="pos-karte-bei">
                {[
                  mengeFolgt(p) ? t("positionen.mengeAufmass") : "",
                  aufMindestmenge(p) ? t("positionen.mindestmenge", { menge: zahlAnzeige(p.mindestmenge ?? 0, 4) }) : "",
                ].filter(Boolean).join(` ${t("allgemein.trennpunkt")} `)}
              </p>
            ) : null}
            {ohnePreis(p) || p.gradAngenommen ? (
              <span className="pos-karte-marken">
                {ohnePreis(p) ? <Marke art="stopp">{t("positionen.keinPreis")}</Marke> : null}
                {p.gradAngenommen
                  ? <Marke art="acht">{t("positionen.gradAngenommen", { grad: p.gradAngenommen })}</Marke>
                  : null}
              </span>
            ) : null}
            {p.art === "text" ? null : (
              <dl className="pos-karte-rechnung">
                <div>
                  <dt>{t("angebotBauen.spalteArt")}</dt>
                  <dd>{t(ARTWORT[p.art ?? "leistung"] ?? "angebotBauen.artLeistung")}</dd>
                </div>
                <div>
                  <dt>{t("angebotBauen.spalteMenge")}</dt>
                  <dd>
                    {mengeFolgt(p) ? t("allgemein.strich") : p.menge.replace(".", ",")}
                    {p.einheit ? <span className="rechenweg"> {einheitFuer(t, p.einheit, Number(p.menge))}</span> : null}
                    {/* Gezählter Bestand (0194): die Summe ist Tage × Satz × Stück. */}
                    {p.stueck ? <span className="rechenweg"> {t("bestand.malStueck", { zahl: p.stueck })}</span> : null}
                  </dd>
                </div>
                <div>
                  <dt>{t("angebotBauen.spalteEinzel")}</dt>
                  <dd>{ohnePreis(p) || p.einzel === null || p.einzel === undefined ? t("allgemein.strich") : euro(p.einzel)}</dd>
                </div>
                {mitRabatt ? (
                  <div>
                    <dt>{t("rabatt.spalte")}</dt>
                    <dd>{rabatt || t("allgemein.strich")}</dd>
                  </div>
                ) : null}
                <div className="pos-karte-summe">
                  <dt>{t(form === "angebot" ? "angebotBauen.spalteSumme" : "allgemein.netto")}</dt>
                  <dd>
                    {p.summe === null || p.summe === undefined ? null : <VorRabatt p={p} />}
                    {p.summe === null || p.summe === undefined ? t("allgemein.strich")
                      : zaehlt(p) ? euro(p.summe) : t("optionen.inKlammern", { betrag: euro(p.summe) })}
                  </dd>
                </div>
              </dl>
            )}
          </article>
        );
      })}

      {fuss ? <div className="pos-karten-fuss">{fuss}</div> : null}
    </div>
  );
}

/* ══════════════ Die Positionen einklappen (Durchgang 16.09.2026) ══════════════

   Luca: *„Vortext und Nachtext können auch direkt dastehen, wenn ich eine
   Angebotsmaske öffne, und dann müssen die Positionen ausklappbar sein."*

   Der Tausch: Die Texte stehen offen (`Belegtext`), die Positionen bekommen
   dafür einen Weg, sie wegzuklappen. Die Kopfzeile sagt, was darunter steht —
   **Zahl und Summe**, damit ein zugeklappter Block keine Auskunft schuldig
   bleibt. Sie bleibt ganz weg, solange es keine Position gibt: Dort ist die
   Reihe der Karten darunter der Inhalt, und eine Kopfzeile über nichts wäre
   ein leeres Versprechen.

   **Eingeklappt gehen auch die Karten mit.** Sie sind das Eingabeende
   derselben Sache; eine zugeklappte Liste über vier offenen Karten sähe aus,
   als wäre etwas kaputt. */

/**
 * Ob der Block eingeklappt ist — je Browser gemerkt, nicht je Vorgang: Wer so
 * arbeitet, arbeitet an jedem Angebot so.
 *
 * `localStorage` kann fehlen oder werfen (privates Fenster, gesperrte
 * Seitendaten). Dann ist der Block eben offen — das ist der Regelfall.
 */
function useEingeklappt(schluessel: string): [boolean, (zu: boolean) => void] {
  const [zu, setzeZu] = useState(() => {
    try {
      return window.localStorage.getItem(schluessel) === "zu";
    } catch {
      return false;
    }
  });
  const setzen = (neu: boolean) => {
    setzeZu(neu);
    try {
      window.localStorage.setItem(schluessel, neu ? "zu" : "auf");
    } catch {
      // Absichtlich still: Der Zustand gilt dann nur für diese Sitzung.
    }
  };
  return [zu, setzen];
}

/** Die Kopfzeile über den Positionen — Zahl, Summe, und der Klick, der zuklappt. */
export function Positionsklappe({ zahl, summe, children }: {
  zahl: number;
  /** Netto der Positionen; `undefined` heißt: keine Summe zeigen. */
  summe?: number | undefined;
  children: ReactNode;
}) {
  const t = useT();
  const id = useId();
  const [zu, setzeZu] = useEingeklappt("mm.positionen.zu");
  if (!zahl) return <>{children}</>;
  return (
    <>
      <div className="pos-teil-kopf">
        <button
          type="button"
          className="pos-klappe"
          aria-expanded={!zu}
          aria-controls={id}
          title={t(zu ? "positionen.klappen.auf" : "positionen.klappen.zu")}
          onClick={() => setzeZu(!zu)}
        >
          <span className="pos-klappe-pfeil" aria-hidden="true">{zu ? "▸" : "▾"}</span>
          <h4>{t("positionen.titel")}</h4>
          <span className="zaehler">{zahl}</span>
          {summe === undefined ? null : (
            <span className="pos-klappe-summe">
              {t("positionen.klappen.summe", { betrag: euro(summe) })}
            </span>
          )}
        </button>
      </div>
      <div id={id} hidden={zu}>{children}</div>
    </>
  );
}

/* ══════════════ Die Auswahl aus einem Katalog ══════════════ */

export type Katalogeintrag = {
  id: string;
  titel: string;
  unten?: string | undefined;
  /** Rechts: Preis je Einheit, Bestand, eine Marke. */
  rand?: ReactNode;
  /** Für die Pillen darüber — nur bei den Maschinentypen. */
  gruppe?: string | undefined;
};

/**
 * Suchfeld und Liste, sofort offen.
 *
 * Wer „+ Leistung" klickt, will wählen und nicht erst ein Feld öffnen. Die
 * Liste steht **ohne Suchwort** da — bei drei Leistungsarten braucht niemand
 * zu tippen, bei vierzig Maschinentypen die Pillen. Pfeile und Enter wählen,
 * Escape bricht ab.
 */
/**
 * **Am Telefon fokussiert nichts von selbst ein Eingabefeld** (18.09.2026).
 *
 * Luca, beim Durchklicken der Handy-Ansicht: *„auf jeden Fall nicht die
 * Tastatur triggern, weil das einen verrückt macht."* `Blattvonunten` hält
 * sich seitdem daran; die Positionszeilen und der Katalogwähler nicht — und
 * die stehen unter `/angebote/$nr` am Telefon auf demselben Schirm.
 *
 * **Am Schreibtisch bleibt der Erstfokus**, und zwar mit Absicht: Wer dort
 * eine Zeile aufmacht, tippt weiter. Es gibt keine Tastatur, die etwas
 * zudecken könnte, und ein Klick weniger ist ein Klick weniger.
 *
 * Der Haken ist die eine Stelle, an der das steht — sechs `autoFocus` mit
 * sechs Begründungen laufen auseinander.
 */
const useErstfokus = (): boolean => !useTelefon();

export function Katalogwahl({
  eintraege, platz, leerSatz, kopf, fuss, waehlen, abbrechen, gruppen, zuletzt, zuletztWort,
  sichtbar,
}: {
  eintraege: Katalogeintrag[];
  platz: string;
  leerSatz: (wort: string) => string;
  /**
   * Über dem Suchfeld — **der Zeitraum, bevor das Gerät gewählt ist**
   * (Luca, 21.09.2026).
   *
   * *„Da muss man erst das Gerät wählen und dann den Zeitraum. Es ist
   * eigentlich dumm, dass man in der gleichen Maske machen muss, um zu
   * sehen, welches Gerät verfügbar ist. Mach die möglichen Zeiträume
   * einstellbar, sodass man sieht, was verfügbar ist."*
   *
   * Der Wähler beantwortet damit die Frage, mit der man ihn öffnet — „was
   * habt ihr in der Woche" —, statt sie auf den zweiten Schritt zu
   * vertagen. Er ist ein Slot und kein festes Feld: Leistung und Ware
   * fragen nicht nach einem Zeitraum, und ein leerer Kasten über ihrer
   * Liste wäre eine Frage ohne Antwort.
   */
  kopf?: ReactNode;
  fuss?: ReactNode;
  waehlen: (id: string) => void;
  abbrechen: () => void;
  gruppen?: string[] | undefined;
  /**
   * Meldet, welche Einträge gerade in der Liste stehen — damit der Aufrufer
   * **für genau die** die Lage holen kann und nicht für den ganzen Stamm.
   *
   * Die Filterung liegt hier (Suchwort, Pille), also kann nur diese Stelle
   * sie beantworten. Gekappt wird nicht: `GET /nachschlagen` nimmt bis zu
   * vierzig Typen je Frage, und mehr liest ohnehin niemand in einer Liste.
   */
  sichtbar?: ((ids: string[]) => void) | undefined;
  /**
   * Was oben stehen soll, bevor man sucht — ein paar Kennungen aus
   * `eintraege`, in der Reihenfolge, in der sie angeboten werden.
   *
   * **Es ist ein Vorschlag, keine zweite Liste.** Er steht IM Wähler und
   * nicht daneben: Dieselbe Zeilenform, dieselbe Tastatur, dieselbe
   * Höhe — die Liste rollt ohnehin (`max-height`), also wächst nichts.
   * Sobald jemand tippt oder eine Gruppe wählt, ist er weg und der
   * gewöhnliche Weg steht frei.
   */
  zuletzt?: string[] | undefined;
  /** Die Überschrift über dem Vorschlag. */
  zuletztWort?: string | undefined;
}) {
  const t = useT();
  const id = useId();
  const [wort, setzeWort] = useState("");
  const [gruppe, setzeGruppe] = useState<string | null>(null);
  const [markiert, setzeMarkiert] = useState(0);
  const wahlRaum = useRef<HTMLDivElement>(null);
  const erstfokus = useErstfokus();

  /* **Die ganze Wahl ins Bild, nicht nur das Suchfeld.** Die Eingabezeile
     hängt unten an der Tabelle; `autoFocus` rollt nur das Feld hinein, und
     die Liste darunter lag unter dem Rand. Luca, 16.09.2026: *„Wenn ich
     eine Maschine auswählen muss, muss ich komischerweise scrollen."*
     `nearest` rollt nur, wenn etwas fehlt — steht alles da, bleibt es. */
  useEffect(() => {
    wahlRaum.current?.scrollIntoView({ block: "nearest" });
  }, []);

  const suchwort = wort.trim().toLowerCase();
  const gefiltert = eintraege
    .filter((e) => !gruppe || e.gruppe === gruppe)
    .filter((e) => !suchwort || `${e.titel} ${e.unten ?? ""}`.toLowerCase().includes(suchwort));

  /* Der Vorschlag steht nur im Ruhezustand: Wer tippt, sucht etwas
     Bestimmtes, und dann wäre eine Vorschlagsgruppe oben nur ein Umweg. */
  const vorschlag = !suchwort && !gruppe && zuletzt?.length
    ? zuletzt.map((k) => gefiltert.find((e) => e.id === k)).filter((e): e is Katalogeintrag => !!e)
    : [];
  const treffer = vorschlag.length
    ? [...vorschlag, ...gefiltert.filter((e) => !vorschlag.some((v) => v.id === e.id))]
    : gefiltert;

  useEffect(() => { setzeMarkiert(0); }, [suchwort, gruppe]);

  /* Die sichtbaren Kennungen nach außen — als Zeichenkette verglichen, damit
     eine neu gebaute Liste mit demselben Inhalt keine zweite Abfrage auslöst. */
  const trefferIds = treffer.map((e) => e.id).join(",");
  useEffect(() => {
    sichtbar?.(trefferIds ? trefferIds.split(",") : []);
    // `sichtbar` ist der Aufrufer und wechselt bei jedem Rendern die Kennung.
  }, [trefferIds]);

  function taste(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") { e.preventDefault(); setzeMarkiert((m) => Math.min(m + 1, treffer.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setzeMarkiert((m) => Math.max(m - 1, 0)); }
    if (e.key === "Enter") {
      e.preventDefault();
      const gewaehlt = treffer[markiert];
      if (gewaehlt) waehlen(gewaehlt.id);
    }
    if (e.key === "Escape") { e.preventDefault(); abbrechen(); }
  }

  return (
    <div className="pos-neu-wahl" ref={wahlRaum}>
      {kopf ? <div className="pos-neu-wahl-kopf">{kopf}</div> : null}
      <input
        className="feld pos-neu-suche"
        type="search"
        autoFocus={erstfokus}
        autoComplete="off"
        role="combobox"
        aria-expanded="true"
        aria-controls={`${id}-liste`}
        aria-activedescendant={treffer[markiert] ? `${id}-${markiert}` : undefined}
        value={wort}
        placeholder={platz}
        aria-label={platz}
        onChange={(e) => setzeWort(e.target.value)}
        onKeyDown={taste}
      />
      {gruppen && gruppen.length > 1 ? (
        <div className="pos-neu-gruppen">
          <button className="pille" type="button" aria-pressed={!gruppe} onClick={() => setzeGruppe(null)}>
            {t("allgemein.alle")}
          </button>
          {gruppen.map((g) => (
            <button className="pille" type="button" key={g} aria-pressed={gruppe === g}
              onClick={() => setzeGruppe(gruppe === g ? null : g)}>
              {g}
            </button>
          ))}
        </div>
      ) : null}
      <div className="pos-neu-liste" role="listbox" id={`${id}-liste`}>
        {treffer.length ? treffer.map((e, i) => (
          <Fragment key={e.id}>
            {/* Zwei Überschriften, und nur wenn es einen Vorschlag gibt:
                oben, was der Kunde zuletzt hatte, darunter der Katalog. */}
            {vorschlag.length && i === 0 ? (
              <span className="pos-neu-kopfzeile">{t(zuletztWort ?? "positionen.zuletzt")}</span>
            ) : null}
            {vorschlag.length && i === vorschlag.length ? (
              <span className="pos-neu-kopfzeile">{t("positionen.alleWeiteren")}</span>
            ) : null}
          <button
            type="button"
            role="option"
            id={`${id}-${i}`}
            aria-selected={i === markiert}
            className={`pos-neu-eintrag${i === markiert ? " markiert" : ""}`}
            onPointerEnter={(e) => { if (e.pointerType === "mouse") setzeMarkiert(i); }}
            onClick={() => waehlen(e.id)}
          >
            <span className="pos-neu-wort">
              <b>{e.titel}</b>
              {e.unten ? <span>{e.unten}</span> : null}
            </span>
            {e.rand ? <span className="pos-neu-rand">{e.rand}</span> : null}
          </button>
          </Fragment>
        )) : (
          <p className="blatt-satz pos-neu-leer">{leerSatz(wort.trim())}</p>
        )}
      </div>
      {fuss ? <div className="pos-neu-liste-fuss">{fuss}</div> : null}
    </div>
  );
}

/* ══════════════ Textbausteine — der Schnellzugriff ══════════════ */

/**
 * Ein gespeicherter Satz als Zeile im Wähler: Stichwort oben, der Anfang des
 * Textes darunter.
 *
 * Der Text wird gekürzt und nicht umgebrochen — die Liste ist zum Wiederfinden
 * da, nicht zum Lesen. Wer den ganzen Satz sehen will, wählt ihn: Danach steht
 * er im Feld und ist dort noch änderbar.
 */
const BAUSTEIN_VORSCHAU = 90;

function bausteinEintraege(liste: Textbaustein[], t: (k: string, w?: Record<string, string | number>) => string): Katalogeintrag[] {
  return liste.map((b) => ({
    id: b.id,
    titel: b.stichwort,
    unten: b.text.length > BAUSTEIN_VORSCHAU
      ? t("textbausteine.gekuerzt", { anfang: b.text.slice(0, BAUSTEIN_VORSCHAU).trimEnd() })
      : b.text,
    // Die Zahl steht nur da, wenn sie etwas sagt. „0" bei jedem zweiten
    // Eintrag wäre eine Spalte, die immer dasselbe meldet.
    rand: b.benutzt > 0
      ? <span className="nr">{t("textbausteine.benutztKurz", { zahl: b.benutzt })}</span>
      : undefined,
  }));
}

/**
 * Baustein wählen und Baustein sichern — beides an einem Feld.
 *
 * Luca, 16.09.2026: *„Wir wollen Freitexte auch während der Anlage speichern
 * können und dann den Schnellzugriff auf diese abgespeicherten Schnelltexte
 * haben."*
 *
 * Also zwei Knöpfe und kein Weg aus der Maske heraus:
 *
 * - **Wählen** klappt die Liste der gespeicherten Sätze auf (derselbe
 *   `Katalogwahl` wie bei Leistung und Ware — suchen, Pfeile, Enter). Der
 *   gewählte Text steht danach im Feld und ist dort **noch änderbar**: Ein
 *   Baustein ist eine Vorlage, keine Bindung.
 * - **Sichern** nimmt, was gerade im Feld steht, fragt nach dem Stichwort und
 *   legt es an. Danach steht der Satz für alle im Schnellzugriff.
 *
 * Gesichert wird nur, was da ist: Ohne Text kein Knopf. Und ist der Text
 * Zeichen für Zeichen einer, der schon gespeichert ist, steht der Knopf auch
 * nicht da — zweimal derselbe Satz unter zwei Stichwörtern ist der Anfang
 * einer Liste, die niemand mehr pflegt.
 *
 * **Vor- und Nachtext tragen ihre Belegart mit** (Migration 0105): Am
 * Lieferschein stehen nur die Sätze des Lieferscheins zur Wahl, und was dort
 * gesichert wird, wird ein Baustein des Lieferscheins. Ein Positionstext hat
 * keine Belegart.
 */
export function Bausteinzugriff({ art, beleg, wert, einsetzen, klein }: {
  art: Bausteinart;
  /** Nur bei Vor- und Nachtext — zu welchem Beleg der Satz gehört. */
  beleg?: Belegart | undefined;
  /** Was gerade im Feld steht — die Vorlage für „sichern". */
  wert: string;
  einsetzen: (text: string) => void;
  /** In einer Tabellenzeile ist weniger Platz als unter einem Textfeld. */
  klein?: boolean | undefined;
}) {
  const t = useT();
  const erstfokus = useErstfokus();
  const ruf = useRuf();
  const { data } = useTextbausteine(art, true, beleg);
  const anlegen = useTextbausteinAnlegen();
  const benutzt = useBausteinBenutzt();

  const [offen, setzeOffen] = useState<"" | "waehlen" | "sichern">("");
  const [stichwort, setzeStichwort] = useState("");

  const bausteine = useMemo(() => data?.zeilen ?? [], [data]);
  const inhalt = wert.trim();
  const schonDa = bausteine.some((b) => b.text.trim() === inhalt);

  function waehlen(id: string) {
    const b = bausteine.find((x) => x.id === id);
    if (!b) return;
    /* Eingefügt, nicht ersetzt (Paket 9): Was schon im Feld steht, bleibt,
       und der Baustein kommt dahinter (`bausteinEinfuegen`). */
    einsetzen(bausteinEinfuegen(wert, b.text));
    benutzt(b.id);
    setzeOffen("");
  }

  function sichern() {
    const kurz = stichwort.trim();
    if (!kurz || !inhalt) return;
    anlegen.mutate({ stichwort: kurz, text: inhalt, art, ...(beleg ? { beleg } : {}) }, {
      onSuccess: () => {
        setzeStichwort("");
        setzeOffen("");
        ruf("bausteinGesichert", { stichwort: kurz });
      },
    });
  }

  return (
    <div className={`baustein-zugriff${klein ? " klein" : ""}`}>
      <div className="baustein-knoepfe">
        <button
          type="button"
          className="baustein-knopf"
          aria-expanded={offen === "waehlen"}
          onClick={() => setzeOffen(offen === "waehlen" ? "" : "waehlen")}
        >
          {t("textbausteine.waehlen")}
          {bausteine.length ? <span className="texte-zahl">{bausteine.length}</span> : null}
        </button>
        {inhalt && !schonDa ? (
          <button
            type="button"
            className="baustein-knopf"
            aria-expanded={offen === "sichern"}
            onClick={() => setzeOffen(offen === "sichern" ? "" : "sichern")}
          >
            {t("textbausteine.sichern")}
          </button>
        ) : null}
        {/* Der Satz steht schon im Katalog — das ist eine Feststellung und
            kein Knopf, der nichts täte. */}
        {inhalt && schonDa ? <Marke art="gut">{t("textbausteine.istBaustein")}</Marke> : null}
      </div>

      {offen === "waehlen" ? (
        <Katalogwahl
          eintraege={bausteinEintraege(bausteine, t)}
          platz={t("textbausteine.suche")}
          leerSatz={(suchwort) => (bausteine.length
            ? t("textbausteine.keiner", { wort: suchwort })
            : t(`textbausteine.leer.${art}`))}
          waehlen={waehlen}
          abbrechen={() => setzeOffen("")}
        />
      ) : null}

      {offen === "sichern" ? (
        <div className="baustein-sichern">
          <input
            className="feld"
            autoFocus={erstfokus}
            value={stichwort}
            maxLength={80}
            placeholder={t("textbausteine.stichwortPlatz")}
            aria-label={t("textbausteine.stichwort")}
            onChange={(e) => setzeStichwort(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); sichern(); }
              if (e.key === "Escape") { e.preventDefault(); setzeOffen(""); }
            }}
          />
          <Knopf art="haupt" klein disabled={!stichwort.trim() || anlegen.isPending} onClick={sichern}>
            {t("textbausteine.sichernJetzt")}
          </Knopf>
          <Knopf art="stumm" klein onClick={() => setzeOffen("")}>{t("allgemein.abbrechen")}</Knopf>
          {anlegen.isError ? <FehlerBand pfad={positionsFehlerPfad(anlegen.error)} fehler={anlegen.error} /> : null}
        </div>
      ) : null}
    </div>
  );
}

/* ══════════════ Wer fährt ══════════════ */

/** `2026-09-22 18:00:00+00` oder `2026-09-22` → `22.09.` */
const tagKurz = (iso: string | null): string => tagKurzImBetrieb(iso); // deutscher Tag (N8-02)

/**
 * Das Gerät an der Miete — und was daran hängt.
 *
 * Befund vom 18.09.2026 (V-2026-0006): Im Brett stand der Tieflöffel, der
 * Bagger nicht, und am Vorgang war keins von beiden zu sehen. Die Zeile trug
 * nur den Bediener. Jetzt steht hier, was gebucht ist: der Träger zuerst,
 * dann Anbau und Zubehör, jedes mit seiner Kennung.
 *
 * **Ein Klick klappt am Ort auf** (Paket 9, 07.10.2026). Bis dahin führte er
 * ins Brett, und wer nur nachsehen wollte, stand in einer anderen Maske. Die
 * Planung ist jetzt ein ausdrücklicher Verweis im Blatt der Marke
 * (`Geraeteklappe`).
 *
 * **Fehlt der Träger, sagt die Zeile es** — aber nur, solange der Vorgang
 * offen ist (`fehltZeigen`). An einem stornierten Auftrag sind die Belegungen
 * weg, und „kein Gerät" wäre dort keine Auskunft, sondern Lärm. Warum er
 * fehlen kann: Ist beim Anlegen nichts frei, entsteht die Position trotzdem,
 * und die Annahme bucht nach, was dann frei ist (dienste/belegung.ts,
 * `gespannNachbuchen`). Was danach noch fehlt, steht zusätzlich als Band über
 * dem Auftrag.
 *
 * Diese Fassung liest nur — die Karten (`Positionskarten`). In der Liste
 * steht `Mietangaben`, mit Wahl.
 */
function Geraete({ nr, p, fehltZeigen }: { nr: string; p: Position; fehltZeigen: boolean }) {
  const t = useT();
  if (p.art !== "miete" || p.anbauAn || !p.geraete) return null;

  const mitTraeger = p.geraete.some((g) => g.rolle === "traeger");
  const fehlt = fehltZeigen && !mitTraeger && Boolean(p.von && p.bis);
  if (!p.geraete.length && !fehlt) return null;

  return (
    <span className="pos-geraete">
      {p.geraete.map((g) => (
        <Geraeteklappe
          key={g.id}
          nr={nr}
          g={g}
          rolleWort={t(`positionen.geraet.${g.rolle}`)}
          name={eigenerName(g, p)}
          gestrichelt={g.rolle !== "traeger"}
          bis={p.bis}
        />
      ))}
      {fehlt ? <Marke art="stopp">{t("positionen.geraet.fehlt")}</Marke> : null}
    </span>
  );
}

/**
 * Der Name hinter der Kennung — nur, wenn er etwas sagt. Am Träger ist er
 * meist der Typ, und der steht schon fett darüber; am Anbau ist er das
 * Einzige, was „LOE-01" lesbar macht.
 */
function eigenerName(g: Geraetestueck, p: Position): string {
  const name = (g.typ ?? "").trim();
  if (!name) return "";
  /* „Baumaschinenanhänger 3 t / 3,5 t · mit Baumaschine" trägt den Typ vorn —
     dann wiederholt der Name nur, was fett darüber steht. */
  return g.rolle === "traeger" && p.was.trim().toLowerCase().startsWith(name.toLowerCase()) ? "" : name;
}

/**
 * **Welches Stück** — an der bestehenden Mietzeile wählbar (18.09.2026, B5).
 *
 * Luca: *„Man soll ja auch innerhalb des Angebotes wählen können, wer der
 * Bediener ist und wer die Maschine ist, weil es theoretisch auch sein kann,
 * dass ein Kunde unbedingt diese eine Maschine haben möchte."*
 *
 * **Regel 2 bleibt die Vorgabe:** Verkauft wird der Typ, und die automatische
 * Wahl steht schon da. Wer hier greift, legt fest — für diesen einen Kunden,
 * der genau diesen Bagger will.
 *
 * Ein Stück, das ein anderes Angebot reserviert, steht mit seinem Vorgang
 * daneben und wird erst nach der Rückfrage genommen; dann fällt die fremde
 * Vormerkung (`belegung.reserviert`).
 */
/**
 * Die Daten und Handgriffe zur Wahl des Stücks — **einmal**, für die Zeile am
 * Schreibtisch (`Mietangaben`) und das Blatt am Telefon (`Positionsblatt`).
 * Zwei Stellen, die dieselbe Frage stellen, stellen sie über denselben Haken;
 * sonst nimmt die eine das reservierte Stück nach Rückfrage und die andere
 * gar nicht (20.09.2026).
 */
function useGeraetewahl(nr: string, p: Position | null) {
  const aendern = usePositionAendern();
  const [fragt, setzeFragt] = useState("");
  const gilt = p !== null && p.art === "miete" && !p.anbauAn && Boolean(p.id);
  const lage = useVerfuegbarkeit(p?.typ_id ?? undefined, p?.von ?? undefined, p?.bis ?? undefined,
    gilt && Boolean(p?.typ_id && p?.von && p?.bis));

  const traeger = (p?.geraete ?? []).find((g) => g.rolle === "traeger");
  const unterwegs = traeger?.unterwegs === true;

  const frei = lage.data?.vorschlaege ?? [];
  const reserviert = lage.data?.reserviert ?? [];
  const stuecke = [
    ...(traeger ? [{ id: traeger.id, kennung: traeger.kennung, wer: "" }] : []),
    ...frei.filter((f) => f.maschine_id !== traeger?.id)
      .map((f) => ({ id: f.maschine_id, kennung: f.kennung, wer: "" })),
    ...reserviert.filter((r) => r.maschine_id !== traeger?.id)
      .map((r) => ({ id: r.maschine_id, kennung: r.kennung, wer: r.vorgang })),
  ];

  const setzen = (maschine_id: string, uebergehen: boolean) => {
    setzeFragt("");
    if (!p?.id) return;
    aendern.mutate(
      { nr, id: p.id, maschine_id, ...(uebergehen ? { reservierung_uebergehen: true } : {}) },
      {
        onError: (f) => {
          if (f instanceof Fehler && f.schluessel === "belegung.reserviert") setzeFragt(maschine_id);
        },
      },
    );
  };

  return { gilt, traeger, unterwegs, stuecke, setzen, fragt, aendern, laedt: gilt && lage.isPending };
}

/**
 * **Was an einer Miete hängt und wann sie läuft** — die Mietzeile in der
 * Liste (Paket 9, Luca 07.10.2026: *„Vielleicht kann man das alles ein
 * bisschen zusammenfassen, sodass da nur steht: Mini-Bagger, darunter das
 * genaue Gerät, der Anbau nebeneinander."*).
 *
 * - **Zeile 2:** das Gerät als Marke (Kennung in Mono, der Name nur, wenn er
 *   sich vom Typ unterscheidet), daneben die Anbauten als Marken. Ein Klick
 *   klappt am Ort auf (`Geraeteklappe`) — dort steht, was zu wählen ist: ein
 *   anderes Stück, die Anbauteile. Fehlt der Träger, steht die Wahl offen da.
 * - **Zeile 3:** der Zeitraum und „mit Bediener" nebeneinander.
 * - **Die Auswahl der Anbauteile steht nur, wenn noch etwas zu wählen ist** —
 *   ein Pflichtteil ohne Stück. Sonst war sie eine Doppelung der Marke
 *   („Anbau LOE-01" und darunter noch einmal „Tieflöffel · LOE-01"). Wer
 *   ein freiwilliges Teil dazunehmen will, klappt sie mit „Anbauteile" auf.
 */
function Mietangaben({ nr, p, rechte, zeitraum }: {
  nr: string; p: Position; rechte: Positionsrechte; zeitraum: ReactNode;
}) {
  const t = useT();
  const geraet = useGeraetewahl(nr, p);
  const [anbauOffen, setzeAnbauOffen] = useState(false);
  const { traeger, unterwegs, stuecke, setzen, fragt, aendern } = geraet;

  /* Dieselbe Abfrage wie die Auswahl selbst (`ZubehoerWahl`) — sie fragt
     nicht zweimal. Nur wer ändern darf, braucht die Antwort. */
  const zubehoer = useZubehoerAuswahl(
    rechte.aendern ? (p.typ_id ?? undefined) : undefined, p.von ?? null, p.bis ?? null,
  );
  const lage = zubehoer.data ?? [];
  const dran = (p.geraete ?? []).filter((g) => g.rolle !== "traeger");
  const gebunden = dran.map((g) => g.id);
  /* Ein Pflichtteil, an dem kein Stück hängt — dann ist etwas zu wählen. */
  const pflichtOffen = lage.some((z) => z.pflicht && !z.stuecke.some((s) => gebunden.includes(s.id)));
  const auswahlZeigen = rechte.aendern && Boolean(p.id) && (pflichtOffen || anbauOffen);

  const fehlt = rechte.aendern && !traeger && Boolean(p.von && p.bis);
  /* Steht ein Träger da, liegt die Wahl in seinem Blatt; fehlt er, steht sie offen. */
  const mitWahl = rechte.aendern && !unterwegs && (traeger ? stuecke.length > 1 : stuecke.length > 0);

  const wahlfeld = mitWahl ? (
    <select
      className="fein pos-geraet-wahl"
      aria-label={t("positionen.geraet.wahl")}
      value={traeger?.id ?? ""}
      disabled={aendern.isPending}
      onChange={(e) => setzen(e.target.value, false)}
    >
      {traeger ? null : <option value="">{t("positionen.geraet.wahl")}</option>}
      {stuecke.map((s) => (
        <option key={s.id} value={s.id}>
          {s.kennung}{s.wer ? ` · ${t("positionen.geraet.reserviertKurz", { vorgang: s.wer })}` : ""}
        </option>
      ))}
    </select>
  ) : null;
  const trotzdem = fragt ? (
    <Knopf klein onClick={() => setzen(fragt, true)}>{t("positionen.geraet.trotzdem")}</Knopf>
  ) : null;

  const zeigtStuecke = Boolean(traeger) || dran.length > 0 || fehlt || (mitWahl && !traeger);
  /* Freiwillige Anbauteile, von denen noch keins gewählt ist: Ein leiser
     Knopf klappt die Auswahl auf. Sind schon welche dran, öffnet ihre Marke
     dieselbe Auswahl. */
  const anbauKnopf = rechte.aendern && !pflichtOffen && lage.length > 0 && dran.length === 0;

  return (
    <>
      {zeigtStuecke || anbauKnopf ? (
        <span className="pos-stuecke">
          {traeger ? (
            <Geraeteklappe
              nr={nr}
              g={traeger}
              rolleWort={t("positionen.geraet.traeger")}
              name={eigenerName(traeger, p)}
              bis={p.bis}
              inhalt={wahlfeld ? (
                <label className="geraet-klappe-wahl">
                  <span className="feld-schild">{t("positionen.geraet.anderes")}</span>
                  {wahlfeld}
                  {trotzdem}
                </label>
              ) : null}
            />
          ) : (
            <>
              {wahlfeld}
              {trotzdem}
            </>
          )}
          {dran.map((g) => (
            <Geraeteklappe
              key={g.id}
              nr={nr}
              g={g}
              rolleWort={t(`positionen.geraet.${g.rolle}`)}
              name={eigenerName(g, p)}
              gestrichelt
              bis={p.bis}
              inhalt={rechte.aendern ? <Zubehoerzeile nr={nr} p={p} rechte={rechte} /> : null}
            />
          ))}
          {fehlt ? <Marke art="stopp">{t("positionen.geraet.fehlt")}</Marke> : null}
          {anbauKnopf ? (
            <button
              type="button"
              className="pos-anbau-knopf"
              aria-expanded={anbauOffen}
              onClick={() => setzeAnbauOffen((o) => !o)}
            >
              {t(anbauOffen ? "positionen.geraet.anbauZu" : "positionen.geraet.anbauDazu")}
            </button>
          ) : null}
        </span>
      ) : null}
      {aendern.isError && !fragt ? (
        <span className="pos-bediener-fehler">
          <FehlerBand pfad={positionsFehlerPfad(aendern.error)} werte={fehlerWerte(aendern.error)} />
        </span>
      ) : null}
      <span className="pos-mietlage">
        {zeitraum}
        <Bedienerwahl nr={nr} p={p} rechte={rechte} />
      </span>
      {auswahlZeigen ? <Zubehoerzeile nr={nr} p={p} rechte={rechte} /> : null}
    </>
  );
}

/**
 * **Das Zubehör an einer bestehenden Miete** — wählbar, nicht nur lesbar.
 *
 * Bis zum 18.09.2026 gab es die Wahl nur beim **Anlegen** einer Zeile. Wer an
 * einem Angebot merkte, dass der Pflichtlöffel fehlt, musste die Position
 * löschen und neu anlegen. Mit dem Riegel aus dem Durchgang (B4) — ohne
 * Pflichtzubehör keine Annahme — wäre das eine Sackgasse geworden: abgewiesen,
 * und kein Weg heraus. Also steht die Wahl jetzt dort, wo Gerät und Bediener
 * stehen.
 *
 * **Gelesen wird der Stand, nicht vorbelegt** (`gebunden`): Wer die Zeile nur
 * ansieht, ändert nichts. Erst ein Griff schreibt, und dann sofort — wie beim
 * Bediener. Was aus der Liste fällt, wird gelöst; was dazukommt, wird gebucht
 * und bekommt, wenn es einen eigenen Tarif hat, seine Preiszeile
 * (dienste/anbauteile.ts).
 */
function Zubehoerzeile({ nr, p, rechte, handy = false }: {
  nr: string; p: Position; rechte: Positionsrechte;
  /** Im Blatt am Telefon: Zeilen im Hausmaß statt der Kästchen (`ZubehoerWahl`, `handy`). */
  handy?: boolean;
}) {
  const aendern = usePositionAendern();
  const [wert, setzeWert] = useState<ZubehoerWert>(ZUBEHOER_LEER);

  const gebunden = useMemo(
    () => (p.geraete ?? []).filter((g) => g.rolle !== "traeger").map((g) => g.id),
    [p.geraete],
  );

  if (p.art !== "miete" || p.anbauAn || !p.id || !rechte.aendern) return null;

  const schreiben = (neu: ZubehoerWert, vonHand = false) => {
    setzeWert(neu);
    /* **Ansehen schreibt nicht** (V-19, Durchgang 03.10.2026). Die Auswahl
       meldet beim ersten Zeichnen ihren Stand; stand an der Position etwas,
       das die Auswahl nicht als Wahl führt, wich der Stand von `gebunden` ab,
       und schon das Öffnen des Reiters schickte `PATCH {zubehoer: []}` —
       mit „AB nachziehen" als Folge. Geschrieben wird nur nach einem Griff. */
    if (!vonHand) return;
    const gleich = neu.stuecke.length === gebunden.length
      && neu.stuecke.every((id) => gebunden.includes(id));
    if (gleich || !p.id) return;
    aendern.mutate({
      nr,
      id: p.id,
      zubehoer: neu.stuecke.map((ressource_id) => ({ ressource_id })),
    });
  };

  return (
    <ZubehoerWahl
      typId={p.typ_id}
      von={p.von ?? null}
      bis={p.bis ?? null}
      wert={wert}
      gebunden={gebunden}
      aendern={schreiben}
      className="pos-zubehoer"
      handy={handy}
    />
  );
}

/**
 * **Mit Bediener — und wer.** An der Miete, unter ihrem Zeitraum.
 *
 * Der Haken kam am 16.09.2026 (Migration 0096), damit Zuschläge wissen, ob
 * jemand mitfährt. Luca, am selben Tag: *„Wenn man jetzt den Haken mit
 * ‚Bediener' setzt, dann muss man ja auch auswählen, wer — damit er dann
 * theoretisch auch geblockt wird, wenn man das Angebot anlegt, weil man ja auch
 * einen freien Bediener haben muss in dem Zeitraum."*
 *
 * **Die Wahl bucht.** Wer gewählt wird, hängt an derselben Position wie die
 * Maschine und bekommt eine eigene Belegung (dienste/bediener.ts): im Entwurf
 * vorgemerkt, nach der Annahme fest — dieselbe Regel wie das Zubehör. Geprüft
 * werden Belegung und Qualifikation gegen das Ende des Einsatzes.
 *
 * **Die Liste filtert nicht, sie begründet.** Wer nicht kann, steht mit dem
 * Grund da — „belegt bis 22.09.", „ohne Kranführerschein" — und ist nicht
 * wählbar. Ein fehlender Name wäre keine Auskunft.
 *
 * **Kein Pflichtfeld, aber eine Marke.** Ein Angebot darf hinausgehen, bevor
 * feststeht, wer fährt — wie in der Schnellerfassung, wo „mit Bediener" ohne
 * Namen eine Absicht bleibt (Regel 11). Dass noch jemand fehlt, steht gelb an
 * der Zeile.
 *
 * Nur bei `wahlweise` ist „mit Bediener" ein Haken. Ein Kran (`nur_mit_person`)
 * fährt nie allein — dort steht es als Wort. Ein Gerät `ohne_person` und die
 * Zeile eines Anbauteils (keine Pflicht vom Server) bekommen nichts.
 */
/**
 * Die Daten und Handgriffe zur Bedienerwahl — **einmal**, für die Zeile am
 * Schreibtisch (`Bedienerwahl`) und das Blatt am Telefon (`Positionsblatt`).
 * Die Liste filtert nicht, sie begründet: `gruende` sagt je Mensch, warum er
 * nicht kann oder was für ihn spricht.
 */
function useBedienerwahl(nr: string, p: Position | null, rechte: Positionsrechte) {
  const t = useT();
  const ruf = useRuf();
  const aendern = usePositionAendern();

  const pflicht = p?.bedienungspflicht ?? null;
  const mit = pflicht === "nur_mit_person" || (pflicht === "wahlweise" && p?.bedient === true);
  /* **Holt der Kunde ab, gibt es keinen Haken „mit Bediener"** (Paket 9,
     Runde 4, Luca: *„Dann wäre es ja eine ganz normale Auslieferung."*). Bei
     `wahlweise` verschwindet die Wahl; steht der Haken noch (an der Position
     abgeholt, bevor es die Regel gab), bleibt er sichtbar, damit man ihn
     abnehmen kann. Bei Bedienerpflicht ist es ein Widerspruch — gesagt,
     nicht verboten (`lieferwegregeln.ts`). */
  const lage = p ? bedienerBeiAbholung(p) : "keiner";
  const gilt = p !== null && p.art === "miete" && Boolean(pflicht) && pflicht !== "ohne_person"
    && !(lage === "entfaellt" && p.bedient !== true);
  const { data: kandidaten, isFetching } = useBedienerauswahl(nr, p?.id, gilt && mit && rechte.aendern);

  /* Was zuletzt geschickt wurde -- fuer "Trotzdem einplanen", wenn die API bei
     einer abgelaufenen Qualifikation zurueckfragt (0230, E3). */
  const [zuletzt, setzeZuletzt] = useState<{ bedient?: boolean; bediener_id?: string | null } | null>(null);
  const schreiben = (teil: { bedient?: boolean; bediener_id?: string | null }, bestaetigt = false) => {
    if (!p?.id || !nr) return;
    setzeZuletzt(teil);
    aendern.mutate(
      { nr, id: p.id, ...teil, ...(bestaetigt ? { qualifikation_bestaetigt: true } : {}) },
      { onSuccess: () => ruf("posGeaendert") },
    );
  };
  const trotzdem = () => { if (zuletzt) schreiben(zuletzt, true); };

  /* Was zu einem Menschen zu sagen ist — in der Reihenfolge, in der es die
     Wahl entscheidet. */
  const gruende = (k: Bedienerkandidat): string[] => {
    const dazu: string[] = [];
    if (k.grund === "belegt") dazu.push(t("positionen.bedienerBelegt", { bis: tagKurz(k.belegtBis) }));
    if (k.grund === "qualifikation_fehlt") dazu.push(t("positionen.bedienerOhne", { qual: k.qualifikation ?? "" }));
    if (k.grund === "qualifikation_abgelaufen") {
      dazu.push(t("positionen.bedienerAbgelaufen", { qual: k.qualifikation ?? "", am: tagKurz(k.gueltigBis) }));
    }
    if (k.frei && k.stammfahrer) dazu.push(t("positionen.bedienerStammfahrer"));
    if (k.frei && k.vorgemerkt) dazu.push(t("positionen.bedienerAnderswo"));
    return dazu;
  };
  /* Die Beschriftung im Auswahlfeld: der Name, und was dazu zu sagen ist. */
  const wort = (k: Bedienerkandidat): string => [k.name, ...gruende(k)].join(` ${t("allgemein.trennpunkt")} `);

  /* Der Gewählte steht immer in der Liste — auch wenn er dort nicht mehr
     auftaucht (ausgetreten, nicht mehr disponierbar). Sonst zeigte das Feld
     „wer fährt?", obwohl jemand gebucht ist. */
  const liste = kandidaten ?? [];
  const gewaehlt = p?.bediener?.id ?? "";
  const ohneEintrag = Boolean(gewaehlt) && !liste.some((k) => k.id === gewaehlt);
  const gesperrt = !rechte.aendern || !p?.id || aendern.isPending || p?.bediener?.unterwegs === true;

  return { gilt, pflicht, mit, lage, kandidaten, isFetching, liste, gewaehlt, ohneEintrag, gesperrt, gruende, wort, schreiben, aendern, trotzdem };
}

function Bedienerwahl({ nr, p, rechte }: { nr: string; p: Position; rechte: Positionsrechte }) {
  const t = useT();
  const {
    gilt, pflicht, mit, lage, kandidaten, isFetching, liste, gewaehlt, ohneEintrag, gesperrt, wort, schreiben, aendern, trotzdem,
  } = useBedienerwahl(nr, p, rechte);

  if (!gilt) return null;

  return (
    <span className="pos-bediener">
      {pflicht === "wahlweise" ? (
        <label className="hakenzeile">
          <input
            type="checkbox"
            checked={p.bedient === true}
            disabled={!rechte.aendern || !p.id || aendern.isPending || p.bediener?.unterwegs === true}
            title={rechte.aendern ? undefined : t(rechte.grund)}
            onChange={(e) => schreiben({ bedient: e.target.checked })}
          />
          {t("positionen.mitBediener")}
        </label>
      ) : (
        <span className="leise">{t("positionen.bedienerPflicht")}</span>
      )}
      {/* Bedienerpflicht und „Kunde holt ab" zugleich: offen, nicht verboten. */}
      {lage === "widerspruch" ? (
        <span title={t("positionen.pflichtGegenAbholungSatz")}>
          <Marke art="acht">{t("positionen.pflichtGegenAbholung")}</Marke>
        </span>
      ) : null}

      {mit ? (
        <select
          className="pos-bediener-wahl"
          value={gewaehlt}
          disabled={gesperrt}
          aria-label={t("positionen.bedienerWer")}
          title={p.bediener?.unterwegs ? t("positionen.bedienerUnterwegs") : undefined}
          onChange={(e) => schreiben({ bediener_id: e.target.value || null })}
        >
          <option value="">{t(isFetching && !kandidaten ? "allgemein.laedt" : "positionen.bedienerWer")}</option>
          {ohneEintrag && p.bediener ? <option value={p.bediener.id}>{p.bediener.name}</option> : null}
          {liste.map((k) => (
            <option key={k.id} value={k.id} disabled={!k.waehlbar && !k.gewaehlt}>{wort(k)}</option>
          ))}
        </select>
      ) : null}

      {/* Gelb, nicht rot: Es fehlt noch etwas, falsch ist nichts. */}
      {mit && !p.bediener ? <Marke art="acht">{t("positionen.bedienerFehlt")}</Marke> : null}
      {p.bediener?.vorgemerkt ? <span className="leise">{t("positionen.bedienerVorgemerkt")}</span> : null}

      {aendern.isError ? (
        <span className="pos-bediener-fehler">
          <Qualifikationsband
            fehler={aendern.error}
            pfad={positionsFehlerPfad(aendern.error)}
            werte={fehlerWerte(aendern.error)}
            trotzdem={trotzdem}
            laeuft={aendern.isPending}
          />
        </span>
      ) : null}
    </span>
  );
}

/* ══════════════ Die Summe unter den Positionen ══════════════ */

/**
 * Das Netto, das unten im Fuß steht: die Positionen plus die Zuschläge nach
 * heutigem Stand. **Eine Rechnung, eine Stelle** — die Klappe über der Liste
 * nennt dieselbe Zahl, und zwei Rechnungen dafür wären zwei Zahlen.
 */
export function nettoMit(positionen: number, zuschlaege?: Erwartung | undefined): number {
  return Math.round((positionen + (zuschlaege?.netto ?? 0)) * 100) / 100;
}

/**
 * Das Netto im Fuß der Positionsliste — **mit** den Zuschlägen.
 *
 * Bis zum 16.09.2026 stand hier das Netto der Positionen und darunter ein
 * eigener Block „Dazu nach heutigem Stand" mit einer zweiten Summe. Luca:
 * *„Die Netto-Summe ist dann ja die Gesamtsumme, die darunter steht."* Seit die
 * Zuschläge Zeilen der Tabelle sind, ist die Summe darunter die Summe dieser
 * Zeilen — alles andere hieße, dass die Tabelle nicht aufgeht.
 *
 * **Was noch keinen Betrag hat, steht als „zzgl." darunter** — mit Namen. Eine
 * Summe, der man nicht ansieht, dass der Diesel noch fehlt, ist dieselbe
 * falsche Endsumme wie vorher, nur kleiner.
 *
 * Gerechnet wird hier nichts Neues: `positionen` ist die Summe der Maske,
 * `zuschlaege.netto` die des Servers (`dienste/zuschlagserwartung.ts`). Die
 * Erklärung am Wort sagt, dass die Zuschläge aus dem Ist abgerechnet werden
 * (Regel 3) — als Auskunft auf Nachfrage, nicht als Satz für jeden Aufruf.
 */
export function Nettozeile({ positionen, zuschlaege }: {
  positionen: number;
  zuschlaege?: Erwartung | undefined;
}) {
  const t = useT();
  const zeilen = zuschlaege?.zeilen ?? [];
  const betrag = nettoMit(positionen, zuschlaege);
  /* Doppelte Namen einmal: Zwei Maschinen mit Diesel nach Verbrauch sind ein
     „zzgl. Diesel nach Verbrauch", nicht zwei. */
  const ohneBetrag = [...new Set(zeilen.filter((z) => z.netto === null).map((z) => z.bezeichnung))];

  return (
    <>
      <div className="summen-zeile">
        <span>
          {t("allgemein.netto")}
          {zeilen.length ? <Erklaerung k="zuschlaege.was" /> : null}
        </span>
        <span className="z">{euro(betrag)}</span>
      </div>
      {ohneBetrag.length ? (
        <p className="nebensatz">
          {t("zuschlaege.zzgl", { was: ohneBetrag.join(` ${t("allgemein.trennpunkt")} `) })}
        </p>
      ) : null}
    </>
  );
}

/**
 * **Der Betrag vor dem Rabatt, durchgestrichen vor der Summe** (Systemprüfung
 * B-12, 24.09.2026). Lisa: Der Ursprungspreis vor dem Rabatt ist nicht
 * sichtbar. Die Summe ist der rabattierte Betrag; wer nur „10 %" daneben
 * liest, rechnet im Kopf zurück.
 *
 * Die Zahl rechnet die API (`netto_vor_rabatt`, dienste/rabatt.ts) — hier
 * entsteht kein Betrag. Ohne Rabatt steht nichts da.
 */
function VorRabatt({ p }: { p: Position }) {
  const t = useT();
  if (p.vorRabatt === null || p.vorRabatt === undefined || p.vorRabatt === p.summe) return null;
  return (
    <s className="pos-vor-rabatt" title={t("rabatt.vorherTitel")}>
      {euro(p.vorRabatt)}
    </s>
  );
}

/* ══════════════ Vortext und Nachtext ══════════════ */

/** Wo der Text auf dem Papier steht — und damit, welches Feld er ist. */
const STELLE: Record<"vor" | "nach", {
  art: Bausteinart; schild: string; platz: string; was: string;
}> = {
  vor: {
    art: "vortext",
    schild: "textbausteine.vortext",
    platz: "textbausteine.vortextPlatz",
    was: "textbausteine.vortextErklaerung",
  },
  nach: {
    art: "nachtext",
    schild: "textbausteine.nachtext",
    platz: "textbausteine.nachtextPlatz",
    was: "textbausteine.nachtextErklaerung",
  },
};

/**
 * Wie lang ein Belegtext sein darf: so lang, wie Lexware Office ihn in ein
 * Textfeld nimmt (`packages/lex`, `TEXT_HOECHSTLAENGE`). Die API weist mehr
 * schon beim Schreiben ab; das Feld lässt es gar nicht erst zu.
 */
export const BELEGTEXT_HOECHSTLAENGE = 2000;

/**
 * Der Satz über oder unter der Positionsliste — da, wo er auch auf dem Papier
 * steht.
 *
 * Lisa (Innendienst): Es sind fast immer dieselben Sätze — „Preise netto zzgl.
 * MwSt.", „Anlieferung nach Absprache". Wer sie jedes Mal tippt, tippt sie
 * jedes Mal anders. Deshalb hängt der Schnellzugriff direkt am Feld.
 *
 * **Er sagt, zu welchem Beleg er gehört** (Migration 0105, Durchgang
 * 16.09.2026, AG2). Bis dahin stand hier „geht auf Angebot · Auftragsbestätigung
 * · Lieferschein · Rechnung", und das stimmte nicht mehr, sobald der Satz über
 * dem Angebot ein anderer sein sollte als der über der Auftragsbestätigung:
 *
 * - `beleg="angebot"` — die Texte am Vorgang (`vortext`, `notiz_kunde`), über
 *   `PATCH`. Leer ist leer: Der Standard wurde beim Anlegen vorbelegt.
 * - `beleg="auftragsbestaetigung"` — der abweichende Text am Vorgang
 *   (`order_text`). **Leer heißt Standard**, und der Standard steht sichtbar als
 *   Platzhalter da. Lieferschein und Rechnung stehen hinter dem Knopf „Texte".
 *
 * **Was drüben schon liegt, ändert kein Feld hier.** Liegt die
 * Auftragsbestätigung in Lexware Office, steht der Text fest und der Satz sagt
 * es — ein stummes Feld, das nichts mehr bewirkt, wäre schlimmer als ein
 * gesperrtes.
 *
 * **Ein Text bewegt keinen Preis** (Regel 4). Deshalb steht das Feld auch nach
 * der Annahme offen, während die Positionen längst eingefroren sind.
 *
 * **Und es steht offen, sobald die Maske aufgeht** (Durchgang 16.09.2026).
 * Luca: *„Vortext und Nachtext können auch direkt dastehen, wenn ich eine
 * Angebotsmaske öffne, und dann müssen die Positionen ausklappbar sein."*
 * Bis dahin war der Text zugeklappt — eine Zeile „kein Text", und erst nach
 * einem Klick ein Feld, mit dem Gedanken, die Positionen nicht nach unten zu
 * schieben. Der Tausch geht jetzt andersherum: Die **Positionen** klappen ein
 * (`Positionsklappe`), die Texte stehen.
 */
export function Belegtext({ nr, stelle, beleg, fest: festVon = false }: {
  nr: string;
  stelle: "vor" | "nach";
  /* **Alle vier Belegarten** (22.09.2026). Der Baustein konnte Angebot und
     Auftragsbestätigung; Lieferschein und Rechnung lagen hinter dem Knopf
     „Texte" im Kopf, hinter einem Deckblatt, hinter einem von drei Reitern.
     Der Weg dorthin ist derselbe (`PUT /vorgaenge/:id/texte/:art`), also
     braucht es keinen zweiten Baustein — nur zwei Wörter mehr im Typ. */
  beleg: "angebot" | "auftragsbestaetigung" | "lieferschein" | "rechnung";
  fest?: boolean | undefined;
}) {
  const t = useT();
  /* Wer nur liest, schreibt hier nichts (P7-03) — gleich, was der Aufrufer sagt. */
  const fest = festVon || !useDarfSchreiben();
  const { data: vorgang } = useVorgang(nr);
  /* Auch am Angebot gelesen — nicht wegen „leer heißt Standard" (das gilt dort
     nicht), sondern damit der Standardbaustein im leeren Feld zu sehen ist und
     sich mit einem Griff übernehmen lässt. Dieselbe Abfrage, die der Knopf
     „Texte" im Kopf ohnehin schon geholt hat. */
  const { data: texte } = useBelegtexte(nr);
  const aendern = useVorgangAendern();
  const schreiben = useBelegtexteSchreiben();
  const wie = STELLE[stelle];

  const stand = texte?.find((z) => z.art === beleg);
  const feld = stand ? (stelle === "vor" ? stand.vortext : stand.nachtext) : undefined;
  const bestand = beleg === "angebot"
    ? ((stelle === "vor" ? vorgang?.vortext : vorgang?.notiz_kunde) ?? "")
    : (feld?.eigen ?? "");
  const standard = feld?.standard ?? null;
  /* **Leer heißt Standard — aber nicht am Angebot.** Dessen Texte werden beim
     Anlegen vorbelegt und gehören danach dem Vorgang (0094); wer sie dort
     löscht, bekommt sie nicht zurück. Der Baustein steht deshalb nur als
     Vorschlag da, mit einem Griff, ihn zu übernehmen. */
  const standardGilt = beleg !== "angebot";
  const hinaus = beleg === "angebot" ? undefined : stand?.hinaus.at(-1);
  const gesperrt = fest || Boolean(hinaus);

  /* **Der getippte Text gilt vor dem gesicherten** (Paket 9): Er liegt im
     gemeinsamen Entwurf (`belegtextentwurf.ts`), damit das Blatt „Texte"
     und ein neu gezeichnetes Feld (anderer Reiter) dasselbe zeigen. */
  const [text, setzeTextRoh] = useState(() => textImFeld(nr, beleg, stelle, bestand));
  const setzeText = (neu: string) => {
    setzeTextRoh(neu);
    if (neu === bestand) entwurfLoeschen(nr, beleg, stelle);
    else entwurfSetzen(nr, beleg, stelle, neu);
  };

  const wohin = hinaus
    ? t(hinaus.nummer ? "belegtexte.wohinFest" : "belegtexte.wohinFestOhneNummer", {
        beleg: t(`vorgang.papier.${beleg}`),
        nummer: hinaus.nummer ?? "",
      })
    : t(`belegtexte.wohin.${beleg}`);

  /* Der Bestand wird übernommen, wenn der Vorgang wechselt oder frisch
     ankommt — nicht bei jedem Tastendruck zurück (wie am Kundentext). */
  useEffect(() => {
    /* Ist der Stand beim Entwurf angekommen, ist der Entwurf erledigt. Erst
       dann — nicht schon, wenn das Schreiben zurückkommt: Bis der Vorgang
       neu geladen ist, stünde sonst im Blatt „Texte" der alte Satz. */
    const offen = textImFeld(nr, beleg, stelle, bestand);
    if (offen.trim() === bestand.trim()) entwurfLoeschen(nr, beleg, stelle);
    setzeTextRoh(offen);
  }, [nr, beleg, stelle, bestand]);

  /* **Der Standard des Angebots, bevor es den Entwurf gibt** (Systemprüfung
     I-05, 24.09.2026). Lisa: Der Vortext sieht im Angebot leer aus, obwohl
     ein Baustein da ist. Im neuen Angebot stand hier gar nichts — der Entwurf
     entsteht erst mit der ersten Position, und erst dann wird der Standard
     hineingeschrieben (0094). Jetzt steht er vorher schon da, als das, was
     kommen wird. Dieselbe Abfrage wie im Schnellzugriff daneben. */
  const { data: bausteine } = useTextbausteine(wie.art, true, beleg);
  const vorschlag = (bausteine?.zeilen ?? []).find((b) => b.standard) ?? null;

  /* Ohne Vorgangsnummer gibt es nichts zu schreiben: Im leeren Angebot
     entsteht der Entwurf erst mit der ersten Position. Ein Feld, dessen
     Inhalt beim Verlassen verfällt, wäre schlimmer als keins — es steht
     darum gesperrt da und sagt, was mit der ersten Position hineinkommt. */
  if (!nr) {
    if (beleg !== "angebot") return null;
    return (
      <div className={`bogen belegtext belegtext-${stelle}`}>
        <div className="bogen-kopf belegtext-kopf">
          <span className="feld-schild">
            {t(wie.schild)}
            <Erklaerung k={wie.was} />
          </span>
          <span className="belegtext-wohin">{t("belegtexte.wohin.angebot")}</span>
        </div>
        <textarea
          className="feld texte-feld belegtext-feld"
          rows={6}
          disabled
          value=""
          placeholder={vorschlag ? vorschlag.text : t(wie.platz)}
          aria-label={t(wie.schild)}
        />
        <p className="nebensatz">
          {vorschlag
            ? t("belegtexte.kommtMitErsterPosition", { stichwort: vorschlag.stichwort })
            : t("belegtexte.ohneStandardErstePosition")}
        </p>
      </div>
    );
  }

  function sichern(wert: string) {
    const sauber = wert.trim();
    if (sauber === bestand.trim()) { entwurfLoeschen(nr, beleg, stelle); return; }
    /* Der Entwurf bleibt, bis der Stand angekommen ist (Wirkung oben) — wer
       in der Zwischenzeit „Texte" öffnet, sieht den neuen Satz. */
    if (beleg === "angebot") {
      aendern.mutate({ nr, felder: stelle === "vor" ? { vortext: sauber } : { nachtext: sauber } });
    } else {
      // Leer heißt: zurück auf den Standard der Belegart.
      schreiben.mutate({
        nr, art: beleg, felder: stelle === "vor" ? { vortext: sauber || null } : { nachtext: sauber || null },
      });
    }
  }

  const fehler = aendern.error ?? schreiben.error;

  return (
    /* Ein Bogen wie jede Liste: weißes Blatt, Kopf in der Fläche des
       Spaltenkopfs, dieselbe Kante, derselbe Radius. Luca, 16.09.2026:
       *„Bei Vortext und Nachtext im Angebot muss der Tabellenbody auch den
       Weißton haben wie immer bei den Tabellen."* Was den Block abhebt, ist
       der Kopf und die Kante — nicht eine dunklere Fläche. */
    <div className={`bogen belegtext belegtext-${stelle}`}>
      <div className="bogen-kopf belegtext-kopf">
        <span className="feld-schild">
          {t(wie.schild)}
          <Erklaerung k={wie.was} />
        </span>
        {/* Zu welchem Beleg der Satz gehört — und ob der schon hinaus ist. */}
        <span className="belegtext-wohin">{wohin}</span>
        {gesperrt ? null : (
          <Bausteinzugriff
            art={wie.art}
            beleg={beleg}
            wert={text}
            einsetzen={(neuerText) => { setzeText(neuerText); if (!textZuLang(neuerText)) sichern(neuerText); }}
          />
        )}
      </div>

      {/* Das Feld steht offen — kein Schalter davor (siehe oben). */}
      {/* Sechs Zeilen und aufziehbar (02.10.2026, Luca: *„Der Vortext muss mehr
          Platz haben im Angebot."*). Seit das Angebot hier entsteht, tragen
          Vor- und Nachtext die Hinweise, die vorher Lexware druckte. */}
      <textarea
        className="feld texte-feld belegtext-feld"
        id={`belegtext-${stelle}-${nr}`}
        rows={6}
        disabled={gesperrt}
        aria-invalid={textZuLang(text)}
        /* Wo leer Standard heißt, steht der Standard im leeren Feld. Am
           Angebot sagt das leere Feld, dass es leer hinausgeht, wenn es einen
           Standard gäbe (I-05) — der Baustein wartet darunter. Ein Standard
           als Platzhalter sähe dort aus, als ginge er mit. */
        placeholder={standard
          ? (standardGilt ? standard.text : t("belegtexte.platzAngebotLeer"))
          : t(wie.platz)}
        value={text}
        onChange={(e) => setzeText(e.target.value)}
        // Zu lang: stehen lassen und sagen, nicht abschneiden (N4-16).
        onBlur={(e) => { if (!textZuLang(e.target.value)) sichern(e.target.value); }}
        onKeyDown={(e) => {
          if (e.key === "Escape") { setzeTextRoh(bestand); entwurfLoeschen(nr, beleg, stelle); }
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) e.currentTarget.blur();
        }}
      />
      <Laengensatz text={text} />
      {/* **Woher der Text kommt** (Paket 9, Runde 4). Luca: *„Bei ‚Weitere
          Texte‘ — wird da der Standard der Texte genommen von Rechnung und
          so, oder was nimmt er da?"* Für Auftragsbestätigung, Lieferschein
          und Rechnung gilt: eigener Text am Vorgang, sonst der Standard-
          baustein der Belegart, sonst nichts — gelesen, wenn das Papier
          entsteht (`dienste/belegtexte.ts`). Der Platzhalter zeigte den
          Standard grau, aber nicht, dass er einer ist; jetzt sagt es ein
          leiser Satz unter jedem Feld. Am Angebot gilt die Regel nicht. */}
      {standardGilt && !gesperrt ? (
        <p className="nebensatz belegtext-herkunft">
          {text.trim()
            ? (standard ? t("belegtexte.herkunftEigen", { stichwort: standard.stichwort }) : t("belegtexte.herkunftEigenOhne"))
            : (standard ? t("belegtexte.herkunftStandard", { stichwort: standard.stichwort }) : t("belegtexte.herkunftKeiner"))}
        </p>
      ) : null}
      {/* Am Angebot: der Standardbaustein als Vorschlag, ein Griff zum
          Übernehmen. Ohne ihn wäre „gefüllt" eine Frage des Zufalls — ein
          Angebot, an dem jemand den Vortext gelöscht hat, bliebe leer. */}
      {!standardGilt && standard && !text.trim() && !gesperrt ? (
        <button
          className="belegtext-leer belegtext-standard"
          type="button"
          title={standard.text}
          onClick={() => { setzeText(standard.text); sichern(standard.text); }}
        >
          {t("belegtexte.standardUebernehmen", { text: standard.text })}
        </button>
      ) : null}
      {fehler ? <FehlerBand pfad={positionsFehlerPfad(fehler)} fehler={fehler} /> : null}
    </div>
  );
}

/* ══════════════ Die Eingabezeilen ══════════════ */

type ZeilenEigenschaften = {
  /** Wie viele Zellen die Zeile hat — dieselbe Form wie die Liste darüber. */
  form: Positionsform;
  vorgang: () => Promise<string>;
  kunde?: string | undefined;
  vorgabe?: Spanne | undefined;
  gesperrt?: string | undefined;
  /**
   * Die Zeilen, die schon dastehen — für die Wahl „statt dieser hier".
   * Ohne sie gibt es nur „in Summe" und „optional".
   */
  zeilen?: Position[] | undefined;
  /** Ob die Rolle überhaupt zur Wahl steht. Nur im Entwurf eines Angebots. */
  wahl?: boolean | undefined;
  /** Angelegt — die Zeile geht zu, die Liste zeigt die neue Position. */
  fertig: () => void;
  /** Abgebrochen — nur die Zeile geht zu. */
  abbrechen: () => void;
};

/* ══════════════ Die Rolle, schon beim Anlegen ══════════════ */

/** „in Summe" · „optional" · `statt:<id>` — was im Auswahlfeld steht. */
export type Rollenwert = string;

/**
 * Was die gewählte Rolle der API mitgibt.
 *
 * `POST …/positionen` nimmt `optional` und `alternative_zu` seit jeher an und
 * prüft sie durch dieselbe Regel wie das Ändern (`rolleSetzen`,
 * `packages/fach/src/optionen.ts`). Es fehlte nur der Weg in der Maske: Bis
 * zum 16.09.2026 legte man eine gewöhnliche Position an und schaltete sie
 * danach um — wenn man wusste, dass es das Feld gibt.
 */
export function rollenfelder(wert: Rollenwert): { optional?: boolean; alternative_zu?: string } {
  if (wert === "optional") return { optional: true };
  if (wert.startsWith("statt:")) return { alternative_zu: wert.slice(6) };
  return {};
}

/**
 * Das Auswahlfeld für die Rolle einer **neuen** Zeile.
 *
 * Es zeigt die Regeln, statt jemanden hineinlaufen zu lassen
 * (`packages/fach/src/optionen.ts`):
 *
 * - Eine **Textzeile** trägt keinen Betrag und kann weder optional noch
 *   Alternative sein — dort steht das Feld gar nicht erst.
 * - **Optional und Alternative zugleich** gibt es nicht. Ein einziges
 *   Auswahlfeld macht das unmöglich, statt es hinterher zu melden.
 * - Eine Alternative hängt an einer Position, die **selbst zählt** und einen
 *   Betrag trägt — keine Kette, keine Alternative zu einer Option. Zur Wahl
 *   stehen deshalb nur solche Elternpositionen.
 *
 * Gibt es noch gar keine Zeile, stehen „in Summe" und „optional" da und sonst
 * nichts: Eine Alternative braucht etwas, wozu sie die Alternative ist.
 */
function Rollenwahl({ art, zeilen, wert, aendern }: {
  art: NeueArt;
  zeilen: Position[];
  wert: Rollenwert;
  aendern: (wert: Rollenwert) => void;
}) {
  const t = useT();
  const nummern = useMemo(() => positionsnummern(zeilen, t), [zeilen, t]);

  if (!WAEHLBAR.has(art)) return null;

  const eltern = zeilen.filter((p) => p.id && zaehlt(p) && WAEHLBAR.has(p.art ?? ""));

  return (
    <select
      className="opt-rolle"
      value={wert}
      title={t("optionen.rolleSchild")}
      aria-label={t("optionen.rolleSchild")}
      onChange={(e) => aendern(e.target.value)}
    >
      <option value="summe">{t("optionen.rolleSumme")}</option>
      <option value="optional">{t("optionen.rolleOptional")}</option>
      {eltern.map((p) => (
        <option key={p.id} value={`statt:${p.id}`}>
          {t("optionen.rolleStatt", { nr: nummern.get(p.id ?? "") ?? "", was: p.was })}
        </option>
      ))}
    </select>
  );
}

/** Anlegen, mit dem Fehler aus dem Auflösen der Vorgangsnummer. */
function useAnlegen(vorgang: () => Promise<string>, fertig: () => void) {
  const ruf = useRuf();
  const anlegen = usePositionAnlegen();
  const [wegFehler, setzeWegFehler] = useState<unknown>(null);

  async function los(eingabe: Omit<Parameters<typeof anlegen.mutateAsync>[0], "nr">) {
    setzeWegFehler(null);
    try {
      const nr = await vorgang();
      await anlegen.mutateAsync({ nr, ...eingabe });
      ruf("posDazu");
      fertig();
    } catch (e) {
      setzeWegFehler(e);
    }
  }

  const fehler = wegFehler ?? (anlegen.isError ? anlegen.error : null);
  return { los, laeuft: anlegen.isPending, fehler };
}

/**
 * Der Fuß einer Eingabezeile: Fehler, Hinzufügen, Abbrechen — fester Platz.
 *
 * Er ist eine **zweite Zeile der Tabelle** über die ganze Breite, wie die
 * aufgeklappte Unterzeile in der Abrechnung: Rechenweg, Zubehör und die zwei
 * Knöpfe passen in keine Spalte, und ein Kasten neben der Tabelle wäre wieder
 * das Formular, das hier gerade abgeschafft wird.
 */
function Zeilenfuss({ form, bereit, laeuft, fehler, los, abbrechen, gesperrt, vorne, losWort }: {
  form: Positionsform;
  bereit: boolean;
  laeuft: boolean;
  fehler: unknown;
  los: () => void;
  abbrechen: () => void;
  gesperrt?: string | undefined;
  vorne?: ReactNode;
  /** Ein anderes Wort für den Hauptknopf — nach einer Rückfrage (Befund 23). */
  losWort?: string | undefined;
}) {
  const t = useT();
  const mitRabatt = useContext(MitRabattspalte);
  return (
    <tr role="row" className="pos-neu-fuss-zeile">
      <td role="cell" colSpan={zellenZahl(form) + (mitRabatt ? 1 : 0)}>
        <div className="pos-neu-fuss">
          {vorne ? <div className="pos-neu-vorne">{vorne}</div> : null}
          {fehler ? <FehlerBand pfad={positionsFehlerPfad(fehler)} werte={fehlerWerte(fehler)} /> : null}
          <div className="pos-neu-knoepfe">
            <Knopf art="haupt" klein disabled={!bereit || laeuft || Boolean(gesperrt)} onClick={los}>
              {t(losWort ?? "positionen.hinzufuegen")}
            </Knopf>
            <Knopf art="stumm" klein onClick={abbrechen}>{t("allgemein.abbrechen")}</Knopf>
          </div>
        </div>
      </td>
    </tr>
  );
}

/**
 * Die Zellen einer Eingabezeile — dieselben sieben (oder sechs) wie im Kopf.
 *
 * Sie stehen hier einmal und nicht dreimal: Miete, Katalog und Text schicken
 * ihre Inhalte als Werte hinein, und die Zahl der Zellen bleibt damit
 * zwangsläufig dieselbe wie in `zellenZahl`.
 */
function Eingabezeile({ form, was, art, menge, einzel, summe }: {
  form: Positionsform;
  was: ReactNode;
  art: ReactNode;
  menge?: ReactNode;
  einzel?: ReactNode;
  summe?: ReactNode;
}) {
  const t = useT();
  // Steht die Rabattspalte, bekommt auch die neue Zeile ihre Zelle — leer:
  // Rabattiert wird eine Zeile, die es schon gibt.
  const mitRabatt = useContext(MitRabattspalte);
  return (
    <tr role="row" className="pos-neu-zeile">
      <td role="cell" className="pos-nr" data-kopf={t("angebotBauen.spaltePos")} />
      <td role="cell" className="pos-was" data-kopf={t("angebotBauen.spaltePosition")} data-traeger="ja">
        {was}
      </td>
      <td role="cell" className="pos-art" data-kopf={t("angebotBauen.spalteArt")}>{art}</td>
      <td role="cell" className="r pos-menge" data-kopf={t("angebotBauen.spalteMenge")}>{menge}</td>
      <td role="cell" className="r" data-kopf={t("angebotBauen.spalteEinzel")}>{einzel}</td>
      {mitRabatt ? <td role="cell" className="r pos-rabatt-zelle" data-kopf={t("rabatt.spalte")} /> : null}
      <td role="cell" className="r geld" data-kopf={t("angebotBauen.spalteSumme")}>{summe}</td>
      {form === "angebot" ? <td role="cell" className="pos-weg" /> : null}
    </tr>
  );
}

/**
 * Miete: Typ, Zeitraum, Preis mit Rechenweg — als Zeile.
 *
 * Wer einen Preis mitgibt, bekommt seinen Preis; ohne rechnet die API aus dem
 * Tarif. Der Zeitraum der ersten Miete ist die Vorgabe (Regel 11).
 */
function MieteZeile({ form, vorgang, kunde, vorgabe, gesperrt, zeilen = [], wahl = false, fertig, abbrechen }: ZeilenEigenschaften) {
  const t = useT();
  const erstfokus = useErstfokus();
  const { data: typenListe } = useMaschinentypen();
  const [typWahl, setzeTypWahl] = useState("");
  const [spanne, setzeSpanne] = useState<Spanne>(vorgabe ?? { von: null, bis: null });
  const [neuPreis, setzeNeuPreis] = useState("");
  const [rolle, setzeRolle] = useState<Rollenwert>("summe");
  /* Zubehör ist eine eigene Maschine mit eigenem Kalender, gebunden an den
     Träger — deshalb geht es mit der Position hinaus, nicht als Textzeile. */
  const [zubehoer, setzeZubehoer] = useState<ZubehoerWert>(ZUBEHOER_LEER);
  const { los, laeuft, fehler } = useAnlegen(vorgang, fertig);

  useEffect(() => {
    if (!vorgabe?.von || !vorgabe.bis) return;
    setzeSpanne((alt) => (alt.von || alt.bis ? alt : vorgabe));
  }, [vorgabe]);

  /* **Nach der Maschine kommt der Zeitraum — ohne Umweg.** Steht noch keiner,
     geht der Kalender gleich auf: Das Feld bekommt den Fokus, und der
     Wähler öffnet sich daran als Überlagerung (`Zeitraum.tsx`). Steht schon
     einer (die Vorgabe aus der ersten Miete), bleibt es still. */
  useEffect(() => {
    if (typWahl && !spanne.von && erstfokus) document.getElementById("pos-neu-zeitraum")?.focus();
    // Nur beim Wechsel des Typs — nicht bei jeder Änderung am Zeitraum.
  }, [typWahl]);

  /* **Was nur am Trägergerät hinausgeht, steht hier nicht** (Systemprüfung,
     Befund 10) — wie in der Schnellerfassung. Lisa bot die Schaufel allein
     an und bekam eine Meldung, die auf ein rotes Feld zeigte, das es nicht
     gab. Sie kommt mit ihrem Träger dazu (Anbauteile an der Miete). */
  const { data: typenMitZubehoer } = useZubehoerTypen();
  const typen = useMemo(() => {
    const nurMitTraeger = new Set(
      (typenMitZubehoer?.zeilen ?? []).filter((x) => x.art === "anbaupflichtig").map((x) => x.id),
    );
    return (typenListe?.zeilen ?? []).filter((x) => !nurMitTraeger.has(x.id));
  }, [typenListe, typenMitZubehoer]);
  const gruppen = useMemo(
    () => [...new Set(typen.map((x) => x.gruppe).filter(Boolean))].sort((a, b) => a.localeCompare(b, "de")),
    [typen],
  );

  /* ── Was in dieser Woche frei ist — schon in der Geräteliste ──
     Luca, 21.09.2026: *„Da muss man erst das Gerät wählen und dann den
     Zeitraum. Es ist eigentlich dumm, dass man in der gleichen Maske machen
     muss, um zu sehen, welches Gerät verfügbar ist."*

     Bis dahin stand der Zeitraum **nach** der Wahl: Wer wissen wollte, ob
     der Radlader frei ist, wählte ihn, sah „belegt", ging zurück und
     probierte den nächsten. Jetzt steht er darüber, und jede Zeile trägt
     ihre Lage.

     **Dieselbe Frage wie im Nachschlagen** (`GET /nachschlagen`, `typen=…`)
     und keine zweite: Gefragt wird für genau die Zeilen, die gerade in der
     Liste stehen — das meldet der Wähler (`sichtbar`). Ohne Zeitraum wird
     nicht gefragt; eine Lage zu einem erfundenen Fenster wäre eine Antwort
     auf eine Frage, die niemand gestellt hat. */
  const [inListe, setzeInListe] = useState<string[]>([]);
  const lagen = useNachschlagen(
    { wort: "", gruppe: null, von: spanne.von ?? "", bis: spanne.bis ?? "", typen: inListe },
    Boolean(spanne.von && spanne.bis && inListe.length > 0),
  );
  const lageJeTyp = useMemo(() => {
    const karte = new Map<string, Typenlage>();
    for (const z of lagen.data?.zeilen ?? []) karte.set(z.typId, z);
    return karte;
  }, [lagen.data]);

  /* ── „Das letzte Mal war ja gut." ──
     Dieselbe Abfrage wie in der Schnellerfassung (`useVerlauf`): die
     Aufträge dieses Kunden, das Neueste zuerst. Aus ihrer Spalte
     `geraete` — den **Typen**, mit „ · " verbunden (Regel 2) — werden
     wieder Typen, indem die Namen im Stamm nachgeschlagen werden. Das
     geht auf, weil `geraete` aus `machine_type.label` gebaut ist und der
     Stamm dieselben Namen führt; was dort nicht steht, hatte damals
     keinen Typ und lässt sich auch nicht wiederholen.

     **Keine zweite Route dafür.** Die Liste bringt alles mit; ein Feld
     mit Kennungen wäre eine Angabe, die sich ableiten lässt
     (28-designkonzept.md, Leitsatz 4).

     Vier, nicht die Mietgeschichte: Es ist ein kurzer Weg und kein
     zweiter Katalog. Wer den fünften Typ sucht, sucht ihn. */
  const { data: verlauf } = useVerlauf(kunde ?? "");
  const zuletzt = useMemo(() => {
    if (!kunde) return [];
    const namen: string[] = [];
    (verlauf?.zeilen ?? []).forEach((v) => {
      (v.geraete ?? "").split(" · ").forEach((n) => {
        const name = n.trim();
        if (name && !namen.includes(name)) namen.push(name);
      });
    });
    return namen
      .map((n) => typen.find((x) => x.label === n)?.id)
      .filter((x): x is string => !!x)
      .slice(0, 4);
  }, [kunde, verlauf, typen]);
  const gewaehlterTyp = typen.find((x) => x.id === typWahl);
  const vollstaendig = Boolean(typWahl && spanne.von && spanne.bis);

  /* Die Ausstattung folgt dem Typ: Wer den Typ wechselt, bekommt dessen
     Vorgabe — nicht die Ausstattung des vorigen, die es dort nicht gibt. */
  const [variante, setzeVariante] = useState("");
  // An der Kennung, nicht am Objekt: Ein Nachladen des Katalogs soll eine
  // gewählte Ausstattung nicht auf die Vorgabe zurücksetzen.
  useEffect(() => { setzeVariante(ausstattungVon(gewaehlterTyp).vorgabe); }, [gewaehlterTyp?.id]);

  const { data: preis, isFetching: preisLaeuft } = usePreis(
    typWahl || undefined, spanne.von, spanne.bis, kunde, undefined, variante || undefined,
  );
  /* **Die Stückzahl** (Migration 0194, Lisas Pool-Wunsch): nur bei einem
     gezählten Typ — zwanzig Fahrbahnplatten sind eine Zeile. Die Summe ist
     Satz × Tage × Stück, und die Verfügbarkeit fragt nach genau so vielen. */
  const pool = gewaehlterTyp?.pool === true;
  const [stueckFeld, setzeStueckFeld] = useState("1");
  useEffect(() => { setzeStueckFeld("1"); }, [typWahl]);
  const stueck = pool ? Math.max(1, Math.round(alsZahl(stueckFeld) ?? 1)) : 1;
  const { data: lage } = useVerfuegbarkeit(typWahl || undefined, spanne.von, spanne.bis, true, stueck);

  /* **Kein Gerät frei: erst fragen, dann anlegen** (Systemprüfung, Befund 23,
     24.09.2026). Lisa bot in der neuen Fassung genau das Gerät wieder an,
     das nicht frei war — der Grund für die neue Fassung —, und nichts hielt
     sie auf: Die Karte sagte leise „belegt", der Knopf legte an. Luca
     entschied (b): Rückfrage „trotzdem anbieten?". Keine Sperre — Regel 2
     und Prozess B1 wollen den verkauften Typ auch ohne festes Stück; die
     Annahme bucht nach, was dann frei ist. Ein anderer Typ oder Zeitraum
     nimmt die Frage zurück. */
  const belegt = lage?.lage === "nein";
  const [rueckfrage, setzeRueckfrage] = useState(false);
  useEffect(() => { setzeRueckfrage(false); }, [typWahl, spanne.von, spanne.bis]);

  const vonHand = alsZahl(neuPreis);
  const summe = preis
    ? Math.round((vonHand !== undefined ? vonHand * preis.menge : preis.netto) * stueck * 100) / 100
    : null;

  const anlegen = () => {
    if (!typWahl || !spanne.von || !spanne.bis) return;
    if (betragFehler(neuPreis) !== null) return; // der Satz steht am Feld (N4-06)
    if (belegt && !rueckfrage) { setzeRueckfrage(true); return; }
    void los({
      art: "miete",
      maschinentyp_id: typWahl,
      ...(variante ? { variante_id: variante } : {}),
      bezeichnung: mitAusstattung(gewaehlterTyp?.label ?? t("allgemein.position"), gewaehlterTyp, variante),
      von: spanne.von,
      bis: spanne.bis,
      ...(vonHand !== undefined ? { einzelpreis: vonHand } : {}),
      ...(wahl ? rollenfelder(rolle) : {}),
      zubehoer: zubehoer.stuecke,
      ...(pool && stueck > 1 ? { stueck } : {}),
    });
  };

  return (
    <>
      <Eingabezeile
        form={form}
        was={
          <>
            {gewaehlterTyp ? (
              <button type="button" className="pos-neu-gewaehlt" onClick={() => setzeTypWahl("")}
                title={t("positionen.andererTyp")}>
                <b>{gewaehlterTyp.label}</b>
                <span>{gewaehlterTyp.gruppe}</span>
              </button>
            ) : (
              <Katalogwahl
                /* **Der Zeitraum steht über der Liste**, nicht dahinter
                   (21.09.2026). Solange keiner steht, sagt die Liste den
                   Bestand — die Auskunft, die sie vorher immer gab. */
                kopf={
                  <>
                    <span className="pos-neu-wahl-wort">{t("angebotBauen.zuerstWann")}</span>
                    <Zeitraum id="pos-neu-zeitraum-wahl" wert={spanne} aendern={setzeSpanne} />
                  </>
                }
                sichtbar={setzeInListe}
                eintraege={typen.map((x) => {
                  const l = lageJeTyp.get(x.id);
                  return {
                    id: x.id,
                    titel: x.label,
                    unten: x.gruppe,
                    gruppe: x.gruppe,
                    /* **Punkt und Zahl, nie Farbe allein** (28-designkonzept.md).
                       „2 von 5 frei" ist die Auskunft, mit der jemand wählt;
                       „ab Do 24.09." ist die, mit der er weitersucht, wenn
                       die erste nein heißt. */
                    rand: l
                      ? (
                        <>
                          <Punkt art={l.lage === "geht" ? "gut" : l.lage === "teils" ? "acht" : "stopp"} />
                          {l.frei > 0
                            ? t("angebotBauen.typFrei", { frei: l.frei, bestand: l.bestand })
                            : l.freiAb
                              ? t("angebotBauen.typFreiAb", { tag: tagKurz(l.freiAb) })
                              : t("angebotBauen.typBelegt")}
                        </>
                      )
                      : t(x.bestand === 1 ? "angebotBauen.typBestand" : "angebotBauen.typBestandPlural", { zahl: x.bestand }),
                  };
                })}
                gruppen={gruppen}
                platz={t("angebotBauen.typSuche")}
                leerSatz={() => t("angebotBauen.keinTyp")}
                zuletzt={zuletzt}
                zuletztWort="positionen.zuletztGemietet"
                waehlen={setzeTypWahl}
                abbrechen={abbrechen}
              />
            )}
            <AusstattungWahl typ={gewaehlterTyp} wert={variante} aendern={setzeVariante}
              className="feld pos-neu-feld pos-neu-ausstattung" />
            {pool ? (
              <label className="pos-neu-stueck">
                <span>{t("bestand.stueckFrage")}</span>
                <input
                  className="feld feld-zahl pos-neu-feld"
                  inputMode="numeric"
                  value={stueckFeld}
                  aria-label={t("bestand.stueckFrage")}
                  onChange={(e) => setzeStueckFeld(e.target.value.replace(/\D/g, ""))}
                />
                {lage?.pool ? (
                  <span className="rechenweg">{t("bestand.freiVon", { frei: lage.frei ?? 0, bestand: lage.bestand ?? 0 })}</span>
                ) : null}
              </label>
            ) : null}
            {gewaehlterTyp ? (
              <span className="pos-neu-zeit">
                <Zeitraum id="pos-neu-zeitraum" wert={spanne} aendern={setzeSpanne} />
              </span>
            ) : null}
          </>
        }
        art={
          <>
            <span className="pos-art-wort">{t("angebotBauen.artMiete")}</span>
            {wahl ? <Rollenwahl art="miete" zeilen={zeilen} wert={rolle} aendern={setzeRolle} /> : null}
          </>
        }
        menge={preis && vollstaendig ? (
          <>
            <span className="pos-mengeneinheit">
              <span className="pos-regel-zahl">{preis.menge}</span>
              <span className="pos-einheit">{einheitFuer(t, preis.einheit_key, Number(preis.menge))}</span>
            </span>
            {stueck > 1 ? <span className="rechenweg">{t("bestand.malStueck", { zahl: stueck })}</span> : null}
          </>
        ) : <span className="leise">{t("allgemein.strich")}</span>}
        einzel={gewaehlterTyp ? (
          <>
            <input
              className="feld feld-zahl pos-neu-feld"
              inputMode="decimal"
              value={neuPreis}
              aria-invalid={betragFehler(neuPreis) !== null}
              placeholder={preis && vollstaendig ? euro(preis.einzelpreis) : t("allgemein.strich")}
              aria-label={t("angebotBauen.preisVonHand")}
              onChange={(e) => setzeNeuPreis(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && betragFehler(neuPreis) === null) anlegen();
                if (e.key === "Escape") abbrechen();
              }}
            />
            <Betragssatz text={neuPreis} />
          </>
        ) : null}
        summe={summe !== null && vollstaendig ? euro(summe) : <span className="leise">{t("allgemein.strich")}</span>}
      />
      <Zeilenfuss
        form={form}
        bereit={vollstaendig}
        laeuft={laeuft}
        fehler={fehler}
        los={anlegen}
        abbrechen={abbrechen}
        gesperrt={gesperrt}
        losWort={rueckfrage && belegt ? (form === "angebot" ? "positionen.trotzdemAnbieten" : "positionen.trotzdemAufnehmen") : undefined}
        vorne={gewaehlterTyp ? (
          <>
            {rueckfrage && belegt ? (
              <span className="pos-neu-lage">
                <Punkt art="stopp" /> {t(form === "angebot" ? "positionen.belegtFrageAngebot" : "positionen.belegtFrageAuftrag")}
              </span>
            ) : null}
            <ZubehoerWahl typId={typWahl} von={spanne.von} bis={spanne.bis}
              wert={zubehoer} aendern={setzeZubehoer} className="pos-neu-zubehoer" />
            {!vollstaendig ? (
              <span className="rechenweg">{t("angebotBauen.preisOhneZeitraum")}</span>
            ) : (
              <>
                <span className="pos-neu-lage">
                  {lage ? <><Punkt art={lage.punkt} /> {t(lage.text_key)}</> : t("allgemein.laedt")}
                </span>
                {preisLaeuft && !preis ? <span className="rechenweg">{t("allgemein.laedt")}</span> : null}
                {!preisLaeuft && !preis ? <span className="rechenweg">{t("angebotBauen.preisKeinTarif")}</span> : null}
                {/* Der Rechenweg: Wer am Telefon einen Preis nennt, muss sagen
                    können, wie er zustande kommt. */}
                {preis?.schritte.map((sch, i) => (
                  <span className="rechenweg" key={`${sch.key}${i}`}>{t(sch.key, sch as never)}</span>
                ))}
                {vonHand !== undefined && preis ? (
                  <span className="rechenweg opt-hinweis">{t("angebotBauen.vonHandStatt", { tarif: euro(preis.einzelpreis) })}</span>
                ) : null}
              </>
            )}
          </>
        ) : null}
      />
    </>
  );
}

/**
 * Leistung und Ware: aus dem Katalog, Menge gefragt.
 *
 * Bezeichnung, Einheit und Satz kommen aus dem Stamm; die API schreibt
 * `service_type_id` bzw. `goods_article_id` an die Position. Freitext ist der
 * letzte Eintrag der Liste — ein ausdrücklicher Weg, kein stiller.
 */
function KatalogZeile({ art, form, vorgang, gesperrt, zeilen = [], wahl = false, fertig, abbrechen }: ZeilenEigenschaften & { art: "leistung" | "verkauf" }) {
  const t = useT();
  const erstfokus = useErstfokus();
  const { data: leistungsListe } = useLeistungsarten();
  const { data: warenListe } = useWaren();
  const [gewaehlt, setzeGewaehlt] = useState("");
  const [bezeichnung, setzeBezeichnung] = useState("");
  const [menge, setzeMenge] = useState("");
  const [preis, setzePreis] = useState("");
  const [rolle, setzeRolle] = useState<Rollenwert>("summe");
  /* **Wann die Leistung stattfinden soll** (21.09.2026, Migration 0154).
     Luca: *„Was bei der PV-Reinigung fehlt, ist eine Einordnung vom Datum,
     weil man ja auch abschätzen muss, wann das passiert. Man macht die grobe
     Vorplanung mit KW im Angebot."*

     Eine **Woche**, kein Datum: Ein Tag im Angebot wäre eine Zusage, die
     niemand gegeben hat, und er stünde auf dem Papier des Kunden. Freiwillig
     — wer die Woche noch nicht weiß, lässt sie leer und die Zeile steht ohne
     Zeit da, wie bisher. Nur bei einer Leistung: Eine Ware hat keinen
     Zeitraum. */
  const [woche, setzeWoche] = useState<{ von: string; bis: string } | null>(null);
  const mengeFeld = useRef<HTMLInputElement>(null);
  const { los, laeuft, fehler } = useAnlegen(vorgang, fertig);

  const FREI = "frei";

  /* Beide Kataloge in dieselbe Form: Titel, Einheit, Satz, Aufmaß. */
  const katalog = useMemo(() => (art === "leistung"
    ? (leistungsListe?.zeilen ?? []).filter((l) => l.aktiv).map((l) => ({
        id: l.id, titel: l.label, einheit: einheitAusGroesse(l.groesse), netto: l.netto, aufmass: l.ortsaufnahme,
      }))
    : (warenListe?.zeilen ?? []).filter((w) => w.aktiv).map((w) => ({
        id: w.id, titel: w.label, einheit: w.einheit, netto: w.netto, aufmass: false,
      }))), [art, leistungsListe, warenListe]);

  const satz = katalog.find((k) => k.id === gewaehlt);

  useEffect(() => {
    if (gewaehlt && gewaehlt !== FREI && erstfokus) mengeFeld.current?.focus();
  }, [gewaehlt, erstfokus]);

  const waehlen = (id: string) => {
    setzeGewaehlt(id);
    setzeBezeichnung(katalog.find((k) => k.id === id)?.titel ?? "");
  };

  const mengeZahl = alsZahl(menge);
  const vonHand = alsZahl(preis);
  const einzel = vonHand ?? satz?.netto ?? null;
  const summe = einzel !== null ? Math.round((mengeZahl ?? (satz?.aufmass ? 0 : 1)) * einzel * 100) / 100 : null;

  const bereit = gewaehlt === FREI ? Boolean(bezeichnung.trim()) : Boolean(satz);

  const anlegen = () => {
    if (!bereit || betragFehler(preis) !== null) return;
    const katalogfeld = art === "leistung" ? "leistungsart_id" : "ware_id";
    void los({
      art,
      ...(gewaehlt === FREI ? { bezeichnung: bezeichnung.trim() } : { [katalogfeld]: gewaehlt }),
      // Nur eine geänderte Bezeichnung geht mit — sonst nimmt die API die aus dem Stamm.
      ...(gewaehlt !== FREI && bezeichnung.trim() && bezeichnung.trim() !== satz?.titel
        ? { bezeichnung: bezeichnung.trim() } : {}),
      ...(mengeZahl !== undefined ? { menge: mengeZahl } : {}),
      ...(vonHand !== undefined ? { einzelpreis: vonHand } : {}),
      // Die Woche geht als `zeitschaerfe: 'kw'` mit — Montag und Sonntag
      // rechnet die API daraus (Regel 11, `routen/vorgaenge.ts`).
      ...(art === "leistung" && woche ? { von: woche.von, bis: woche.bis, zeitschaerfe: "kw" as const } : {}),
      ...(wahl ? rollenfelder(rolle) : {}),
    });
  };

  const beiTaste = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") { e.preventDefault(); anlegen(); }
    if (e.key === "Escape") { e.preventDefault(); abbrechen(); }
  };

  /* Die Schlüssel ausgeschrieben, nicht zusammengesetzt — sonst findet sie
     niemand, der nach einem verwaisten Satz sucht. */
  const satzfuer = art === "leistung"
    ? { suche: "positionen.sucheLeistung", kein: "positionen.keineLeistung", leer: "positionen.leerLeistung", andere: "positionen.andereLeistung" }
    : { suche: "positionen.sucheWare", kein: "positionen.keineWare", leer: "positionen.leerWare", andere: "positionen.andereWare" };

  return (
    <>
      <Eingabezeile
        form={form}
        was={!gewaehlt ? (
            <Katalogwahl
              eintraege={katalog.map((k) => ({
                id: k.id,
                titel: k.titel,
                unten: t(k.einheit),
                rand: (
                  <>
                    {k.aufmass ? <Marke art="acht">{t("positionen.aufmassMarke")}</Marke> : null}
                    {k.netto === null
                      ? <Marke art="stopp">{t("positionen.keinPreis")}</Marke>
                      : <span className="nr">{t("positionen.jeEinheit", { betrag: euro(k.netto), einheit: t(k.einheit) })}</span>}
                  </>
                ),
              }))}
              platz={t(satzfuer.suche)}
              leerSatz={(suchwort) => (katalog.length
                ? t(satzfuer.kein, { wort: suchwort })
                : t(satzfuer.leer))}
              waehlen={waehlen}
              abbrechen={abbrechen}
              fuss={
                <button type="button" className="pos-neu-frei" onClick={() => { setzeGewaehlt(FREI); setzeBezeichnung(""); }}>
                  {t(satzfuer.andere)}
                </button>
              }
            />
          ) : (
            <>
              <input
                className="feld feld-zeile pos-neu-feld"
                value={bezeichnung}
                autoFocus={erstfokus && gewaehlt === FREI}
                placeholder={t(art === "leistung" ? "angebotBauen.platzLeistung" : "angebotBauen.platzVerkauf")}
                aria-label={t("angebotBauen.bezeichnung")}
                onChange={(e) => setzeBezeichnung(e.target.value)}
                onKeyDown={beiTaste}
              />
              {/* **Die Woche steht neben der Bezeichnung**, wo bei einer
                  Miete der Zeitraum steht — dieselbe Stelle, dieselbe
                  Frage, nur gröber. Eine Ware bekommt sie nicht: Sie wird
                  geliefert und nicht terminiert. */}
              {art === "leistung" ? (
                <span className="pos-neu-zeit">
                  <Wochenwahl
                    id="pos-neu-woche"
                    wert={woche?.von ?? null}
                    bis={woche?.bis ?? null}
                    aendern={setzeWoche}
                    leerWort={t("positionen.wannOffen")}
                  />
                </span>
              ) : null}
              {satz?.aufmass && mengeZahl === undefined ? (
                <span className="pos-hinweis">{t("positionen.mengeAufmass")}</span>
              ) : null}
              {(satz && satz.netto === null && vonHand === undefined) || (gewaehlt === FREI && vonHand === undefined) ? (
                <span className="pos-marke"><Marke art="stopp">{t("positionen.keinPreis")}</Marke></span>
              ) : null}
            </>
          )}
        art={
          <>
            <span className="pos-art-wort">{t(ARTWORT[art] ?? "angebotBauen.artLeistung")}</span>
            {wahl ? <Rollenwahl art={art} zeilen={zeilen} wert={rolle} aendern={setzeRolle} /> : null}
          </>
        }
        menge={gewaehlt ? (
          <span className="pos-mengeneinheit">
            <input
              ref={mengeFeld}
              className="feld feld-zahl pos-neu-feld"
              inputMode="decimal"
              value={menge}
              // Leer heißt eins — außer, die Menge folgt aus dem Aufmaß.
              placeholder={satz?.aufmass ? t("allgemein.strich") : String(1)}
              aria-label={t("angebotBauen.spalteMenge")}
              onChange={(e) => setzeMenge(e.target.value)}
              onKeyDown={beiTaste}
            />
            {satz ? <span className="pos-einheit">{einheitFuer(t, satz.einheit, alsZahl(menge) ?? 1)}</span> : null}
          </span>
        ) : null}
        einzel={gewaehlt ? (
          <>
            <input
              className="feld feld-zahl pos-neu-feld"
              inputMode="decimal"
              value={preis}
              aria-invalid={betragFehler(preis) !== null}
              placeholder={satz?.netto !== null && satz?.netto !== undefined ? euro(satz.netto) : t("allgemein.strich")}
              aria-label={t("angebotBauen.spalteEinzel")}
              onChange={(e) => setzePreis(e.target.value)}
              onKeyDown={beiTaste}
            />
            <Betragssatz text={preis} />
          </>
        ) : null}
        summe={gewaehlt
          ? (summe !== null ? euro(summe) : <span className="leise">{t("allgemein.strich")}</span>)
          : null}
      />
      <Zeilenfuss
        form={form}
        bereit={bereit}
        laeuft={laeuft}
        fehler={fehler}
        los={anlegen}
        abbrechen={abbrechen}
        gesperrt={gesperrt}
      />
    </>
  );
}

/**
 * Text: eine Zeile ohne Betrag — und seit dem 16.09.2026 mit Schnellzugriff.
 *
 * Luca: *„Im Angebot haben wir die Möglichkeit, Freitexte zu positionieren.
 * Wir wollen Freitexte auch während der Anlage speichern können und dann den
 * Schnellzugriff auf diese abgespeicherten Schnelltexte haben."*
 *
 * Das freie Feld bleibt, was es war — man tippt hinein und drückt Enter. Der
 * Schnellzugriff steht **daneben**, nicht davor: „+ Text" ist der Weg zu einer
 * eigenen Zeile, und wer jedes Mal erst eine Liste wegklicken müsste, hätte
 * einen Schritt mehr für den Regelfall (Regel 11). Ein gewählter Baustein
 * steht danach im Feld und ist dort **noch änderbar**.
 *
 * Eine Textzeile kann weder optional noch Alternative sein
 * (`packages/fach/src/optionen.ts`) — deshalb steht hier kein Rollenfeld.
 */
function TextZeile({ form, vorgang, gesperrt, fertig, abbrechen }: ZeilenEigenschaften) {
  const t = useT();
  const erstfokus = useErstfokus();
  const [text, setzeText] = useState("");
  const feld = useRef<HTMLInputElement>(null);
  const { los, laeuft, fehler } = useAnlegen(vorgang, fertig);

  const anlegen = () => {
    if (!text.trim()) return;
    // Eine Textzeile hat keinen Betrag — das ist ihr ganzer Zweck.
    void los({ art: "text", bezeichnung: text.trim(), menge: 0, einzelpreis: 0 });
  };

  return (
    <>
      <Eingabezeile
        form={form}
        was={
          <input
            ref={feld}
            className="feld feld-zeile pos-neu-feld"
            autoFocus={erstfokus}
            value={text}
            placeholder={t("angebotBauen.platzText")}
            aria-label={t("angebotBauen.bezeichnung")}
            onChange={(e) => setzeText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); anlegen(); }
              if (e.key === "Escape") { e.preventDefault(); abbrechen(); }
            }}
          />
        }
        art={<span className="pos-art-wort">{t("angebotBauen.artText")}</span>}
      />
      <Zeilenfuss
        form={form}
        bereit={Boolean(text.trim())}
        laeuft={laeuft}
        fehler={fehler}
        los={anlegen}
        abbrechen={abbrechen}
        gesperrt={gesperrt}
        vorne={
          <Bausteinzugriff
            art="positionstext"
            wert={text}
            klein
            einsetzen={(neuerText) => { setzeText(neuerText); feld.current?.focus(); }}
          />
        }
      />
    </>
  );
}

/* ══════════════ Die Karten ══════════════ */

/* Fünf Zeichen, gezeichnet und nicht geladen: Maschine, Werkzeug, Paket,
   Aufschlag, Textzeile. Sie tragen keine Bedeutung allein — das Wort steht
   daneben. */
const ZEICHEN: Record<NeueArt, ReactNode> = {
  miete: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6.5" cy="17" r="2.8" />
      <circle cx="15.5" cy="17" r="2.8" />
      <path d="M3 14.5V10h10.5l3.5 4.5" />
      <path d="M5.5 10V4.5H11V10" />
      <path d="M16 11.5l3.5-4" />
      <path d="M18 5.5h3.5V10l-3-1" />
    </svg>
  ),
  leistung: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 6.2a4 4 0 0 0-5.2 5.2L4 16.7 7.3 20l5.3-5.3a4 4 0 0 0 5.2-5.2l-2.6 2.6-2.3-.6-.6-2.3z" />
    </svg>
  ),
  verkauf: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.5 7.5 12 3.5l8.5 4v9L12 20.5l-8.5-4z" />
      <path d="M3.5 7.5 12 11.5l8.5-4M12 11.5v9" />
    </svg>
  ),
  zuschlag: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5.5v13M5.5 12h13" />
      <circle cx="12" cy="12" r="8.5" />
    </svg>
  ),
  text: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M4 6.5h16M4 10.5h16M4 14.5h11M4 18.5h7" />
    </svg>
  ),
};

/** Was das Angebot zeigt. Die Schnellerfassung gibt ihre eigene Reihe mit. */
const REIHENFOLGE: NeueArt[] = ["miete", "leistung", "verkauf", "text"];

/**
 * Die vier Karten.
 *
 * `erste`: Die Tabelle ist leer — die Karten sind der Inhalt, mit Wort und
 * einem Beispiel aus den echten Daten. Sonst: eine schmale Reihe
 * „+ Miete · + Leistung · + Ware · + Text", die Tabelle hat Vorrang.
 *
 * Tastatur: Pfeile wandern, 1–4 wählt, Enter und Leertaste wie gewohnt.
 */
export function Karten({ erste, aktiv, gesperrt, waehlen, arten = REIHENFOLGE, schmal, zusatz }: {
  erste: boolean;
  aktiv: NeueArt | "";
  gesperrt?: string | undefined;
  waehlen: (art: NeueArt) => void;
  /** Welche Arten zur Wahl stehen. Ohne Angabe die vier des Angebots. */
  arten?: NeueArt[];
  /** In einer Seitenleiste ist kein Platz für vier Karten nebeneinander —
   *  dort stehen sie als Reihe kleiner Knöpfe, die umbrechen darf. */
  schmal?: boolean | undefined;
  /** Was hinter den Karten in derselben Reihe steht — „+ Rabatt". Keine Art. */
  zusatz?: ReactNode;
}) {
  const t = useT();
  const reihe = useRef<HTMLDivElement>(null);
  const { data: typenListe } = useMaschinentypen();
  const { data: leistungsListe } = useLeistungsarten();
  const { data: warenListe } = useWaren();

  /* Die Beispiele sind gelesen, nicht geschrieben: die ersten drei
     Gerätegruppen, Leistungsarten und Warenartikel. Ist ein Katalog leer,
     steht nichts — ein erfundenes Beispiel wäre eine Behauptung. */
  const beispiele = useMemo(() => {
    const kurz = (liste: string[]) => {
      if (!liste.length) return "";
      const drei = liste.slice(0, 3).join(` ${t("allgemein.trennpunkt")} `);
      return liste.length > 3 ? t("positionen.beispielMehr", { liste: drei }) : drei;
    };
    return {
      miete: kurz([...new Set((typenListe?.zeilen ?? []).map((x) => x.gruppe).filter(Boolean))]),
      leistung: kurz((leistungsListe?.zeilen ?? []).filter((l) => l.aktiv).map((l) => l.label)),
      verkauf: kurz((warenListe?.zeilen ?? []).filter((w) => w.aktiv).map((w) => w.label)),
      zuschlag: "",
      text: "",
    } as Record<NeueArt, string>;
  }, [typenListe, leistungsListe, warenListe, t]);

  function taste(e: KeyboardEvent<HTMLDivElement>) {
    const knoepfe = [...(reihe.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
    const jetzt = knoepfe.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const weiter = (jetzt + (e.key === "ArrowRight" ? 1 : -1) + knoepfe.length) % knoepfe.length;
      knoepfe[weiter]?.focus();
    }
    const ziffer = Number(e.key);
    if (Number.isInteger(ziffer) && ziffer >= 1 && ziffer <= arten.length && !gesperrt) {
      e.preventDefault();
      const art = arten[ziffer - 1];
      if (art) waehlen(art);
    }
  }

  return (
    <div className={`pos-neu-karten${erste ? " erste" : ""}${schmal ? " schmal" : ""}${zusatz ? " mit-zusatz" : ""}`}>
      {erste ? <h4 className="pos-neu-titel">{t("positionen.erste")}</h4> : null}
      <div className="pos-neu-reihe" role="group" aria-label={t("angebotBauen.artWaehlen")} ref={reihe} onKeyDown={taste}>
        {arten.map((art, i) => (
          <button
            type="button"
            key={art}
            className="pos-neu-karte"
            aria-pressed={aktiv === art}
            aria-keyshortcuts={String(i + 1)}
            disabled={Boolean(gesperrt)}
            onClick={() => waehlen(art)}
          >
            <span className="pos-neu-zeichen" aria-hidden="true">{ZEICHEN[art]}</span>
            <span className="pos-neu-karte-wort">
              <b>{erste ? t(ARTWORT[art] ?? "") : t("positionen.dazuArt", { art: t(ARTWORT[art] ?? "") })}</b>
              {erste && beispiele[art] ? <span>{beispiele[art]}</span> : null}
            </span>
          </button>
        ))}
        {zusatz}
      </div>
      {gesperrt ? <p className="nebensatz zuerst"><T k={gesperrt} /></p> : null}
    </div>
  );
}

/**
 * „+ Rabatt" — und die eine Frage danach: Prozent oder Betrag.
 *
 * Gewählt steht der Knopf gedrückt da und sagt, in welcher Art die leeren
 * Felder rechnen; ein zweiter Klick blendet die Spalte wieder aus, sofern an
 * keiner Zeile ein Rabatt steht.
 */
function RabattKnopf({ spalte, gesperrt }: { spalte: Rabattspalte; gesperrt?: string | undefined }) {
  const t = useT();
  const [fragt, setzeFragt] = useState(false);

  if (spalte.art) {
    return (
      <button
        type="button"
        className="pos-neu-karte pos-rabatt-knopf"
        aria-pressed="true"
        title={t("rabatt.ausblenden")}
        onClick={() => spalte.setzen("")}
      >
        <span className="pos-neu-karte-wort">
          <b>{t(spalte.art === "betrag" ? "rabatt.anBetrag" : "rabatt.anProzent")}</b>
        </span>
      </button>
    );
  }

  if (fragt) {
    return (
      <span className="pos-rabatt-frage" role="group" aria-label={t("rabatt.frage")}>
        <span className="pos-rabatt-frage-wort">{t("rabatt.frage")}</span>
        <button type="button" className="pos-neu-karte" autoFocus
          onClick={() => { spalte.setzen("prozent"); setzeFragt(false); }}>
          <span className="pos-neu-karte-wort"><b>{t("rabatt.artProzent")}</b></span>
        </button>
        <button type="button" className="pos-neu-karte"
          onClick={() => { spalte.setzen("betrag"); setzeFragt(false); }}>
          <span className="pos-neu-karte-wort"><b>{t("rabatt.artBetrag")}</b></span>
        </button>
        <button type="button" className="pos-neu-karte pos-rabatt-zu"
          aria-label={t("allgemein.abbrechen")} title={t("allgemein.abbrechen")}
          onClick={() => setzeFragt(false)}>×</button>
      </span>
    );
  }

  return (
    <button
      type="button"
      className="pos-neu-karte pos-rabatt-knopf"
      disabled={Boolean(gesperrt)}
      onClick={() => setzeFragt(true)}
    >
      <span className="pos-neu-karte-wort"><b>{t("rabatt.dazu")}</b></span>
    </button>
  );
}

/* ══════════════ Das Anlegen ══════════════ */

type DazuEigenschaften = {
  /** Die Form der Tabelle, in die die Eingabezeile gehängt wird. */
  form: Positionsform;
  /**
   * Die Nummer des Vorgangs — ein Versprechen und keine Zeichenkette.
   * Im Angebot entsteht der Entwurf **erst mit der ersten Position**.
   */
  vorgang: () => Promise<string>;
  /** Die Kundennummer für die Preisauskunft. */
  kunde?: string | undefined;
  rechte: Positionsrechte;
  /** Warum die Karten nicht gehen, obwohl der Zustand es erlaubt. i18n-Schlüssel. */
  gesperrt?: string | undefined;
  /** Der Zeitraum, mit dem die nächste Miete beginnt — der der ersten. */
  vorgabe?: Spanne | undefined;
  /** Nach dem Anlegen. Am Auftrag klappt damit die Werkbank zu. */
  fertig?: (() => void) | undefined;
  /** Die Tabelle ist leer — die Karten sind dann der Inhalt. */
  erste?: boolean | undefined;
  /**
   * Die Zeilen, die schon dastehen.
   *
   * Sie werden nur für die Rolle gebraucht: „statt dieser hier" muss wissen,
   * was „hier" ist. Fehlen sie, gibt es beim Anlegen nur „in Summe" und
   * „optional" — und die Maske, die sie nicht mitgibt, verliert nichts
   * anderes.
   */
  zeilen?: Position[] | undefined;
};

/**
 * Das Anlegen — **zwei Stücke, ein Zustand.**
 *
 * Bis zum 16.09.2026 war das eine Komponente unter der Liste: Eingabezeile
 * und Karten in einem Kasten. Seit die Liste eine Tabelle ist, gehören die
 * beiden an verschiedene Stellen — die **Zeile** in den Tabellenkörper
 * (sonst wäre sie wieder ein Formular daneben), die **Karten** darunter aufs
 * Blatt. Ein Haken statt einer Komponente: Er gibt beide Stücke heraus, und
 * `neueArt` bleibt trotzdem ein einziger Zustand.
 *
 * Die Maske schreibt damit:
 *
 *     const dazu = usePositionDazu({ … });
 *     <Positionsliste … eingabe={dazu.zeile} />
 *     {dazu.karten}
 */
export function usePositionDazu(
  { form, vorgang, kunde, rechte, gesperrt, vorgabe, fertig, erste, zeilen }: DazuEigenschaften,
): { zeile: ReactNode; karten: ReactNode; rabatt: Rabattspalte } {
  const [neueArt, setzeNeueArt] = useState<NeueArt | "">("");
  /* Die freigeschaltete Rabattspalte. Hier und nicht in der Liste: Der Knopf
     steht bei den Karten, die Spalte in der Tabelle — die Maske reicht den
     einen Zustand an beide (`rabatt={dazu.rabatt}`). */
  const [rabattArt, setzeRabattArt] = useState<Rabattart | "">("");
  const rabatt: Rabattspalte = { art: rabattArt, setzen: setzeRabattArt };

  /* Wo nichts angelegt werden darf, steht der Grund und keine Karte. */
  if (!rechte.anlegen) {
    return {
      rabatt,
      zeile: null,
      karten: (
        <div className="angebot-block">
          <p className="blatt-satz"><T k={rechte.grund} /></p>
        </div>
      ),
    };
  }

  const eigenschaften: ZeilenEigenschaften = {
    form,
    vorgang,
    kunde,
    vorgabe,
    gesperrt,
    /* **Die Rolle wird beim Anlegen entschieden, nicht danach.** Bis zum
       16.09.2026 legte man eine gewöhnliche Position an und schaltete sie in
       der Liste um — wenn man wusste, dass es das Feld gibt. Luca: *„Prüf auch
       noch mal, wer sich um die Angebotserstellung kümmert und um die
       optionalen Artikel, weil die auch hinzugefügt werden wollen."*

       `rechte.wahl` ist dieselbe Bedingung wie in der Liste: nur im Entwurf.
       Versendet liegt die Wahl beim Kunden, angenommen ist sie getroffen. */
    zeilen: zeilen ?? [],
    wahl: rechte.wahl,
    fertig: () => { setzeNeueArt(""); fertig?.(); },
    abbrechen: () => setzeNeueArt(""),
  };

  /* `key` auf der Art: Wer von Miete auf Ware wechselt, beginnt leer. */
  const zeile =
    neueArt === "miete" ? <MieteZeile key="miete" {...eigenschaften} />
      : neueArt === "leistung" || neueArt === "verkauf"
        ? <KatalogZeile key={neueArt} art={neueArt} {...eigenschaften} />
        : neueArt === "text" ? <TextZeile key="text" {...eigenschaften} />
          : null;

  /* „+ Rabatt" erst, wenn es etwas zu rabattieren gibt: Auf dem leeren Blatt
     sind die Karten der Inhalt, und dort hätte die Frage keinen Gegenstand. */
  const rabattKnopf = !erste && (zeilen ?? []).some((p) => p.rabattfaehig)
    ? <RabattKnopf spalte={rabatt} gesperrt={gesperrt} />
    : null;

  return {
    zeile,
    rabatt,
    karten: (
      <div className="pos-neu">
        <Karten
          erste={Boolean(erste) && !neueArt}
          aktiv={neueArt}
          gesperrt={gesperrt}
          waehlen={(art) => setzeNeueArt(neueArt === art ? "" : art)}
          zusatz={rabattKnopf}
        />
      </div>
    ),
  };
}

/* ══════════════════════════════════════════════════════════════════
   Die Positionen am Telefon (20.09.2026).

   Luca: *„Die Positionsansicht in den Angeboten, wenn man das bearbeiten
   will, ist eine Vollkatastrophe. Da musst du generell ran, dass das
   Angebotsmodul ein bisschen so ist wie zum Beispiel bei Vermieten …
   weil da funktioniert das ja sehr gut."*

   ── Was vorher da war ─────────────────────────────────────────────

   Die Liste zeigte am Telefon Zweizeiler — gut. Wer aber **ändern**
   wollte, bekam mit einem Knopf „Ändern" die Tabelle des Schreibtischs
   in den Schirm gelegt: acht Spalten auf 390 px, Felder von 40 px,
   Datum, Menge, Einzelpreis, Rabatt, Rolle und Bediener nebeneinander.
   Gemessen am 20.09.2026: waagerecht rollbar, kein Feld traf den Daumen
   zweimal gleich.

   ── Was jetzt gilt ────────────────────────────────────────────────

   **Dieselbe Sprache wie die Sammlung beim Vermieten**
   (`bausteine/Postenblatt.tsx`, „Details"): Die Zeile öffnet ein Blatt
   von unten, und darin steht jedes Stück für sich — Zeitraum über den
   Zeitraumwähler des Hauses, Menge, Preis und Rabatt als Felder von
   56 px, die Rolle im Angebot als Wahlzeilen, Löschen im Fuß. Keine
   Tabelle, kein zweites Bild derselben Zeile.

   **Geschrieben wird wie in der Liste** (`feldSchreiben` oben): beim
   Verlassen des Feldes, und nur, was sich geändert hat. Die Mutationen
   sind dieselben (`usePositionAendern`, `usePositionLoeschen`), also
   auch dieselbe Rechnung dahinter — an den Zahlen ändert sich nichts
   (`docs/43-rechenreferenz.md`).
   ══════════════════════════════════════════════════════════════════ */

/** Die leise zweite Zeile: Nummer · Art · Zeitraum oder Menge · Rolle. */
function zeilenmerkmal(
  p: Position,
  nummern: Map<string, string>,
  t: (k: string, w?: Record<string, string | number>) => string,
): string {
  const trenn = ` ${t("allgemein.trennpunkt")} `;
  const bedienerFehlt = !p.bediener
    && (p.bedienungspflicht === "nur_mit_person" || (p.bedienungspflicht === "wahlweise" && Boolean(p.bedient)));
  return [
    nummern.get(p.id ?? String(p.nr)) ?? "",
    t(ARTWORT[p.art ?? "leistung"] ?? "angebotBauen.artLeistung"),
    p.art === "miete" && (p.von || p.bis)
      ? t("positionen.karte.zeitraum", { von: tagKurz(p.von ?? null), bis: tagKurz(p.bis ?? null) })
      : p.art === "text" || mengeFolgt(p) ? "" : [p.menge, p.einheit ? einheitFuer(t, p.einheit, Number(p.menge)) : ""].filter(Boolean).join(" "),
    p.art === "leistung" ? (p.von ? leistungstermin(p, t) : t("positionen.terminOffen")) : "",
    p.stueck ? t("bestand.stueckZahl", { zahl: p.stueck }) : "",
    mengeFolgt(p) ? t("positionen.mengeAufmass") : "",
    p.optional ? t("optionen.markeOptional") : "",
    p.alternativeZu ? t("optionen.markeStatt", { nr: nummern.get(p.alternativeZu) ?? "" }) : "",
    bedienerFehlt ? t("positionen.bedienerFehlt") : "",
  ].filter(Boolean).join(trenn);
}

/**
 * Die Positionen als Zeilen — eine Karte, eine Zeile je Position, die
 * Zuschläge der Regeln dahinter. Angebot und Auftrag zeichnen dieselbe
 * Liste; was sie unterscheidet, ist nur, was ein Tipp öffnet.
 */
export function Positionszeilen({ zeilen, zuschlaege, oeffnen, zuschlagOeffnen, fuss }: {
  zeilen: Position[];
  zuschlaege?: Erwartung | undefined;
  /** Eine Position antippen — ohne ihn ist die Liste nur zu lesen. */
  oeffnen?: ((p: Position) => void) | undefined;
  /** Eine Zuschlagszeile antippen. */
  zuschlagOeffnen?: ((z: Erwartungszeile) => void) | undefined;
  fuss?: ReactNode;
}) {
  const t = useT();
  const trenn = ` ${t("allgemein.trennpunkt")} `;
  /* Die abgewählte Anlieferung an ihrem Regelplatz, wie am Schreibtisch
     (`lieferwegregeln.ts`). */
  const { vorne, hinten } = useMemo(() => listenfolge(zeilen, zuschlaege?.zeilen), [zeilen, zuschlaege]);
  const nummern = useMemo(() => positionsnummern(vorne, t), [vorne, t]);
  /* Alternativen stehen unter ihrer Elternposition — dieselbe Folge wie auf
     dem Papier (`positionsnummern`). */
  const folge = useMemo(() => vorne
    .filter((p) => !p.alternativeZu || !vorne.some((x) => x.id === p.alternativeZu))
    .flatMap((p) => [p, ...vorne.filter((a) => istAlternativeVon(a, p))]), [vorne]);

  if (!folge.length && !hinten.length) {
    return <Leersatz>{t("angebotBauen.nochKeinePositionen")}</Leersatz>;
  }

  const punktVon = (p: Position): Punktart => {
    const bedienerFehlt = !p.bediener
      && (p.bedienungspflicht === "nur_mit_person" || (p.bedienungspflicht === "wahlweise" && Boolean(p.bedient)));
    return ohnePreis(p) ? "stopp" : mengeFolgt(p) || bedienerFehlt ? "acht" : "ruhig";
  };

  const betragVon = (p: Position): string | null => {
    if (p.art === "text") return null;
    if (ohnePreis(p) || mengeFolgt(p) || p.summe === null || p.summe === undefined) return t("allgemein.strich");
    return zaehlt(p) ? euro(p.summe) : t("optionen.inKlammern", { betrag: euro(p.summe) });
  };

  const zeileVon = (p: Position) => (
    <Fragment key={p.id ?? p.nr}>
      <Zweizeiler
        titel={p.art === "text" ? t("angebotTelefon.textzeile") : p.was}
        unter={p.art === "text" ? p.was : zeilenmerkmal(p, nummern, t)}
        punkt={punktVon(p)}
        rechts={betragVon(p)}
        {...(oeffnen ? { beiKlick: () => oeffnen(p) } : {})}
      />
      {/* Der Haken unter der Zeile, nicht in ihr: Die Zeile öffnet ihr Blatt (Befund 17). */}
      <AbholHaken zeile={p} />
    </Fragment>
  );
  const regelzeileVon = (z: Erwartungszeile, i: number) => (
    <Fragment key={`regel-${z.code}-${i}`}>
      <Zweizeiler
        titel={z.bezeichnung}
        unter={[
          t(ARTWORT.zuschlag ?? "angebotBauen.artZuschlag"),
          zuschlagsbezug(z, nummern, t),
          z.prozent !== null ? t("zuschlaege.wegProzent", { prozent: z.prozent })
            : z.menge !== null ? [z.menge, z.einheit_key ? einheitFuer(t, z.einheit_key, z.menge) : ""].filter(Boolean).join(" ") : "",
          z.offen ? t(offenWort(z.offen)) : "",
        ].filter(Boolean).join(trenn)}
        punkt={z.offen === "betrag_fehlt" || z.offen === "preis_fehlt" ? "stopp" : z.offen ? "acht" : "ruhig"}
        rechts={z.netto === null ? t("allgemein.strich") : euro(z.netto)}
        {...(zuschlagOeffnen ? { beiKlick: () => zuschlagOeffnen(z) } : {})}
      />
      <AbholHaken code={z.code} />
    </Fragment>
  );

  return (
    <>
      <Zeilenblatt>
        {folge.map(zeileVon)}
        {hinten.map((h, i) => (h.zeile ? zeileVon(h.zeile) : regelzeileVon(h.regel, i)))}
      </Zeilenblatt>
      {fuss}
    </>
  );
}

/**
 * Eine Position im Blatt von unten — **hier wird sie geändert**, Stück für
 * Stück, aus denselben Formen wie das Blatt der Sammlung beim Vermieten
 * (`bausteine/Postendetails.tsx`, „Das Blatt einer Position am Telefon"):
 * ein Abschnitt je Sache, Zeilen von 56 px, die Zahl rechts in Mono, die
 * Handlung im Fuß.
 *
 * **Seit dem 20.09.2026 mit allem, was am Schreibtisch an der Zeile steht**:
 * Gerät, Bediener, Anbauteile. Luca: *„Was ich immer noch nicht kann, ist,
 * die Einsätze in der Position zu bearbeiten … so wie beim Direktvermieten."*
 * Bis dahin hatte das Blatt Zeitraum, Menge, Preis, Rabatt und Rolle — wer
 * am Telefon den Fahrer wechseln wollte, fand ihn nirgends. Die Daten und
 * Handgriffe dafür sind dieselben wie in der Zeile (`useGeraetewahl`,
 * `useBedienerwahl`, `Zubehoerzeile`); zwei Stellen, eine Frage.
 *
 * **Die Zuschläge stehen nicht hier.** Sie folgen aus Regeln je Vorgang
 * (`Erwartung`), nicht je Position, und stehen als eigene Zeilen in der
 * Liste — jede mit ihrem Blatt zum Lesen.
 */
export function Positionsblatt({ nr, p, zeilen, rechte, form, offen, schliessen }: {
  nr: string;
  /** Die Position — `null` schließt das Blatt. */
  p: Position | null;
  /** Alle Zeilen des Vorgangs: für die Nummern und die Wahl „statt …". */
  zeilen: Position[];
  rechte: Positionsrechte;
  form: Positionsform;
  offen: boolean;
  schliessen: () => void;
}) {
  const t = useT();
  const ruf = useRuf();
  const aendern = usePositionAendern();
  const loeschen = usePositionLoeschen();
  const [entwurf, setzeEntwurf] = useState<Record<string, string>>({});
  const [fragtWeg, setzeFragtWeg] = useState(false);
  const [vorschlag, setzeVorschlag] = useState<Zeitraumvorschlag | null>(null);
  const geraet = useGeraetewahl(nr, p);
  const bediener = useBedienerwahl(nr, p, rechte);

  const nummern = useMemo(() => positionsnummern(zeilen, t), [zeilen, t]);
  useEffect(() => { setzeEntwurf({}); setzeFragtWeg(false); setzeVorschlag(null); }, [p?.id]);

  if (!p) return null;

  const trenn = ` ${t("allgemein.trennpunkt")} `;
  /* **Regel 4 friert den Preis ein, nicht die Zeile.** Eingefroren ist der
     Betrag, mit dem der Kunde zugesagt hat; Bezeichnung, Zeitraum und Menge
     bleiben änderbar, solange der Zustand es zulässt. Genau so trennt es die
     Liste am Schreibtisch (`einzelfeld`/`rabattfeld` gegen `zeitfelder`). */
  const aenderbar = rechte.aendern;
  const preisFest = !aenderbar || Boolean(p.eingefroren);
  const grund = p.eingefroren ? t("angebotBauen.eingefroren") : rechte.aendern ? undefined : t(rechte.grund);
  const wert = (feld: string, serverwert: string) => entwurf[feld] ?? serverwert;
  const setze = (feld: string, w: string) => setzeEntwurf((alt) => ({ ...alt, [feld]: w }));

  async function schreiben(feld: "was" | "menge" | "einzel" | "rabatt", roh: string) {
    if (!p?.id) return;
    setzeEntwurf((alt) => { const rest = { ...alt }; delete rest[feld]; return rest; });
    const teil: Parameters<typeof aendern.mutateAsync>[0] = { nr, id: p.id };
    if (feld === "was") {
      if (roh.trim() === p.was || !roh.trim()) return;
      teil.bezeichnung = roh.trim();
    }
    if (feld === "menge") {
      const z = alsZahl(roh);
      if (z === undefined || String(z) === p.menge) return;
      teil.menge = z;
    }
    if (feld === "einzel") {
      // Leer heißt „zurück zum Tarif" (AG-06) — wie in der Liste.
      if (roh.trim() === "") teil.einzelpreis = null;
      else {
        const z = alsZahl(roh);
        if (z === undefined || z === p.einzel) return;
        teil.einzelpreis = z;
      }
    }
    if (feld === "rabatt") {
      const z = alsZahl(roh) ?? 0;
      if (p.rabattBetrag) {
        if (z === p.rabattBetrag) return;
        teil.rabatt_betrag = z;
      } else {
        if (z === (p.rabattProzent ?? 0)) return;
        teil.rabatt_prozent = z;
      }
    }
    await aendern.mutateAsync(teil);
    if (teil.einzelpreis === null) ruf("posPreisTarif");
    else ruf(feld === "einzel" ? "posPreisVonHand" : "posGeaendert", undefined, feld === "einzel" ? "acht" : "gut");
  }

  async function zeitSchreiben(von: string, bis: string) {
    if (!p?.id) return;
    if (von === tagImBetrieb(p.von) && bis === tagImBetrieb(p.bis)) return;
    const neu = await aendern.mutateAsync({ nr, id: p.id, von, bis });
    ruf("posGeaendert");
    /* F3: Der neue Zeitraum bringt einen Betragsvorschlag mit — das Blatt fragt. */
    setzeVorschlag(neu.vorschlag);
  }

  async function rolleSchreiben(w: string) {
    if (!p?.id) return;
    await aendern.mutateAsync(
      w === "optional" ? { nr, id: p.id, optional: true }
        : w.startsWith("statt:") ? { nr, id: p.id, alternative_zu: w.slice(6) }
          : { nr, id: p.id, optional: false, alternative_zu: null },
    );
    ruf("posGeaendert");
  }

  async function weg() {
    if (!p?.id) return;
    await loeschen.mutateAsync({ nr, id: p.id });
    ruf("posWeg", undefined, "stopp");
    schliessen();
  }

  const bedienerFehlt = bediener.gilt && bediener.mit && !p.bediener;

  /* Die Sätze, die keine Zahl sind: Sie stehen oben, damit niemand erst
     rollt, um zu sehen, warum ein Betrag fehlt. */
  const saetze = [
    mengeFolgt(p) ? t("positionen.mengeAufmass") : "",
    ohnePreis(p) ? t("positionen.keinPreis") : "",
    p.optional ? t("angebotTelefon.satzOptional") : "",
    p.alternativeZu ? t("angebotTelefon.satzStatt", { nr: nummern.get(p.alternativeZu) ?? "" }) : "",
    p.eingefroren ? t("angebotBauen.eingefroren") : "",
    bedienerFehlt ? t("positionen.satzBedienerFehlt") : "",
    bediener.lage === "widerspruch" ? t("positionen.pflichtGegenAbholungSatz") : "",
  ].filter(Boolean);

  const rolle = p.optional ? "optional" : p.alternativeZu ? `statt:${p.alternativeZu}` : "summe";
  const hatAlternativen = zeilen.some((x) => istAlternativeVon(x, p));
  const eltern = zeilen.filter((x) => x.id && x.id !== p.id && !x.alternativeZu && zaehlt(x) && WAEHLBAR.has(x.art ?? ""));
  const rolleWaehlbar = form === "angebot" && rechte.wahl && rechte.aendern && WAEHLBAR.has(p.art ?? "") && Boolean(p.id);

  const summe = p.summe === null || p.summe === undefined ? t("allgemein.strich")
    : zaehlt(p) ? euro(p.summe) : t("optionen.inKlammern", { betrag: euro(p.summe) });

  /* Die Zeilen der Bedienerwahl — der Haken „mit Bediener", wo er eine Frage
     ist, und darunter, wer kann und wer nicht. Ein Gewählter, den die Liste
     nicht mehr führt, steht trotzdem da (`ohneEintrag`). */
  const bedienerZeilen: ReactNode[] = [];
  if (bediener.gilt) {
    if (bediener.pflicht === "wahlweise") {
      bedienerZeilen.push(
        <Hakenzeile
          key="mit"
          wort={t("positionen.mitBediener")}
          an={p.bedient === true}
          gesperrt={!rechte.aendern || !p.id || bediener.aendern.isPending || p.bediener?.unterwegs === true}
          grund={rechte.aendern ? (p.bediener?.unterwegs ? t("positionen.bedienerUnterwegs") : undefined) : t(rechte.grund)}
          aendern={(an) => bediener.schreiben({ bedient: an })}
        />,
      );
    }
    if (bediener.mit && rechte.aendern) {
      if (bediener.ohneEintrag && p.bediener) {
        bedienerZeilen.push(
          <Wahlzeile key={p.bediener.id} wort={p.bediener.name} an gesperrt={bediener.gesperrt} waehlen={() => undefined} />,
        );
      }
      bediener.liste.forEach((k) => {
        bedienerZeilen.push(
          <Wahlzeile
            key={k.id}
            wort={k.name}
            unter={bediener.gruende(k).join(trenn) || undefined}
            an={bediener.gewaehlt === k.id}
            gesperrt={bediener.gesperrt || (!k.waehlbar && !k.gewaehlt)}
            waehlen={() => bediener.schreiben({ bediener_id: bediener.gewaehlt === k.id ? null : k.id })}
          />,
        );
      });
    } else if (bediener.mit) {
      /* Nicht mehr änderbar: Wer fährt, steht als Wert da. */
      bedienerZeilen.push(
        <Wertzeile key="wer" was={t("positionen.bedienerWer")}>{p.bediener?.name ?? t("allgemein.strich")}</Wertzeile>,
      );
    }
  }

  return (
    <Blattvonunten
      offen={offen}
      schliessen={schliessen}
      titel={p.art === "text" ? t("angebotTelefon.textzeile") : p.was}
      unter={[
        nummern.get(p.id ?? String(p.nr)) ? t("angebotTelefon.posNr", { nr: nummern.get(p.id ?? String(p.nr)) ?? "" }) : "",
        t(ARTWORT[p.art ?? "leistung"] ?? "angebotBauen.artLeistung"),
      ].filter(Boolean).join(trenn)}
      rast="gross"
      /* **Hergeben hört früher auf als Ändern** (`rechteAus`): Eine Position
         fällt nur im Entwurf wieder weg. Später steht sie auf einem Papier,
         das jemand gesehen hat — dort wird sie geändert, nicht getilgt. */
      fuss={rechte.loeschen && p.id ? (
        <>
          {fragtWeg ? (
            <>
              <p className="posb-frage">{t("positionen.wirklichWeg", { was: p.was })}</p>
              <button type="button" className="handy-knopf" onClick={() => setzeFragtWeg(false)}>
                {t("allgemein.dochNicht")}
              </button>
              <button type="button" className="handy-knopf leise posb-weg" disabled={loeschen.isPending} onClick={() => void weg()}>
                {t("allgemein.loeschen")}
              </button>
            </>
          ) : (
            <button type="button" className="handy-knopf leise posb-weg" onClick={() => setzeFragtWeg(true)}>
              {t("allgemein.loeschen")}
            </button>
          )}
        </>
      ) : undefined}
    >
      <div className="posb">
        {saetze.length ? <Kurzfassung>{saetze.join(trenn)}</Kurzfassung> : null}
        {aendern.isError ? <FehlerBand pfad={positionsFehlerPfad(aendern.error)} werte={fehlerWerte(aendern.error)} /> : null}

        {/* ── Die Bezeichnung ── Sie steht im Kopf; geändert wird sie hier.
            Ein Name ist ein Satz und keine Zahl, also ein Feld über die ganze
            Breite; eine Textzeile bekommt das mehrzeilige, sonst steht der
            halbe Satz außerhalb des Bildes. */}
        <Abschnitt titel={t(p.art === "text" ? "angebotBauen.artText" : "angebotBauen.bezeichnung")}>
          {p.art === "text" ? (
            <textarea
              className="feld posb-feld posb-text"
              rows={3}
              value={wert("was", p.was)}
              disabled={!aenderbar}
              title={grund}
              aria-label={t("angebotTelefon.textzeile")}
              onChange={(e) => setze("was", e.target.value)}
              onBlur={(e) => void schreiben("was", e.target.value)}
            />
          ) : (
            <input
              className="feld posb-feld"
              value={wert("was", p.was)}
              disabled={!aenderbar}
              title={grund}
              aria-label={t("angebotBauen.bezeichnung")}
              onChange={(e) => setze("was", e.target.value)}
              onBlur={(e) => void schreiben("was", e.target.value)}
            />
          )}
        </Abschnitt>

        {/* ── Der Zeitraum ── der Kalenderknopf des Hauses (`Zeitraum`), am
            Telefon mit dem Kalender als Blatt von unten. Gesperrt steht er
            als Wert. */}
        {p.art === "miete" && p.von ? (
          <Abschnitt titel={t("vermieten.blattWann")}>
            {aenderbar ? (
              <Zeitraum
                id={`posb-zeit-${p.id ?? p.nr}`}
                wert={{ von: tagImBetrieb(p.von), bis: p.bis ? tagImBetrieb(p.bis) : null }}
                schild={t("allgemein.zeitraum")}
                aendern={(neu: Spanne) => { if (neu.von && neu.bis) void zeitSchreiben(neu.von, neu.bis); }}
              />
            ) : (
              <Zeilenblatt>
                <Wertzeile was={t("allgemein.zeitraum")}>
                  {t("positionen.karte.zeitraum", { von: tagKurz(p.von), bis: tagKurz(p.bis ?? null) })}
                </Wertzeile>
              </Zeilenblatt>
            )}
          </Abschnitt>
        ) : null}

        {vorschlag && p.id ? (
          <Zeitraumvorschlagband
            nr={nr}
            id={p.id}
            was={p.was}
            vorschlag={vorschlag}
            fertig={() => setzeVorschlag(null)}
          />
        ) : null}

        {/* ── Menge und Preis ── die Zahlen, die den Betrag machen: rechts,
            in Mono, die Einheit leise daneben. */}
        {p.art === "text" ? null : (
          <Abschnitt titel={t("positionen.blattRechnung")}>
            <Zeilenblatt>
              <Zahlzeile
                was={t("angebotBauen.spalteMenge")}
                wert={wert("menge", mengeFolgt(p) ? "" : p.menge)}
                platz={mengeFolgt(p) ? t("allgemein.strich") : undefined}
                einheit={p.einheit ? einheitFuer(t, p.einheit, alsZahl(wert("menge", "")) ?? Number(p.menge)) : undefined}
                dazu={mengeFolgt(p) ? t("positionen.mengeAufmass") : undefined}
                gesperrt={!aenderbar || mengeFolgt(p)}
                grund={mengeFolgt(p) ? t("positionen.mengeAufmass") : grund}
                aendern={(w) => setze("menge", w)}
                fertig={(w) => void schreiben("menge", w)}
              />
              <Zahlzeile
                was={t("angebotBauen.spalteEinzel")}
                betrag
                wert={wert("einzel", ohnePreis(p) ? "" : betragFeldtext(p.einzel))}
                platz={ohnePreis(p) ? t("allgemein.strich") : undefined}
                einheit={t("rabatt.einheitBetrag")}
                dazu={p.eingefroren ? t("angebotBauen.eingefrorenKurz") : undefined}
                gesperrt={preisFest}
                grund={grund}
                aendern={(w) => setze("einzel", w)}
                fertig={(w) => void schreiben("einzel", w)}
              />
              {p.rabattfaehig && p.id ? (
                <Zahlzeile
                  was={t("rabatt.spalte")}
                  schild={t(p.rabattBetrag ? "rabatt.schildBetrag" : "rabatt.schildProzent")}
                  wert={wert("rabatt", p.rabattBetrag
                    ? betragFeldtext(p.rabattBetrag)
                    : p.rabattProzent ? String(p.rabattProzent).replace(".", ",") : "")}
                  platz={t("allgemein.strich")}
                  einheit={t(p.rabattBetrag ? "rabatt.einheitBetrag" : "rabatt.einheitProzent")}
                  gesperrt={preisFest}
                  grund={grund}
                  aendern={(w) => setze("rabatt", w)}
                  fertig={(w) => void schreiben("rabatt", w)}
                />
              ) : null}
              <Wertzeile was={t("angebotBauen.spalteSumme")} summe>{summe}</Wertzeile>
            </Zeilenblatt>
          </Abschnitt>
        )}

        {/* ── Welches Stück ── Regel 2 bleibt die Vorgabe: Verkauft wird der
            Typ, und die automatische Wahl steht schon da. Wer hier greift,
            legt fest — für diesen einen Kunden, der genau diesen Bagger will.
            Ein reserviertes Stück fragt nach (`belegung.reserviert`). */}
        {geraet.gilt ? (
          <Abschnitt
            titel={t("positionen.geraet.traeger")}
            auskunft={geraet.traeger?.vorgemerkt ? t("positionen.geraet.vorgemerkt") : undefined}
          >
            {geraet.stuecke.length === 0 ? (
              <Leersatz>{geraet.laedt ? t("allgemein.laedt") : t("positionen.geraet.fehlt")}</Leersatz>
            ) : (
              <Zeilenblatt>
                {geraet.stuecke.map((s) => (
                  <Wahlzeile
                    key={s.id}
                    wort={<span className="kennung">{s.kennung}</span>}
                    unter={s.wer ? t("positionen.geraet.reserviertKurz", { vorgang: s.wer }) : undefined}
                    an={geraet.traeger?.id === s.id}
                    gesperrt={!rechte.aendern || geraet.unterwegs || geraet.aendern.isPending}
                    grund={rechte.aendern ? (geraet.unterwegs ? t("positionen.geraet.imBrett") : undefined) : t(rechte.grund)}
                    waehlen={() => geraet.setzen(s.id, false)}
                  />
                ))}
              </Zeilenblatt>
            )}
            {geraet.fragt ? (
              <>
                <Blattsatz art="acht">{t("schnell.geraetReserviertHinweis")}</Blattsatz>
                <button type="button" className="handy-knopf leise" onClick={() => geraet.setzen(geraet.fragt, true)}>
                  {t("positionen.geraet.trotzdem")}
                </button>
              </>
            ) : null}
            {geraet.aendern.isError && !geraet.fragt ? (
              <FehlerBand pfad={positionsFehlerPfad(geraet.aendern.error)} werte={fehlerWerte(geraet.aendern.error)} />
            ) : null}
          </Abschnitt>
        ) : null}

        {/* ── Bedienung ── Ein Mensch ist eine Ressource (Regel 8) und wird
            gewählt; die Liste filtert nicht, sie begründet. Ein Kran fährt
            nie allein — dort steht es als Auskunft am Abschnitt. */}
        {bediener.gilt ? (
          <Abschnitt
            titel={t("schnell.bedienung")}
            auskunft={bediener.pflicht === "nur_mit_person" ? t("positionen.bedienerPflicht") : undefined}
          >
            {bedienerZeilen.length ? (
              <Zeilenblatt>{bedienerZeilen}</Zeilenblatt>
            ) : bediener.mit && bediener.isFetching ? (
              <Leersatz>{t("allgemein.laedt")}</Leersatz>
            ) : null}
            {bedienerFehlt ? <Blattsatz art="acht">{t("positionen.bedienerFehlt")}</Blattsatz> : null}
            {p.bediener?.vorgemerkt ? <Blattsatz>{t("positionen.bedienerVorgemerkt")}</Blattsatz> : null}
            {bediener.aendern.isError ? (
              <Qualifikationsband
                fehler={bediener.aendern.error}
                pfad={positionsFehlerPfad(bediener.aendern.error)}
                werte={fehlerWerte(bediener.aendern.error)}
                trotzdem={bediener.trotzdem}
                laeuft={bediener.aendern.isPending}
              />
            ) : null}
          </Abschnitt>
        ) : null}

        {/* ── Die Anbauteile ── wählbar, nicht nur lesbar; der Baustein
            zeichnet seinen Abschnitt selbst und nichts, wo nichts passt. */}
        <Zubehoerzeile nr={nr} p={p} rechte={rechte} handy />

        {/* ── Die Rolle im Angebot ── in der Summe, optional oder statt einer
            anderen Zeile. Wahlzeilen statt eines Auswahlfeldes: Am Telefon
            ist ein `select` ein Fenster, das die Maske verdeckt. */}
        {rolleWaehlbar ? (
          <Abschnitt titel={t("optionen.rolleSchild")}>
            <Zeilenblatt>
              <Wahlzeile wort={t("optionen.rolleSumme")} an={rolle === "summe"} waehlen={() => void rolleSchreiben("summe")} />
              <Wahlzeile wort={t("optionen.rolleOptional")} an={rolle === "optional"} waehlen={() => void rolleSchreiben("optional")} />
              {hatAlternativen ? null : eltern.map((x) => (
                <Wahlzeile
                  key={x.id}
                  wort={t("optionen.rolleStatt", { nr: nummern.get(x.id ?? "") ?? "", was: x.was })}
                  an={rolle === `statt:${x.id}`}
                  waehlen={() => void rolleSchreiben(`statt:${x.id}`)}
                />
              ))}
            </Zeilenblatt>
            {hatAlternativen ? <Blattsatz>{t("optionen.hatAlternativen")}</Blattsatz> : null}
          </Abschnitt>
        ) : null}
      </div>
    </Blattvonunten>
  );
}

/** Das Wort zu einer Zuschlagszeile ohne Betrag — drei Gründe, drei Wörter. */
export function offenWort(offen: NonNullable<Erwartungszeile["offen"]>): string {
  if (offen === "menge_folgt") return "zuschlaege.mengeFolgt";
  if (offen === "preis_fehlt") return "zuschlaege.preisFehlt";
  return "zuschlaege.betragFehlt";
}

/**
 * **Der Preis eines Zuschlags, der immer individuell vergeben wird** — am
 * Vorgang, an der Zeile der Regel (Migration 0227, Entscheidung E1 vom
 * 04.10.2026, die Anlieferung).
 *
 * Luca: *„es gibt keinen festen Preis, der wird immer individuell vergeben."*
 * Was hier eingegeben wird, wird eine Zuschlagszeile mit diesem Preis — sie
 * schlägt die Regel desselben Codes (wie jede Zeile von Hand), wird mit der
 * Annahme eingefroren (Regel 4) und steht auf Auftragsbestätigung und
 * Rechnung. Ohne sie hält die Annahme an. Zwei Nachkommastellen (E11).
 */
export function PreisAnRegel({ nr, z }: { nr: string; z: Erwartungszeile }) {
  const t = useT();
  const anlegen = usePositionAnlegen();
  const [text, setzeText] = useState("");
  const [fehler, setzeFehler] = useState(false);
  const schreiben = async () => {
    // Eine dritte Stelle: rot und der Satz darunter, nichts geht hinaus (N4-06).
    if (betragFehler(text) !== null) { setzeFehler(true); return; }
    const zahl = zahlLesen(text);
    if (zahl === null) { setzeFehler(text.trim() !== ""); return; }
    if (zahl <= 0) { setzeFehler(true); return; }
    setzeFehler(false);
    try {
      await anlegen.mutateAsync({
        nr,
        art: "zuschlag",
        zuschlag_code: z.code,
        bezeichnung: z.bezeichnung,
        menge: z.menge ?? 1,
        einzelpreis: zahl,
      });
      setzeText("");
    } catch {
      setzeFehler(true);
    }
  };
  return (
    <>
      <input
        className={`feld feld-zahl${fehler ? " falsch" : ""}`}
        inputMode="decimal"
        value={text}
        /* In der Spalte „Einzel" (76 px) passt nur das Wort — „Preis fehlt"
           steht ohnehin als Marke an der Zeile (07.10.2026). */
        placeholder={t("zuschlaege.preisKurz")}
        aria-label={t("zuschlaege.preisFuer", { was: z.bezeichnung })}
        {...(fehler || betragFehler(text) !== null ? { "aria-invalid": true } : {})}
        disabled={anlegen.isPending}
        onChange={(e) => setzeText(e.target.value)}
        onBlur={() => void schreiben()}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      />
      <Betragssatz text={text} />
    </>
  );
}
